"use client";

import { AGENT_STAGE_LABELS, AGENT_STAGE_STYLES, AgentStageIcon } from "@/components/agent-stage-badge";
import { AgentTestDialog, type AgentTestDraft } from "@/components/agent-test-dialog";
import { ContainerFilesSidePanel, type AttachableContainerFile } from "@/components/container-files-panel";
import { useContainerFiles } from "@/hooks/use-container-files";
import { FileTextIcon } from "@/components/ui/file-text";
import { PlusIcon } from "@/components/ui/plus";
import { MicIcon } from "@/components/ui/mic";
import { LockIcon, MicOffIcon, PhoneOffIcon } from "lucide-react";
import { Skeleton, ChatSkeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getRecoveryForkMessages } from "@/lib/chat-recovery";
import { ToolApprovalSignatures } from "@/lib/tool-approval-signatures";
import { denyUnansweredToolApprovals } from "@/lib/tool-approval-expiry";
import { shouldEnableStreamResume } from "@/lib/resumable-stream-gate";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { motion, useReducedMotion } from "motion/react";
import {
  Attachment,
  AttachmentPreview,
  AttachmentTypeLabel,
  Attachments,
} from "@/components/ai-elements/attachments";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import {
  Message,
  MessageAction,
  MessageActions,
  MessageContent,
  MessageResponse,
  MessageToolbar,
} from "@/components/ai-elements/message";
import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionAddScreenshot,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputBody,
  PromptInputButton,
  PromptInputFooter,
  PromptInputProvider,
  PromptInputSubmit,
  PromptInputAttachmentPreviews,
  usePromptInputAttachments,
  PromptInputTools,
  usePromptInputController,
  type PromptInputMessage,
  type PreparedAttachment,
} from "@/components/ai-elements/prompt-input";
import {
  AgentMentionTextarea,
  type PendingAgentMention,
} from "@/components/ai-elements/prompt-input-agent-mention";
import { stripAgentMentionToken, type MentionableAgent } from "@/lib/agent-mention";
import { uploadPresentedFileToProject } from "@/lib/add-generated-file-to-project";
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from "@/components/ai-elements/reasoning";
import { Notice } from "@/components/ai-elements/notice";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { BrainIcon } from "@/components/ui/brain";
import { ChevronsRightLeftIcon } from "@/components/ui/chevrons-right-left";
import { FrameIcon } from "@/components/ui/frame";
import {
  Source,
  Sources,
  SourcesContent,
  SourcesTrigger,
} from "@/components/ai-elements/sources";
import { ChevronRightIcon } from "@/components/ui/chevron-right";
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtSearchResult,
  ChainOfThoughtSearchResults,
  ChainOfThoughtStep,
} from "@/components/ai-elements/chain-of-thought";
import {
  Confirmation,
  ConfirmationTitle,
  ConfirmationRequest,
  ConfirmationAccepted,
  ConfirmationRejected,
  ConfirmationActions,
  ConfirmationAction,
} from "@/components/ai-elements/confirmation";
import {
  getWoodsActionName,
  isWoodsActionToolPart,
  WoodsActionCard,
} from "@/components/ai-elements/woods-action";
import {
  Question,
  QuestionTitle,
  QuestionRequest,
  QuestionAnswered,
  QuestionOptions,
  QuestionOption,
  QuestionOther,
  buildClarifyingAnswerReason,
  parseClarifyingAnswerReason,
} from "@/components/ai-elements/question";
import {
  getCodeExecutionDescription,
  getCodeExecutionFiles,
  getWebFetchDomain,
  getWebFetchTitle,
  getWebFetchUrl,
  getWebSearchQuery,
  getWebSearchResults,
  getResultTitle,
  isCodeExecutionInput,
  isSkillCodeExecutionInput,
  getSkillName,
  isKnowledgeBaseToolPart,
  isMemoryToolPart,
  getMemoryToolNotice,
  getMemoryToolPath,
  getToolWriteNotice,
  type ToolWriteNotice,
  isWebFetchToolPart,
  isWebSearchToolPart,
  isPresentFilesTool,
  isCreateFileTool,
  isEditFileTool,
  getPresentFilesOutput,
  getKnowledgeBaseQuery,
  getKnowledgeBaseDocumentName,
  getKnowledgeBaseAnswer,
  getKnowledgeBaseSources,
  type KnowledgeBaseSource,
  isMyFilesToolPart,
  getMyFilesAction,
  getMyFilesListedFiles,
  isMeetingNotesToolPart,
  getMeetingNotesQuery,
  getMeetingNotesAnswer,
  getMeetingNotesSources,
  type MeetingNotesSource,
  isSharedChatsToolPart,
  getSharedChatsQuery,
  getSharedChatsSources,
  isCreateRegisterToolPart,
  isDeleteRegisterToolPart,
  getCreateRegisterInput,
  getDeleteRegisterKey,
  isUpsertRegisterNotesToolPart,
  getUpsertRegisterNotesInput,
  isDeleteRegisterNotesToolPart,
  getDeleteRegisterNotesInput,
  isListRegistersToolPart,
  getListRegistersOutput,
  isSearchRegisterNotesToolPart,
  isQueryRegisterNotesToolPart,
  getRegisterSearchInput,
  getRegisterQueryInput,
  getRegisterNotes,
  getRegisterAnswer,
  isAskClarifyingQuestionToolPart,
  getAskClarifyingQuestionInput,
  GeneratedFileArtifact,
  GeneratedFileArtifacts,
  PersistedArtifact,
  ToolInput,
  ToolOutput,
  ToolScript,
  type ToolPart,
} from "@/components/ai-elements/tool";
import {
  MessageCollapseToggle,
  useCollapsibleMessage,
} from "@/components/chat-message-collapse";
import { DocPreviewPanel } from "@/components/doc-preview-panel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { AudioLinesIcon } from "@/components/ui/audio-lines";
import { BotMessageSquareIcon } from "@/components/ui/bot-message-square";
import { BookTextIcon } from "@/components/ui/book-text";
import { EarthIcon } from "@/components/ui/earth";
import { FoldersIcon } from "@/components/ui/folders";
import { MessageCircleDashedIcon } from "@/components/ui/message-circle-dashed";
import { FeaturedAnnouncementChip } from "@/components/announcements/featured-announcement-chip";
import { ChatFeedbackDialog } from "@/components/chat-feedback-dialog";
import { ChatUsageContext } from "@/components/chat-usage-context";
import { GeneratedFileRow } from "@/components/generated-file-row";
import { MoveChatToProjectDialog } from "@/components/move-chat-to-project-dialog";
import { ShareChatPanel } from "@/components/share-chat-panel";
import { ThinkingIndicator } from "@/components/thinking-indicator";
import { useTabTitle } from "@/components/tab-title-provider";
import { VoiceInputButton } from "@/components/voice-input-button";
import { cleanLiveRequestMessage, getLiveAgentContext } from "@/lib/live-chat";
import { createLiveTextRelay, type LiveDelegateObserver } from "@/lib/live-chat-stream";
import { getLiveVoiceApproval, type LiveVoiceApproval, type LiveVoiceApprovalResponse } from "@/lib/live-chat-approval";
import { useLiveChat } from "@/hooks/use-live-chat";
import { useLiveChatFlag } from "@/components/live-chat-flag-provider";
import { WelcomeScreen } from "@/components/welcome-screen";
import {
  coalesceRenderableTextParts,
  stripDuplicateStructuredResultTables,
} from "@/lib/chat-message-parts";
import { resolveActiveChatModel } from "@/lib/chat-model";
import { getChatReasoning } from "@/lib/chat-reasoning";
import { resolveChatEffort, type ChatEffort } from "@/lib/chat-effort";
import { ChatEffortControl } from "@/components/chat-effort-control";
import {
  getMovedChatDestination,
  isWoodsStructuredResultActionName,
  shouldShowWoodsAction,
} from "@/lib/woods-actions";
import { cn } from "@/lib/utils";
import type { ChatMode } from "@/app/api/chat/chat-runtime";
import {
  MAX_CHAT_TITLE_LENGTH,
  useChatTitleGeneration,
} from "../hooks/use-chat-title-generation";
import { useGlobalModels } from "@/hooks/use-global-models";
import { useMessagePersistence } from "@/hooks/use-message-persistence";
import { armNotifyOnFinish } from "@/lib/notify-on-finish";
import { ResponseNotificationPrompt } from "@/components/response-notification-prompt";
import { DEFAULT_MODEL, isValidModelId, type ModelId } from "@/lib/model-id";
import { useProjects } from "@/hooks/use-projects";
import { useUserSettings, recordToProfileTools } from "@/hooks/use-user-settings";
import { supabase } from "@/lib/supabase";
import { uploadAttachment } from "@/lib/upload-attachment";
import {
  chatAttachmentRejection,
  CONTAINER_ATTACHMENT_METADATA,
  formatMegabytes,
  isHtmlFile,
  MAX_PDF_ATTACHMENT_BYTES,
} from "@/lib/file-kinds";
import { HtmlIntakeDialog, type HtmlIntakeChoice } from "@/components/html-intake-dialog";
import { convertHtmlToPdf } from "@/lib/html-to-pdf";
import { TypingAnimation } from "@/registry/magicui/typing-animation";
import { latestArtifacts } from "@/lib/chat-artifacts";
import type { ArtifactPart, ChatMessageMetadata, ChatUIMessage } from "@/types/chat";
import { getChatNetTotalTokens, sumChatMessageCost, sumChatMessageMetadata } from "@/types/chat";
import type { DbAgent, DbChatType, DbGlobalModel, TokenUsage } from "@/types/db";
import {
  type ActiveToolConfig,
  type ToolId,
  TOOL_IDS,
  getAgentSettings,
  resolveTools,
  resolveToolSettings,
} from "@/types/settings";
import { BUILTIN_TOOL_UUID } from "@/lib/builtin-tools";
import {
  buildAgentRequestOverride,
  type AgentRequestOverride,
  type AgentRuntimeContext,
} from "@/lib/agent-chat-request";
import { useTools } from "@/hooks/use-tools";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithApprovalResponses } from "ai";
import type { FileUIPart, SourceDocumentUIPart, TextUIPart } from "ai";
import {
  ArrowUpRightIcon,
  CheckIcon,
  ChevronDownIcon,
  CircleHelpIcon,
  CopyIcon,
  DatabaseIcon,
  FileIcon,
  FolderMinusIcon,
  FolderPlusIcon,
  GlobeIcon,
  FolderSearchIcon,
  InfoIcon,
  MessageSquareIcon,
  Minimize2Icon,
  PencilIcon,
  RefreshCwIcon,
  SearchIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  Trash2Icon,
  ScrollTextIcon,
  TerminalIcon,
  WrenchIcon,
} from "lucide-react";
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { toast } from "sonner";

function LiveAttachmentSubmit({ busy }: { busy: boolean }) {
  const attachments = usePromptInputAttachments();
  if (!attachments.files.length) return null;
  return <Button type="submit" size="sm" variant="outline" disabled={busy || attachments.files.some((file) => file.uploading)}>Send files</Button>;
}

// ── Shared centred column width ───────────────────────────────────────────────
const COL = "mx-auto w-full max-w-4xl";

// Load Rive only when voice is active; ordinary text chat pays no animation cost.
const LivePersona = dynamic(
  () => import("@/components/ai-elements/persona").then((module) => module.Persona),
  { ssr: false, loading: () => <AudioLinesIcon size={32} /> }
);

const TITLE_TYPING_SPEED = 14;

// ── Doc-link interception (markdown links → sidebar preview) ─────────────────

const SIDEBAR_PREVIEW_EXTS = new Set(["docx", "doc", "pptx", "ppt", "xlsx", "xls", "pdf", "md", "mdx", "html", "htm"]);

const SIDEBAR_PREVIEW_EXT_PATTERN = /(docx|doc|pptx|ppt|xlsx|xls|pdf|md|mdx|html|htm)$/i;

function extractLinkText(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map((child) => extractLinkText(child)).join("");
  }
  if (node && typeof node === "object" && "props" in node) {
    const maybeProps = node as { props?: { children?: React.ReactNode } };
    return extractLinkText(maybeProps.props?.children ?? "");
  }
  return "";
}

function isPreviewableFilename(value: string): boolean {
  const ext = value.split(".").pop()?.toLowerCase();
  return !!ext && SIDEBAR_PREVIEW_EXTS.has(ext);
}

function getFilenameFromHref(href: string): string | null {
  try {
    const parsed = new URL(href, "https://placeholder.invalid");
    const pathname = parsed.pathname;
    const lastSegment = decodeURIComponent(pathname.split("/").pop()?.split("?")[0] ?? "");
    if (lastSegment && isPreviewableFilename(lastSegment)) {
      return lastSegment;
    }

    // Some links carry a filename in query params rather than path segments.
    for (const key of ["filename", "file", "name", "download"]) {
      const value = parsed.searchParams.get(key);
      if (!value) continue;
      const decoded = decodeURIComponent(value);
      if (isPreviewableFilename(decoded)) {
        return decoded;
      }
    }
  } catch {
    // Ignore malformed URLs and allow fallback handling.
  }

  return null;
}

function tokenUsageToMessageMetadata(
  tokenUsage: TokenUsage | null | undefined
): ChatMessageMetadata | undefined {
  if (!tokenUsage) {
    return undefined;
  }

  const lastStep = tokenUsage.breakdown.at(-1) ?? tokenUsage.total;

  return {
    inputTokens: tokenUsage.total.input,
    outputTokens: tokenUsage.total.output,
    cacheReadTokens: tokenUsage.total.cache_read,
    cacheWriteTokens: tokenUsage.total.cache_write,
    totalTokens: tokenUsage.total.input + tokenUsage.total.output,
    lastStepInputTokens: lastStep.input,
    lastStepOutputTokens: lastStep.output,
    tokenUsageBreakdown: tokenUsage.breakdown,
    pricingSnapshot: tokenUsage.pricing,
  };
}

function isCompactionTextPart(part: { type: string; providerMetadata?: TextUIPart["providerMetadata"] }): boolean {
  return (
    part.type === "text" &&
    (part.providerMetadata?.anthropic?.type === "compaction" ||
      part.providerMetadata?.openai?.type === "compaction")
  );
}

const compactTokenCount = (value: number) =>
  new Intl.NumberFormat("en-US", { notation: "compact" }).format(value);

function getApiFileIdFromHref(href: string): string | null {
  const direct = href.trim();
  if (/^file_[A-Za-z0-9]+$/.test(direct)) {
    return direct;
  }

  const directMatch = href.match(/\b(file_[A-Za-z0-9]+)\b/);
  if (directMatch?.[1]) {
    return directMatch[1];
  }

  try {
    const parsed = new URL(href, "https://placeholder.invalid");

    const queryId =
      parsed.searchParams.get("fileId") ??
      parsed.searchParams.get("file_id") ??
      parsed.searchParams.get("id");
    if (queryId && /^file_[A-Za-z0-9]+$/.test(queryId)) {
      return queryId;
    }

    const pathMatch = parsed.pathname.match(/\/(?:api\/)?files\/(file_[A-Za-z0-9]+)(?:\/(?:content|save))?$/i);
    if (pathMatch?.[1]) {
      return pathMatch[1];
    }

    const pathnameId = parsed.pathname.match(/\b(file_[A-Za-z0-9]+)\b/);
    if (pathnameId?.[1]) {
      return pathnameId[1];
    }
  } catch {
    return null;
  }

  return null;
}

function toApiFileContentHref(fileId: string): string {
  return `/api/files/${encodeURIComponent(fileId)}/content?inline=1`;
}

function getDocLinkInfo(
  href: string | undefined,
  children: React.ReactNode
): { filename: string } | null {
  // Prefer filename-like text labels, including nested markdown formatting.
  const text = extractLinkText(children).trim();
  if (text && isPreviewableFilename(text)) return { filename: text };
  const matchedName = text.match(new RegExp(`[^\\s)\\]]+\\.${SIDEBAR_PREVIEW_EXT_PATTERN.source.replace("$", "")}`, "i"));
  if (matchedName?.[0]) return { filename: matchedName[0] };

  // Fall back to checking the URL itself — but skip external HTTP URLs since
  // those should open in a new tab, not the sidebar preview.
  if (href && !/^https?:\/\//i.test(href.trim())) {
    const filenameFromHref = getFilenameFromHref(href);
    if (filenameFromHref) return { filename: filenameFromHref };
  }

  return null;
}

export type WidgetEditingContext = { widgetId: string; expectedRevision: number };

export type MyFilesScopeConfig = {
  fileIds?: string[];
  collectionIds?: string[];
  label?: string;
};

type PersistedChatTool = {
  tool_id: string;
  config: Record<string, unknown>;
};

function buildPersistedChatTools(
  tools: Record<ToolId, boolean>,
  myFilesConfig?: MyFilesScopeConfig | null
): PersistedChatTool[] {
  const entries: PersistedChatTool[] = recordToProfileTools(tools);
  if (!myFilesConfig) return entries;

  return entries.map((entry) =>
    entry.tool_id === BUILTIN_TOOL_UUID.myFiles
      ? { ...entry, config: { ...myFilesConfig } }
      : entry
  );
}

// ── Transport ─────────────────────────────────────────────────────────────────

const useChatTransport = (
  modelRef: React.RefObject<ModelId>,
  activeToolsRef: React.RefObject<ActiveToolConfig[] | undefined>,
  userIdRef: React.RefObject<string | null | undefined>,
  accessTokenRef: React.RefObject<string | null>,
  chatIdRef: React.RefObject<string | null>,
  incognitoRef: React.RefObject<boolean>,
  woodsProjectRef: React.RefObject<string | null>,
  projectIdRef: React.RefObject<string | null>,
  agentIdRef: React.RefObject<string | null>,
  agentPromptRef: React.RefObject<string | null>,
  agentContextRef: React.RefObject<AgentRuntimeContext | null>,
  projectChatIdRef: React.RefObject<string | null>,
  chatModeRef: React.RefObject<ChatMode>,
  reasoningEffortRef: React.RefObject<ChatEffort>,
  widgetEditingRef: React.RefObject<WidgetEditingContext | undefined>
) =>
  useMemo(
    () =>
      new DefaultChatTransport({
        api: "/api/chat",
        // Project chats go to /api/chat/project, which owns its own request
        // contract and system prompt. Everything else keeps /api/chat. The
        // endpoint is chosen per request (not per transport) because a chat's
        // project scope is only known from the refs at send time.
        prepareSendMessagesRequest: ({ api, id, messages, trigger, messageId, body }) => {
          const token = accessTokenRef.current;
          const headers = token
            ? { Authorization: `Bearer ${token}` }
            : ({} as Record<string, string>);

          // A one-off @mention passes its own agentId/activeTools/etc. via
          // sendMessage(msg, { body: requestOverride }); the SDK forwards it
          // here as `body`. It must win over the persistent chat refs, which
          // reflect the chat's normal active agent, not the mentioned one.
          const override = body as Partial<AgentRequestOverride> | undefined;

          const agentId = override?.agentId ?? agentIdRef.current;
          const isAgentChat = Boolean(agentId);
          const baseActiveTools = override?.activeTools ?? activeToolsRef.current;
          const activeTools = isAgentChat
            ? baseActiveTools?.filter((t) => t.toolId !== "memory")
            : baseActiveTools;
          const model = override?.model ?? modelRef.current;
          const agentPrompt = override?.agentPrompt ?? agentPromptRef.current;
          const agentContext = override?.agentContext ?? agentContextRef.current;

          const projectId = projectIdRef.current;
          if (projectId) {
            // Agent turns in a project use the union of the chat's tools and
            // the agent's (a mentioned agent's entry wins on the same toolId,
            // so its KB/web-search config applies). Memory stays: project
            // memory is project-namespaced, not the agent's.
            const projectActiveTools = override?.activeTools
              ? [
                  ...override.activeTools,
                  ...(activeToolsRef.current ?? []).filter(
                    (t) => !override.activeTools!.some((o) => o.toolId === t.toolId)
                  ),
                ]
              : activeToolsRef.current;
            return {
              api: "/api/chat/project",
              headers,
              body: {
                id,
                messages,
                trigger,
                messageId,
                projectId,
                projectChatId: projectChatIdRef.current ?? undefined,
                model,
                activeTools: projectActiveTools,
                userId: userIdRef.current,
                incognito: incognitoRef.current,
                agentId,
                agentPrompt,
                agentContext,
              },
            };
          }

          return {
            api,
            headers,
            body: {
              id,
              messages,
              trigger,
              messageId,
              model,
              activeTools,
              userId: userIdRef.current,
              chatId: chatIdRef.current,
              agentId,
              agentPrompt,
              agentContext,
              incognito: incognitoRef.current,
              source: override?.source ?? (isAgentChat ? "agent-chat" : "general-chat"),
              woodsProject: woodsProjectRef.current,
              mode: chatModeRef.current,
              reasoningEffort: !agentId && !widgetEditingRef.current
                ? resolveChatEffort(model, reasoningEffortRef.current)
                : "default",
              widgetEditing: widgetEditingRef.current,
            },
          };
        },
        // Called on mount when useChat's `resume` option is true, to reattach
        // to a still-generating turn after navigation/disconnect — see
        // src/app/api/chat/[id]/stream and .../project/[id]/stream. Same
        // project-vs-general branch as prepareSendMessagesRequest above.
        prepareReconnectToStreamRequest: ({ id }) => {
          const token = accessTokenRef.current;
          const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
          const projectId = projectIdRef.current;
          return {
            api: projectId ? `/api/chat/project/${id}/stream` : `/api/chat/${id}/stream`,
            headers,
          };
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

// ── Per-chat model dropdown ───────────────────────────────────────────────────

/**
 * Model picker for general and project chats. Overrides the user's default
 * model for this chat only. Not rendered for agent chats — those follow the
 * agent's configured model.
 */
const ChatModelDropdown = ({
  models,
  value,
  onChange,
  claudeOnly = false,
  codeOnly = false,
}: {
  models: DbGlobalModel[];
  value: ModelId;
  onChange: (model: ModelId) => void;
  /** Widget editing currently requires Anthropic's container. */
  claudeOnly?: boolean;
  codeOnly?: boolean;
}) => {
  const activeModels = useMemo(
    () =>
      models.filter(
        (m) => m.is_active && (!claudeOnly || m.provider === "anthropic") &&
          (!codeOnly || m.provider === "anthropic" || m.provider === "openai")
      ),
    [models, claudeOnly, codeOnly]
  );
  const activeLabel =
    activeModels.find((m) => m.model_id === value)?.name ?? value;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label="Change model for this chat"
          className="h-8 gap-1 rounded-full px-3 text-sm font-normal text-muted-foreground hover:text-foreground"
          size="sm"
          type="button"
          variant="ghost"
        >
          <span className="max-w-36 truncate">{activeLabel}</span>
          <ChevronDownIcon className="size-3.5 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          Model for this chat
        </DropdownMenuLabel>
        {activeModels.map((m) => (
          <DropdownMenuItem
            className="font-normal"
            key={m.id}
            onSelect={() => {
              if (m.model_id !== value) onChange(m.model_id);
            }}
          >
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate">{m.name}</span>
              {m.tagline ? (
                <span className="truncate text-xs font-normal text-muted-foreground">
                  {m.tagline}
                </span>
              ) : null}
            </span>
            {m.model_id === value ? (
              <CheckIcon className="ml-2 size-4 shrink-0" />
            ) : null}
          </DropdownMenuItem>
        ))}
        {activeModels.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">
            Loading models…
          </div>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ── Chat title + dropdown ─────────────────────────────────────────────────────

const IncognitoToggleButton = ({
  incognito,
  onToggleIncognito,
}: {
  incognito: boolean;
  onToggleIncognito: () => void;
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          aria-label={incognito ? "Turn off temporary chat" : "Turn on temporary chat"}
          aria-pressed={incognito}
          className={[
            "size-9 rounded-full border-transparent bg-transparent shadow-none",
            incognito
              ? "text-activated hover:bg-transparent hover:text-activated"
              : "text-muted-foreground hover:bg-transparent hover:text-foreground",
          ].join(" ")}
          onClick={onToggleIncognito}
          size="icon"
          type="button"
          variant="ghost"
        >
          <MessageCircleDashedIcon size={16} />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left">
        <p>{incognito ? "Temporary chat on" : "Temporary chat off"}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const ChatHeader = ({
  chatId,
  projectChatId,
  userId,
  title,
  isGeneratingTitle,
  isAnimatingTitle,
  projectId,
  isProjectChat = false,
  sharing: _sharing,
  onRename,
  onDelete,
  onRemoveFromProject,
  onProjectChanged,
  onSharingChanged: _onSharingChanged,
  onChatDeleted,
  modelSelector,
  incognito = false,
  showIncognitoToggle = false,
  onToggleIncognito,
  headerActions,
  headerLeading,
}: {
  chatId: string | null;
  projectChatId?: string | null;
  userId?: string | null;
  title: string;
  isGeneratingTitle: boolean;
  isAnimatingTitle: boolean;
  projectId?: string | null;
  /** True when this session is a project_chats row rather than a personal chat. */
  isProjectChat?: boolean;
  sharing?: string | null;
  onRename: (name: string) => Promise<boolean>;
  onDelete: () => Promise<void>;
  /** Moves this project chat back to the user's personal space. Only relevant when isProjectChat. */
  onRemoveFromProject?: () => Promise<void>;
  onProjectChanged?: () => void;
  onSharingChanged?: (sharing: string) => void;
  onChatDeleted?: () => void;
  /** Per-chat model dropdown, rendered in the header's right-side controls. */
  modelSelector?: React.ReactNode;
  /** Whether this session currently has temporary/incognito chat active. */
  incognito?: boolean;
  /** Whether to show the temporary-chat toggle button. */
  showIncognitoToggle?: boolean;
  /** Called when the user clicks the temporary-chat toggle. */
  onToggleIncognito?: () => void;
  /** Page-supplied controls rendered at the end of the header's right cluster. */
  headerActions?: React.ReactNode;
  headerLeading?: React.ReactNode;
}) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRemoveFromProjectDialogOpen, setIsRemoveFromProjectDialogOpen] = useState(false);
  const [isRemovingFromProject, setIsRemovingFromProject] = useState(false);
  const [isAddToProjectDialogOpen, setIsAddToProjectDialogOpen] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { projects } = useProjects();
  const activeProjectName = projectId
    ? (projects.find((p) => p.id === projectId)?.name ?? null)
    : null;

  const handleRenameOpen = useCallback(() => {
    setRenameValue(title);
    setIsRenaming(true);
    setTimeout(() => renameInputRef.current?.select(), 0);
  }, [title]);

  const handleRenameCommit = useCallback(async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === title) {
      setIsRenaming(false);
      return;
    }
    const ok = await onRename(trimmed);
    if (!ok) toast.error("Failed to rename chat.");
    setIsRenaming(false);
  }, [onRename, renameValue, title]);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") void handleRenameCommit();
      if (e.key === "Escape") setIsRenaming(false);
    },
    [handleRenameCommit]
  );

  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);
    await onDelete();
    setIsDeleting(false);
    setIsDeleteDialogOpen(false);
  }, [onDelete]);

  const handleRemoveFromProjectConfirm = useCallback(async () => {
    setIsRemovingFromProject(true);
    await onRemoveFromProject?.();
    setIsRemovingFromProject(false);
    setIsRemoveFromProjectDialogOpen(false);
  }, [onRemoveFromProject]);


  if (headerLeading) {
    return (
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-2 py-2">
        <div className="min-w-0 flex-[1_1_12rem]">{headerLeading}</div>
        <div className="flex shrink-0 items-center gap-1">{modelSelector}{headerActions}</div>
      </div>
    );
  }

  return (
    <div className="flex shrink-0 items-start justify-between gap-3 px-2 pt-3 pb-2">
      <div className="flex min-w-0 flex-col gap-1">
        {isRenaming ? (
          <Input
            ref={renameInputRef}
            className="h-7 w-64 text-sm"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={() => void handleRenameCommit()}
            onKeyDown={handleRenameKeyDown}
            maxLength={MAX_CHAT_TITLE_LENGTH}
          />
        ) : (
          <div className="flex min-w-0 items-center gap-1">
            {activeProjectName && projectId ? (
              <>
                <button
                  className="shrink-0 rounded-md px-2 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  onClick={() => router.push(`/projects/${projectId}`)}
                  type="button"
                >
                  {activeProjectName}
                </button>
                <span className="text-muted-foreground shrink-0">/</span>
              </>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex min-w-0 items-center gap-1 rounded-md px-2 py-1 text-sm font-normal text-foreground transition-colors hover:bg-muted"
                  type="button"
                >
                  <span className="max-w-xs truncate">
                    {isGeneratingTitle ? (
                      <Shimmer as="span" className="block truncate" duration={1.6}>
                        {title}
                      </Shimmer>
                    ) : isAnimatingTitle ? (
                      <TypingAnimation
                        key={title}
                        className="block truncate"
                        blinkCursor={false}
                        pauseDelay={0}
                        showCursor={false}
                        startOnView={false}
                        typeSpeed={TITLE_TYPING_SPEED}
                        words={[title]}
                      />
                    ) : (
                      <span className="block truncate">{title}</span>
                    )}
                  </span>
                  <ChevronDownIcon className="size-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {isProjectChat ? (
                  <DropdownMenuItem
                    className="font-normal"
                    disabled={!projectChatId}
                    onSelect={() => setIsRemoveFromProjectDialogOpen(true)}
                  >
                    <FolderMinusIcon className="mr-2 size-4" />
                    Remove from project
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    className="font-normal"
                    disabled={!chatId}
                    onSelect={() => setIsAddToProjectDialogOpen(true)}
                  >
                    <FolderPlusIcon className="mr-2 size-4" />
                    Move to project
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="font-normal"
                  disabled={!chatId && !projectChatId}
                  onSelect={handleRenameOpen}
                >
                  <PencilIcon className="mr-2 size-4" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="font-normal text-destructive focus:text-destructive"
                  disabled={!chatId && !projectChatId}
                  onSelect={() => setIsDeleteDialogOpen(true)}
                >
                  <Trash2Icon className="mr-2 size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {modelSelector || (!isProjectChat && chatId) || (showIncognitoToggle && onToggleIncognito) || headerActions ? (
        <div className="flex shrink-0 items-center gap-1">
          <FeaturedAnnouncementChip />
          {modelSelector}
          {!isProjectChat && chatId ? <ShareChatPanel chatId={chatId} /> : null}
          {showIncognitoToggle && onToggleIncognito ? (
            <IncognitoToggleButton incognito={incognito} onToggleIncognito={onToggleIncognito} />
          ) : null}
          {headerActions}
        </div>
      ) : null}

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete chat</DialogTitle>
            <DialogDescription>
              &ldquo;{title}&rdquo; will be permanently deleted. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDeleteConfirm()}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isRemoveFromProjectDialogOpen} onOpenChange={setIsRemoveFromProjectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove from project</DialogTitle>
            <DialogDescription>
              &ldquo;{title}&rdquo; will be moved to your personal chats. It will no longer be visible to other project members.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRemoveFromProjectDialogOpen(false)}
              disabled={isRemovingFromProject}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleRemoveFromProjectConfirm()}
              disabled={isRemovingFromProject}
            >
              {isRemovingFromProject ? "Removing…" : "Remove from project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {chatId ? (
        <MoveChatToProjectDialog
          chatId={chatId}
          userId={userId}
          open={isAddToProjectDialogOpen}
          onOpenChange={setIsAddToProjectDialogOpen}
          onMoved={() => { onProjectChanged?.(); onChatDeleted?.(); }}
        />
      ) : null}
    </div>
  );
};

// ── Chain-of-thought helpers ──────────────────────────────────────────────────

const extractCodeDescription = (codeOrCmd: string): string | null => {
  // Any comment line with ≥3 words
  for (const line of codeOrCmd.split("\n")) {
    const t = line.trim();
    if (!t.startsWith("#")) continue;
    const stripped = t.replace(/^#+\s*/, "").trim();
    if (stripped.split(/\s+/).length >= 3) return stripped;
  }
  return null;
};

const getToolLabel = (toolPart: ToolPart): string => {
  if (toolPart.title) return toolPart.title;

  if (isSkillCodeExecutionInput(toolPart.input)) {
    const skillName = getSkillName(toolPart.input);
    const name = skillName
      ? skillName.charAt(0).toUpperCase() + skillName.slice(1)
      : null;
    return name ? `Reading ${name} skill` : "Reading skill";
  }

  if (isCodeExecutionInput(toolPart.input)) {
    const desc = getCodeExecutionDescription(toolPart.input);
    if (desc) return desc;

    const codeOrCmd =
      "command" in toolPart.input
        ? toolPart.input.command
        : toolPart.input.code;

    const extracted = extractCodeDescription(codeOrCmd);
    if (extracted) return extracted;

    return "command" in toolPart.input ? "Shell command" : "Code";
  }

  const rawName =
    toolPart.type === "dynamic-tool"
      ? toolPart.toolName
      : toolPart.type.replace(/^tool-/, "");
  return rawName
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getToolStepStatus = (state: string): "active" | "complete" | "pending" =>
  state === "output-available" || state === "result" ? "complete" : "active";

type ResolveDateTimePayload = {
  resolved?: {
    date_time?: string;
    date?: string;
    time?: string;
    day_of_week?: string;
    timezone?: string;
  };
  resolved_utc?: {
    utc_time?: string;
  };
  interpreted_rule?: string;
};

const isResolveDateTimeToolPart = (toolPart: ToolPart): boolean => {
  const rawName =
    toolPart.type === "dynamic-tool"
      ? toolPart.toolName
      : toolPart.type.replace(/^tool-/, "");
  return rawName === "resolve_date_time" || rawName === "resolveDateTime";
};

const getResolveDateTimePayload = (output: unknown): ResolveDateTimePayload | null => {
  if (!isRecord(output)) return null;
  return output as ResolveDateTimePayload;
};

const MeetingNotesExcerpt = ({
  source,
  projectId,
}: {
  source: MeetingNotesSource;
  projectId?: string | null;
}) => {
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);
  const isTruncated = source.content.length > 240;
  const preview = isTruncated ? `${source.content.slice(0, 240)}…` : source.content;

  return (
    <div className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-4 [&_li]:mt-0.5">
      <MessageResponse isAnimating={false} disallowedElements={["script", "style"]}>
        {expanded ? source.content : preview}
      </MessageResponse>
      <div className="mt-1.5 flex items-center gap-3">
        {isTruncated && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="font-medium text-foreground hover:underline"
          >
            {expanded ? "Show less" : "More"}
          </button>
        )}
        {projectId && (
          <button
            type="button"
            onClick={() =>
              router.push(`/meeting-minutes?projectRecordingId=${source.recordingId}&projectId=${projectId}`)
            }
            className="flex items-center gap-0.5 font-medium text-foreground hover:underline"
          >
            View meeting
            <ArrowUpRightIcon className="size-3" />
          </button>
        )}
      </div>
    </div>
  );
};

const ResolveDateTimeResult = ({ output }: { output: unknown }) => {
  const payload = getResolveDateTimePayload(output);
  if (!payload?.resolved) return null;

  const { day_of_week, date, time, timezone } = payload.resolved;
  const utcTime = payload.resolved_utc?.utc_time;

  const parts = [
    day_of_week,
    date,
    time && timezone ? `${time} (${timezone})` : time ?? timezone,
    utcTime ? `UTC ${utcTime}` : undefined,
  ].filter(Boolean).join(" · ");

  return (
    <div className="mt-1 rounded-md border bg-muted/30 px-2.5 py-1.5 text-xs text-foreground">
      {parts}
    </div>
  );
};

const ProjectRegisterStepIcon = ({ className }: { className?: string }) => (
  <span className={`${className ?? ""} inline-flex items-center justify-center rounded-full bg-primary`}>
    <DatabaseIcon className="size-2.5 text-primary-foreground" />
  </span>
);

const KnowledgeBaseStepIcon = ({ className }: { className?: string }) => (
  <FoldersIcon size={16} className={className} />
);

// Filled treatment for knowledge_base calls served from a project's own
// files (see applyProjectKnowledgeBase in /api/chat/project/route.ts),
// distinguishing them from an agent's own configured knowledge base.
const ProjectKnowledgeBaseStepIcon = ({ className }: { className?: string }) => (
  <span className={`${className ?? ""} inline-flex items-center justify-center rounded-full bg-primary`}>
    <FoldersIcon size={10} className="text-primary-foreground" />
  </span>
);

const MyFilesStepIcon = ({ className }: { className?: string }) => (
  <FolderSearchIcon size={16} className={className} />
);

const MeetingNotesStepIcon = ({ className }: { className?: string }) => (
  <span className={`${className ?? ""} inline-flex items-center justify-center rounded-full bg-primary`}>
    <AudioLinesIcon size={10} className="text-primary-foreground" />
  </span>
);

const SharedChatsStepIcon = ({ className }: { className?: string }) => (
  <span className={`${className ?? ""} inline-flex items-center justify-center rounded-full bg-primary`}>
    <MessageSquareIcon size={10} className="text-primary-foreground" />
  </span>
);

// Plain icon, not the filled-circle badge — that style is reserved for
// project-scoped tools (registers, project KB, meeting notes, shared
// chats). Memory is a general-purpose tool available in any chat, so it
// matches SearchIcon/GlobeIcon's bare treatment. BrainIcon (animated) sizes
// via a numeric `size` prop rather than className, so it's wrapped to fit
// ChainOfThoughtStep's `ComponentType<{ className }>` icon contract.
const MemoryStepIcon = ({ className }: { className?: string }) => (
  <BrainIcon size={16} className={className} />
);

const UpsertRegisterResult = ({
  registerKey,
  notes,
}: {
  registerKey: string | null;
  notes: Array<{ externalKey: string; data: unknown }>;
}) => {
  const renderValue = (data: unknown) =>
    typeof data === "string" ? data : data === undefined ? "" : JSON.stringify(data);

  if (notes.length === 0) return null;

  if (notes.length === 1) {
    return (
      <p className="text-xs text-muted-foreground">
        Upsert <strong className="font-medium text-foreground">{registerKey ?? "unknown"}</strong>{" "}
        {notes[0].externalKey} {renderValue(notes[0].data)}
      </p>
    );
  }

  return (
    <Collapsible>
      <CollapsibleTrigger className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
        {notes.length} notes in {registerKey ?? "unknown"}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 space-y-1.5">
          {notes.map((d, i) => (
            <div key={i} className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">{d.externalKey}</span> {renderValue(d.data)}
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Retrieval can return several chunks from the same file — collapse to one
// representative chunk per file (best rank, falling back to best similarity)
// so the chain-of-thought excerpt list matches what "Used N sources" shows,
// instead of listing every raw retrieved chunk.
const dedupeKnowledgeBaseSourcesByFile = (sources: KnowledgeBaseSource[]): KnowledgeBaseSource[] => {
  const byFile = new Map<string, KnowledgeBaseSource>();
  for (const source of sources) {
    const key = source.fileId ?? source.fileName ?? source.id ?? "";
    const existing = byFile.get(key);
    if (!existing) {
      byFile.set(key, source);
      continue;
    }
    const isBetter =
      source.rank !== null && existing.rank !== null
        ? source.rank < existing.rank
        : source.similarity !== null && existing.similarity !== null
          ? source.similarity > existing.similarity
          : false;
    if (isBetter) byFile.set(key, source);
  }
  return Array.from(byFile.values());
};

const getChainOfThoughtLabel = (
  parts: { part: ToolPart; index: number }[],
  _descriptions: Map<number, string>,
  isActive: boolean
): string => {
  if (isActive && parts.some(({ part }) => part.state !== "output-available")) {
    return "Working…";
  }

  const count = parts.length;
  return `Used ${count} tool${count !== 1 ? "s" : ""}`;
};

type OpenAIContainerFileSource = {
  fileId: string;
  containerId: string;
  filename: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getOpenAIContainerFileSource = (
  part: SourceDocumentUIPart
): OpenAIContainerFileSource | null => {
  const metadata = part.providerMetadata?.openai;
  if (!isRecord(metadata)) return null;
  if (metadata.type !== "container_file_citation") return null;

  const fileId = metadata.fileId;
  const containerId = metadata.containerId;
  if (typeof fileId !== "string" || typeof containerId !== "string") {
    return null;
  }

  return {
    fileId,
    containerId,
    filename: part.filename ?? part.title ?? fileId,
  };
};

// ── Message renderer ──────────────────────────────────────────────────────────

const useMemoryToolDebugLogging = (message: ChatUIMessage) => {
  const loggedEventKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      return;
    }

    message.parts.forEach((part, index) => {
      if (!(part.type === "dynamic-tool" || part.type.startsWith("tool-"))) {
        return;
      }

      const toolPart = part as ToolPart;

      if (!isMemoryToolPart(toolPart)) {
        return;
      }

      // Logged once per (message, part, state) so a call's full lifecycle
      // (streaming → available/error) shows as separate lines, but each
      // state doesn't spam on every re-render.
      const eventKey = `${message.id ?? "unknown"}:${index}:${toolPart.state}`;
      if (loggedEventKeysRef.current.has(eventKey)) {
        return;
      }
      loggedEventKeysRef.current.add(eventKey);

      const input = toolPart.input;
      const command =
        typeof input === "object" &&
        input !== null &&
        "command" in input &&
        typeof (input as { command?: unknown }).command === "string"
          ? (input as { command: string }).command
          : null;

      const isDone = toolPart.state === "output-available";
      const notice = isDone ? getMemoryToolNotice(toolPart.input, toolPart.output) : null;

      console.debug("[memory tool]", {
        messageId: message.id,
        partIndex: index,
        command,
        state: toolPart.state,
        input: toolPart.input,
        output: isDone ? toolPart.output : undefined,
        errorText: toolPart.state === "output-error" ? toolPart.errorText : undefined,
        notice: notice?.message ?? null,
        // Mirrors the exclusion condition in chainOfThoughtParts/toolPrecedingTexts —
        // if this is false while isDone is true, the step is being filtered out.
        willShowAsStep: !isDone || notice !== null,
      });
    });
  }, [message.id, message.parts]);
};

type KbSourceItem = {
  id: string | null;
  fileId: string | null;
  content: string;
  chunkOrder: number | null;
  similarity: number | null;
  rank: number | null;
  fileName: string;
};

type ProjectSourceItem =
  | { kind: "file"; key: string; label: string; rank: number | null; file: KbSourceItem }
  | { kind: "meeting"; key: string; label: string; recordingId: string; content: string }
  | { kind: "chat"; key: string; label: string; chatId: string };

// AudioLinesIcon (lucide-animated) ignores className for sizing its inner
// <svg> — it only honors a numeric `size` prop — while MessageSquareIcon and
// FileIcon (plain lucide-react) size correctly from className alone.
const SOURCE_ICON_SIZE_CLASS: Record<number, string> = { 12: "size-3", 14: "size-3.5" };

const renderSourceIcon = (kind: ProjectSourceItem["kind"], px: number, className: string) => {
  if (kind === "meeting") return <AudioLinesIcon size={px} className={className} />;
  const Icon = kind === "chat" ? MessageSquareIcon : FileIcon;
  return <Icon className={`${SOURCE_ICON_SIZE_CLASS[px] ?? ""} ${className}`} />;
};

type RegisterCreateValues = { key: string; name: string; description?: string };

const RegisterCreateConfirmation = ({
  toolPart,
  onCancel,
  onSubmit,
}: {
  toolPart: ToolPart;
  onCancel: () => void;
  onSubmit: (values: RegisterCreateValues) => Promise<void>;
}) => {
  const input = getCreateRegisterInput(toolPart.input);
  const inputDescription =
    typeof toolPart.input === "object" &&
    toolPart.input !== null &&
    "description" in toolPart.input &&
    typeof (toolPart.input as Record<string, unknown>).description === "string"
      ? ((toolPart.input as Record<string, unknown>).description as string)
      : "";
  const [name, setName] = useState(input.name ?? "");
  const [key, setKey] = useState(input.key ?? "");
  const [description, setDescription] = useState(inputDescription);
  const [isCreating, setIsCreating] = useState(false);
  const [created, setCreated] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // When the chat is reloaded the component remounts with fresh local state but
  // the tool part already has a persisted response — show the correct concluded view.
  const approvalReason = (toolPart.approval as { reason?: string } | undefined)?.reason ?? "";
  const isAlreadyResponded =
    toolPart.state === "approval-responded" ||
    toolPart.state === "output-denied" ||
    toolPart.state === "output-available" ||
    toolPart.state === "output-error";

  if (isAlreadyResponded && !created) {
    if (approvalReason.startsWith("User created the register via form")) {
      return (
        <div className="rounded-2xl border px-4 py-3 text-sm bg-card text-card-foreground flex items-center gap-2">
          <DatabaseIcon className="size-4 shrink-0 text-muted-foreground" />
          <span>Register <strong>{input.name}</strong> created.</span>
        </div>
      );
    }
    return (
      <Confirmation approval={toolPart.approval} state={toolPart.state}>
        <ConfirmationTitle>
          <ConfirmationRejected>Register creation cancelled.</ConfirmationRejected>
        </ConfirmationTitle>
      </Confirmation>
    );
  }

  if (created) {
    return (
      <div className="rounded-2xl border px-4 py-3 text-sm bg-card text-card-foreground flex items-center gap-2">
        <DatabaseIcon className="size-4 shrink-0 text-muted-foreground" />
        <span>
          Register <strong>{name}</strong> created.
        </span>
      </div>
    );
  }

  const handleCreate = async () => {
    if (!key.trim() || !name.trim()) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      await onSubmit({ key: key.trim(), name: name.trim(), description: description.trim() || undefined });
      setCreated(true);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Failed to create register");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Confirmation approval={toolPart.approval} state={toolPart.state}>
      <ConfirmationTitle>
        <ConfirmationRequest>
          <div className="flex flex-col gap-3">
            <span className="text-sm font-medium">Create Register</span>
            {createError && <p className="text-xs text-destructive">{createError}</p>}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">
                Name <span className="text-destructive">*</span>
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Team members"
                className="h-8 text-sm"
                disabled={isCreating}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">
                Key <span className="text-destructive">*</span>
              </label>
              <Input
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="e.g. team_members"
                className="h-8 font-mono text-sm"
                disabled={isCreating}
              />
              <p className="text-xs text-muted-foreground">
                Unique machine-readable identifier within this project
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Description</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What will this register contain?"
                rows={2}
                className="resize-none text-sm"
                disabled={isCreating}
              />
            </div>
          </div>
        </ConfirmationRequest>
        <ConfirmationRejected>Register creation cancelled.</ConfirmationRejected>
      </ConfirmationTitle>
      <ConfirmationActions>
        <ConfirmationAction variant="outline" onClick={onCancel} disabled={isCreating}>
          Cancel
        </ConfirmationAction>
        <ConfirmationAction
          onClick={handleCreate}
          disabled={!key.trim() || !name.trim() || isCreating}
        >
          {isCreating ? "Creating…" : "Create"}
        </ConfirmationAction>
      </ConfirmationActions>
    </Confirmation>
  );
};

const AskClarifyingQuestionCard = ({
  toolPart,
  onAnswer,
}: {
  toolPart: ToolPart;
  onAnswer: (reason: string) => void;
}) => {
  const { question, options } = getAskClarifyingQuestionInput(toolPart.input);

  const approvalReason = (toolPart.approval as { reason?: string } | undefined)?.reason;
  const parsed = parseClarifyingAnswerReason(approvalReason);

  return (
    <Question approval={toolPart.approval} state={toolPart.state}>
      <QuestionTitle>
        <QuestionRequest>
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-2">
              <CircleHelpIcon className="size-4 shrink-0 translate-y-0.5 text-muted-foreground" />
              <span className="text-sm font-medium">{question}</span>
            </div>
            <QuestionOptions>
              {options.map((option) => (
                <QuestionOption
                  description={option.description}
                  key={option.label}
                  label={option.label}
                  onClick={() =>
                    onAnswer(
                      buildClarifyingAnswerReason({
                        question: question ?? "",
                        kind: "selected",
                        answer: option.label,
                      })
                    )
                  }
                />
              ))}
              <QuestionOther
                onSubmit={(value) =>
                  onAnswer(
                    buildClarifyingAnswerReason({
                      question: question ?? "",
                      kind: "custom",
                      answer: value,
                    })
                  )
                }
              />
            </QuestionOptions>
          </div>
        </QuestionRequest>
        <QuestionAnswered>
          <div className="flex items-start gap-2">
            <CircleHelpIcon className="size-4 shrink-0 translate-y-0.5 text-muted-foreground" />
            <div className="flex flex-col gap-1">
              <span className="text-sm">{question}</span>
              {parsed?.kind === "dismissed" ? (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  Dismissed — no direct answer given.
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CheckIcon className="size-3.5 shrink-0" />
                  {parsed?.answer ?? "Answered."}
                </span>
              )}
            </div>
          </div>
        </QuestionAnswered>
      </QuestionTitle>
    </Question>
  );
};

const extractUserMessageText = (msg: ChatUIMessage): string =>
  msg.parts
    .reduce<string[]>((acc, p) => {
      if (p.type === "text" && p.text.trim()) acc.push(p.text);
      return acc;
    }, [])
    .join("\n\n");

const ChatMessage = memo(function ChatMessage({
  widgetEditor = false,
  chatFeedback,
  feedbackDisabled,
  message,
  index,
  previousIndex,
  isStreaming,
  showThinking,
  showToolDetails = false,
  chatId,
  isProjectChat = false,
  projectId,
  userId,
  onSaveHtmlToWidgets,
  agentsById,
  activeAgentId,
  onDislike,
  onLike,
  onOpenInSidebar,
  onArtifactSaved,
  onRetry,
  onEdit,
  onRetryPrevious,
  onEditPrevious,
  previousUserMessageText,
  onToolApprovalResponse,
  onUseAgent,
  onAgentDeactivated,
  onRegisterCreate,
  onContinue,
}: {
  chatFeedback: "positive" | "negative" | null;
  /** Editor exports feed the dedicated draft preview, not transcript cards. */
  widgetEditor?: boolean;
  feedbackDisabled: boolean;
  message: ChatUIMessage;
  /** This message's index in the `messages` array — passed through to onRetry/onEdit so those callbacks can stay stable references instead of being re-bound per row on every render. */
  index: number;
  /** Index of the preceding user turn, for the "interrupted response" banner's Try again/Edit prompt actions. */
  previousIndex?: number;
  isStreaming: boolean;
  showThinking?: boolean;
  showToolDetails?: boolean;
  chatId?: string | null;
  isProjectChat?: boolean;
  projectId?: string | null;
  userId?: string | null;
  /** Saves present_files/create_file output to the project's Widgets library. */
  onSaveHtmlToWidgets?: (name: string, url: string) => Promise<{ ok: boolean }>;
  /** For resolving a message's attributed @mentioned agent (metadata.agentId) to a label. */
  agentsById?: Map<string, DbAgent>;
  activeAgentId?: string | null;
  onDislike: () => void;
  onLike: () => void;
  onOpenInSidebar?: (
    filename: string,
    url: string,
    onAddToProject?: () => Promise<{ ok: boolean; error?: string }>
  ) => void;
  onArtifactSaved?: (messageId: string, artifact: ArtifactPart) => void;
  onRetry?: (index: number) => void;
  onEdit?: (index: number, text: string) => void;
  /** Bound to the preceding user turn — used by the "interrupted response" banner's Try again/Edit prompt actions. */
  onRetryPrevious?: (index: number) => void;
  onEditPrevious?: (index: number, text: string) => void;
  previousUserMessageText?: string;
  onToolApprovalResponse: (params: { id: string; approved: boolean; reason?: string }) => void;
  onUseAgent?: (agentId: string) => void;
  onAgentDeactivated?: () => void;
  onRegisterCreate?: (values: RegisterCreateValues) => Promise<{ success: boolean; error?: string }>;
  /** Sends a "Continue" turn — used by the step-limit banner's Continue button. */
  onContinue?: () => void;
}) {
  const router = useRouter();
  const isAssistant = message.role === "assistant";
  // Attribute answers to the agent that handled this turn, even when it is
  // still selected. Voice narration is labelled separately.
  const messageAgentId = (message.metadata as ChatMessageMetadata | undefined)?.agentId;
  // The agent's stage when it answered.
  const messageAgentStage = isAssistant
    ? (message.metadata as ChatMessageMetadata | undefined)?.agentStage ?? null
    : null;
  const attributedAgent =
    messageAgentId && (isAssistant || messageAgentId !== activeAgentId)
      ? agentsById?.get(messageAgentId) ?? null
      : null;
  const mentionedAgent = isAssistant ? attributedAgent : null;
  const userMentionNickname = !isAssistant ? attributedAgent?.nickname ?? null : null;
  const mentionedAgentNickname = mentionedAgent?.name ?? mentionedAgent?.nickname ?? null;
  const mentionedAgentDisplayNickname =
    mentionedAgentNickname && mentionedAgentNickname.length > 20
      ? `${mentionedAgentNickname.slice(0, 20)}…`
      : mentionedAgentNickname;
  const [testDraft, setTestDraft] = useState<AgentTestDraft | null>(null);
  const [creatingTest, setCreatingTest] = useState(false);
  const handleCreateTest = async () => {
    if (!messageAgentId || !previousUserMessageText || !userId) return;
    setCreatingTest(true);
    try {
      const { data, error } = await supabase.rpc("can_manage_agent_tests", { p_agent_id: messageAgentId });
      if (error || !data) {
        toast.error(error?.message || "Only the owner, a reviewer or a team admin can add tests.");
        return;
      }
      setTestDraft({
        title: previousUserMessageText.slice(0, 200),
        input: previousUserMessageText,
        model_answer: message.parts.filter((part) => part.type === "text").map((part) => part.text).join("\n"),
        rubric: null,
      });
    } catch {
      toast.error("Couldn't prepare the test. Try again.");
    } finally {
      setCreatingTest(false);
    }
  };
  const handleSaveChatTest = async (draft: AgentTestDraft) => {
    if (!messageAgentId || !userId) return false;
    try {
      const { error } = await supabase.from("agent_tests").insert({
        agent_id: messageAgentId, created_by: userId,
        title: draft.title.trim(), input: draft.input.trim(),
        model_answer: draft.model_answer.trim(), rubric: draft.rubric?.trim() || null,
      });
      if (error) throw error;
      toast.success("Test added to the agent.");
      return true;
    } catch (error) {
      console.error("[ChatMessage] save test failed:", error);
      toast.error("Couldn't add the test. Check your access and try again.");
      return false;
    }
  };
  const [isCopied, setIsCopied] = useState(false);
  const [isUserCopied, setIsUserCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [editPromptValue, setEditPromptValue] = useState("");
  const [kbChunkPreview, setKbChunkPreview] = useState<KbSourceItem | null>(null);
  const [meetingPreview, setMeetingPreview] = useState<{ title: string; content: string } | null>(null);
  const [previewChunks, setPreviewChunks] = useState<{ id: string; label: string; content: string; isMatch: boolean }[]>([]);
  const [chunksLoading, setChunksLoading] = useState(false);
  const matchedChunkRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!chunksLoading && previewChunks.some((c) => c.isMatch)) {
      matchedChunkRef.current?.scrollIntoView({ behavior: "instant", block: "start" });
    }
  }, [chunksLoading, previewChunks]);

  const webSources = useMemo(() => {
    if (!message.parts.some((part) => isWebSearchToolPart(part as ToolPart))) return [];

    const sources = new Map<string, { url: string; title?: string | null }>();
    for (const part of message.parts) {
      const results = part.type === "source-url"
        ? [{ url: part.url, title: part.title }]
        : isWebSearchToolPart(part as ToolPart)
          ? getWebSearchResults((part as ToolPart).output) ?? []
          : [];
      for (const result of results) {
        if (!/^https?:\/\//i.test(result.url)) continue;
        const existing = sources.get(result.url);
        if (!existing || (!existing.title && result.title)) {
          sources.set(result.url, result);
        }
      }
    }
    return [...sources.values()];
  }, [message.parts]);

  const kbSources = useMemo<KbSourceItem[]>(() => {
    const allSources: KnowledgeBaseSource[] = [];
    for (const part of message.parts) {
      const tp = part as ToolPart;
      if (part.type === "tool-knowledge_base") {
        allSources.push(...getKnowledgeBaseSources(tp.output));
      } else if (isMyFilesToolPart(tp) && getMyFilesAction(tp.input) === "search") {
        allSources.push(...getKnowledgeBaseSources(tp.output));
      }
    }
    return dedupeKnowledgeBaseSourcesByFile(allSources).map((s) => ({ ...s, fileName: s.fileName ?? "Document" }));
  }, [message.parts]);

  // Unified citation list across all three project-scoped retrieval tools —
  // knowledge_base files, search_project_meeting_minutes, search_project_chats.
  // Meeting/chat titles are parsed from the chunk header the indexer embeds
  // (`[Meeting: {title} | ...]` / `[Chat: {title} | ...]`) since neither
  // tool's source objects carry a separate title field.
  const projectSources = useMemo<ProjectSourceItem[]>(() => {
    const items: ProjectSourceItem[] = [];
    const seenMeetings = new Set<string>();
    const seenChats = new Set<string>();

    for (const s of kbSources) {
      items.push({ kind: "file", key: `file:${s.fileId ?? s.fileName}`, label: s.fileName, rank: s.rank, file: s });
    }

    for (const part of message.parts) {
      const tp = part as ToolPart;
      if (isMeetingNotesToolPart(tp)) {
        for (const s of getMeetingNotesSources(tp.output)) {
          if (!s.recordingId || seenMeetings.has(s.recordingId)) continue;
          seenMeetings.add(s.recordingId);
          const titleMatch = s.content.match(/^\[Meeting:\s*([^|\]]+)/);
          items.push({
            kind: "meeting",
            key: `meeting:${s.recordingId}`,
            label: titleMatch ? titleMatch[1].trim() : "Meeting",
            recordingId: s.recordingId,
            content: s.content,
          });
        }
      }
      if (isSharedChatsToolPart(tp)) {
        for (const s of getSharedChatsSources(tp.output)) {
          if (!s.chatId || seenChats.has(s.chatId)) continue;
          seenChats.add(s.chatId);
          const titleMatch = s.content.match(/^\[Chat:\s*([^|\]]+)/);
          items.push({
            kind: "chat",
            key: `chat:${s.chatId}`,
            label: titleMatch ? titleMatch[1].trim() : "Chat",
            chatId: s.chatId,
          });
        }
      }
    }

    return items;
  }, [kbSources, message.parts]);

  const handleSourceClick = useCallback(async (source: KbSourceItem) => {
    setKbChunkPreview(source);
    setPreviewChunks([]);
    if (!source.fileId && !source.id) {
      setPreviewChunks([{ id: "matched", label: "matched chunk", content: source.content, isMatch: true }]);
      return;
    }
    setChunksLoading(true);

    // match_file_items RPC doesn't return chunk_order, so resolve it from the row id when missing.
    let chunkOrder = source.chunkOrder;
    if (chunkOrder === null && source.id) {
      const { data: row } = await supabase
        .from("file_items")
        .select("chunk_order")
        .eq("id", source.id)
        .single();
      chunkOrder = row?.chunk_order ?? null;
    }

    if (chunkOrder !== null && source.fileId) {
      const orders = [chunkOrder - 1, chunkOrder, chunkOrder + 1].filter((o) => o >= 0);
      const { data } = await supabase
        .from("file_items")
        .select("id, content, chunk_order")
        .eq("file_id", source.fileId)
        .in("chunk_order", orders)
        .order("chunk_order");
      setChunksLoading(false);
      if (data) {
        setPreviewChunks(data.map((d) => ({
          id: d.id as string,
          label: `#${(d.chunk_order as number) + 1}`,
          content: d.content as string,
          isMatch: d.chunk_order === chunkOrder,
        })));
      }
    } else {
      setChunksLoading(false);
      setPreviewChunks([{ id: source.id ?? "matched", label: "matched chunk", content: source.content, isMatch: true }]);
    }
  }, []);

  const handleProjectSourceClick = useCallback((item: ProjectSourceItem) => {
    if (item.kind === "file") {
      void handleSourceClick(item.file);
    } else if (item.kind === "meeting") {
      setMeetingPreview({ title: item.label, content: item.content });
    } else if (item.kind === "chat") {
      if (!projectId) return;
      router.push(`/?projectChat=${item.chatId}&project=${projectId}&chatName=${encodeURIComponent(item.label)}`);
    }
  }, [handleSourceClick, projectId, router]);
  const copyResetTimeoutRef = useRef<number | null>(null);
  const userCopyResetTimeoutRef = useRef<number | null>(null);

  useMemoryToolDebugLogging(message);

  const chainOfThoughtParts = useMemo(() => {
    const result: { part: ToolPart; index: number }[] = [];
    message.parts.forEach((part, i) => {
      if (part.type !== "dynamic-tool" && !part.type.startsWith("tool-")) return;
      const toolPart = part as ToolPart;
      // Approval controls render inline, outside the combined tool summary.
      if (isMemoryToolPart(toolPart) && toolPart.approval &&
        ["approval-requested", "approval-responded", "output-denied"].includes(toolPart.state)) return;
      // Only a "done" memory call with a notice-worthy outcome (successful
      // read/write) earns a step; an in-progress call still shows so the
      // user sees a "Checking memory..." row while it runs.
      if (
        isMemoryToolPart(toolPart) &&
        toolPart.state === "output-available" &&
        !getMemoryToolNotice(toolPart.input, toolPart.output)
      ) return;
      if (isPresentFilesTool(toolPart)) return;
      if (isCreateFileTool(toolPart)) return;
      if (isCreateRegisterToolPart(toolPart)) return;
      if (isDeleteRegisterToolPart(toolPart)) return;
      if (isAskClarifyingQuestionToolPart(toolPart)) return;
      if (isWoodsActionToolPart(toolPart)) return;
      if (getCodeExecutionFiles(toolPart.output).length > 0) return;
      result.push({ part: toolPart, index: i });
    });
    return result;
  }, [message.parts]);

  // Capture model-generated text that immediately precedes a tool call (Claude-style descriptions).
  // Those text parts are "consumed" and suppressed from standalone rendering.
  // Scans back past other tool parts to find the nearest preceding text part.
  const toolPrecedingTexts = useMemo(() => {
    const descriptions = new Map<number, string>(); // tool part index → preceding text
    const consumed = new Set<number>();             // text part indices to suppress
    message.parts.forEach((part, i) => {
      // Also suppress text that precedes an OpenAI container-file source-document
      // (GPT emits "Download the [file](sandbox://...) [blocked]" before the citation part)
      if (part.type === "source-document") {
        const source = getOpenAIContainerFileSource(part as SourceDocumentUIPart);
        if (source) {
          for (let j = i - 1; j >= 0; j--) {
            const candidate = message.parts[j];
            if (candidate.type === "text") {
              if (candidate.text.trim()) consumed.add(j);
              break;
            }
            if (candidate.type !== "dynamic-tool" && !candidate.type.startsWith("tool-")) break;
          }
        }
        return;
      }

      if (part.type !== "dynamic-tool" && !part.type.startsWith("tool-")) return;
      const toolPart = part as ToolPart;
      // Keep the model's explanation visible beside the memory approval.
      if (isMemoryToolPart(toolPart) && toolPart.approval &&
        ["approval-requested", "approval-responded", "output-denied"].includes(toolPart.state)) return;
      if (
        isMemoryToolPart(toolPart) &&
        toolPart.state === "output-available" &&
        !getMemoryToolNotice(toolPart.input, toolPart.output)
      ) return;
      if (isPresentFilesTool(toolPart)) return;
      if (isCreateFileTool(toolPart)) return;
      if (isCreateRegisterToolPart(toolPart)) return;
      if (isDeleteRegisterToolPart(toolPart)) return;
      if (isAskClarifyingQuestionToolPart(toolPart)) return;
      if (isWoodsActionToolPart(toolPart)) return;
      if (getCodeExecutionFiles(toolPart.output).length > 0) return;
      // Walk back to find the nearest preceding text part (skip other tool parts)
      for (let j = i - 1; j >= 0; j--) {
        const candidate = message.parts[j];
        if (candidate.type === "text") {
          if (candidate.text.trim()) {
            descriptions.set(i, candidate.text.trim());
            if (showToolDetails) consumed.add(j);
          }
          break;
        }
        // Stop if we hit something other than a tool part (e.g. reasoning, file)
        if (candidate.type !== "dynamic-tool" && !candidate.type.startsWith("tool-")) break;
      }
    });
    return { descriptions, consumed };
  }, [message.parts, showToolDetails]);

  const renderableTextParts = useMemo(
    () => coalesceRenderableTextParts(message.parts, toolPrecedingTexts.consumed),
    [message.parts, toolPrecedingTexts.consumed]
  );

  const hasStructuredWoodsResult = useMemo(
    () => message.parts.some((part) => {
      if (part.type !== "dynamic-tool" && !part.type.startsWith("tool-")) return false;
      const actionName = getWoodsActionName(part as ToolPart);
      return Boolean(
        actionName &&
        isWoodsStructuredResultActionName(actionName) &&
        shouldShowWoodsAction(actionName, (part as ToolPart).input) &&
        (part as ToolPart).state === "output-available"
      );
    }),
    [message.parts]
  );

  // User messages only ever render text inside the bubble (attachments render
  // separately above it), so an attachment-only send has nothing to show here —
  // skip the bubble entirely instead of rendering an empty padded box.
  const hasVisibleBubbleContent = isAssistant || renderableTextParts.size > 0;

  // A long user message would otherwise dominate the viewport; clip it behind a
  // Show more toggle. Assistant replies are never collapsed.
  const collapse = useCollapsibleMessage(!isAssistant && hasVisibleBubbleContent);

  const chainOfThoughtFirstIndex = chainOfThoughtParts[0]?.index ?? -1;

  const reasoningPartIndex = useMemo(
    () => message.parts.findIndex((p) => p.type === "reasoning"),
    [message.parts]
  );
  const reasoningDisplay = useMemo(() => getChatReasoning(message.parts), [message.parts]);

  // The combined Reasoning block renders at whichever comes first: reasoning or first tool step
  const combinedBlockIndex = useMemo(() => {
    if (reasoningPartIndex !== -1 && chainOfThoughtFirstIndex !== -1) {
      return Math.min(reasoningPartIndex, chainOfThoughtFirstIndex);
    }
    if (reasoningPartIndex !== -1) return reasoningPartIndex;
    return chainOfThoughtFirstIndex;
  }, [reasoningPartIndex, chainOfThoughtFirstIndex]);

  const copyText = useMemo(
    () =>
      message.parts
        .reduce<string[]>((chunks, part) => {
          if (part.type === "text" && part.text.trim() && !isCompactionTextPart(part)) {
            chunks.push(part.text);
          }

          return chunks;
        }, [])
        .join("\n\n"),
    [message.parts]
  );

  useEffect(() => {
    if (!isCopied) {
      return;
    }

    window.clearTimeout(copyResetTimeoutRef.current ?? undefined);
    copyResetTimeoutRef.current = window.setTimeout(() => {
      setIsCopied(false);
      copyResetTimeoutRef.current = null;
    }, 2000);

    return () => {
      window.clearTimeout(copyResetTimeoutRef.current ?? undefined);
    };
  }, [isCopied]);

  const handleCopyMessage = useCallback(async () => {
    if (!copyText) {
      return;
    }

    if (typeof window === "undefined" || !navigator?.clipboard?.writeText) {
      toast.error("Clipboard is not available in this browser.");
      return;
    }

    try {
      await navigator.clipboard.writeText(copyText);
      setIsCopied(true);
      toast.success("Message copied to clipboard.");
    } catch {
      toast.error("Couldn't copy the message.");
    }
  }, [copyText]);

  const CopyActionIcon = isCopied ? CheckIcon : CopyIcon;
  const UserCopyActionIcon = isUserCopied ? CheckIcon : CopyIcon;

  useEffect(() => {
    if (!isUserCopied) return;
    window.clearTimeout(userCopyResetTimeoutRef.current ?? undefined);
    userCopyResetTimeoutRef.current = window.setTimeout(() => {
      setIsUserCopied(false);
      userCopyResetTimeoutRef.current = null;
    }, 2000);
    return () => {
      window.clearTimeout(userCopyResetTimeoutRef.current ?? undefined);
    };
  }, [isUserCopied]);

  const userMessageText = useMemo(
    () =>
      message.parts
        .reduce<string[]>((chunks, part) => {
          if (part.type === "text" && part.text.trim()) chunks.push(part.text);
          return chunks;
        }, [])
        .join("\n\n"),
    [message.parts]
  );

  const handleCopyUserMessage = useCallback(async () => {
    if (!userMessageText) return;
    if (typeof window === "undefined" || !navigator?.clipboard?.writeText) {
      toast.error("Clipboard is not available in this browser.");
      return;
    }
    try {
      await navigator.clipboard.writeText(userMessageText);
      setIsUserCopied(true);
      toast.success("Message copied to clipboard.");
    } catch {
      toast.error("Couldn't copy the message.");
    }
  }, [userMessageText]);

  const resolveApiFilename = useCallback(async (fileId: string) => {
    try {
      const response = await fetch(`/api/files/${encodeURIComponent(fileId)}`);
      if (!response.ok) return null;
      const data = (await response.json()) as { filename?: string };
      return data.filename?.trim() || null;
    } catch {
      return null;
    }
  }, []);

  const handlePreviewLinkClick = useCallback(
    async (href: string, children: React.ReactNode) => {
      if (!onOpenInSidebar) return;

      const info = getDocLinkInfo(href, children);
      const fileId = getApiFileIdFromHref(href);
      const isApiFileLink = !!fileId;
      if (!info && !isApiFileLink) {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }

      // Only route through the API proxy when the href is a bare file ID (not an
      // HTTP URL). Supabase storage paths embed the Anthropic file ID
      // (e.g. "…/file_01abc-report.pdf"), so getApiFileIdFromHref can match
      // incorrectly — using the proxy URL for a valid Supabase URL breaks preview.
      const hrefIsHttpUrl = /^https?:\/\//i.test(href.trim());
      const previewHref = (fileId && !hrefIsHttpUrl) ? toApiFileContentHref(fileId) : href;
      const resolvedFilename = info?.filename ?? (fileId ? await resolveApiFilename(fileId) : null);
      if (!resolvedFilename) {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }

      onOpenInSidebar(resolvedFilename, previewHref);
    },
    [onOpenInSidebar, resolveApiFilename]
  );

  // Custom link component that opens previewable files in the sidebar instead of navigating away
  const docLinkComponents = useMemo(() => {
    if (!onOpenInSidebar) return undefined;
    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      a: ({ href, children, node: _node, ...rest }: any) => {
        if (href) {
          const info = getDocLinkInfo(href, children);
          const isApiFileLink = !!getApiFileIdFromHref(href);
          if (info || isApiFileLink) {
            return (
              <button
                type="button"
                className="font-medium text-primary underline underline-offset-4 hover:no-underline cursor-pointer"
                onClick={() => {
                  void handlePreviewLinkClick(href, children);
                }}
              >
                {children}
              </button>
            );
          }
        }
        return (
          <a href={href} rel="noreferrer" target="_blank" data-streamdown="link" {...rest}>
            {children}
          </a>
        );
      },
    };
  }, [onOpenInSidebar, handlePreviewLinkClick]);

  const handleArtifactSaved = useCallback(
    (artifact: ArtifactPart) => onArtifactSaved?.(message.id, artifact),
    [message.id, onArtifactSaved]
  );

  if (!isAssistant && isEditing) {
    return (
      <Message from={message.role}>
        <div className="ml-auto w-full max-w-[85%] flex flex-col gap-2">
          <Textarea
            autoFocus
            className="min-h-[80px] resize-none bg-background text-foreground"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") { setIsEditing(false); return; }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                const trimmed = editValue.trim();
                if (trimmed) {
                  onEdit?.(index, trimmed);
                  setIsEditing(false);
                }
              }
            }}
          />
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <InfoIcon size={12} />
            Use ↵ to save · Shift+↵ for new line
          </p>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!editValue.trim() || editValue.trim() === userMessageText}
              onClick={() => {
                const trimmed = editValue.trim();
                if (trimmed) {
                  onEdit?.(index, trimmed);
                  setIsEditing(false);
                }
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </Message>
    );
  }

  return (
    <>
      <Message from={message.role}>
        {/* File attachments — rendered outside the message bubble, above it */}
        {!isAssistant && message.parts.some((p) => p.type === "file") && (
          <Attachments variant="grid" className="ml-auto justify-end">
            {message.parts
              .filter((p) => p.type === "file")
              .map((p, i) => {
                const filePart = p as FileUIPart;
                const previewFilename = filePart.filename ?? filePart.url?.split("/").pop() ?? "document";
                const canPreview = !!filePart.url && !!onOpenInSidebar;
                return (
                  <div
                    key={i}
                    className={canPreview ? "cursor-pointer" : undefined}
                    onClick={canPreview ? () => onOpenInSidebar!(previewFilename, filePart.url!) : undefined}
                  >
                    <Attachment data={{ ...filePart, id: String(i) }}>
                      <AttachmentPreview />
                      <AttachmentTypeLabel />
                    </Attachment>
                  </div>
                );
              })}
          </Attachments>
        )}
        {mentionedAgentNickname ? (
          <div className="mx-auto flex w-[95%] justify-start">
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    "flex h-6 max-w-full shrink-0 items-center gap-1 rounded-full px-2.5 text-[11px] font-medium",
                    // Coloured by the stage it answered at, so a Draft answer never looks approved.
                    !isStreaming && messageAgentStage
                      ? AGENT_STAGE_STYLES[messageAgentStage].pill
                      : "bg-activated/10 text-activated"
                  )}
                >
                  <BotMessageSquareIcon aria-hidden="true" className="shrink-0" size={10} />
                  <span className="truncate">{mentionedAgentDisplayNickname}</span>
                  {isStreaming ? (
                    <span>· Creating response</span>
                  ) : messageAgentStage ? (
                    <span className="flex shrink-0 items-center gap-1">
                      ·
                      <AgentStageIcon stage={messageAgentStage} className="size-3 text-current" />
                      {AGENT_STAGE_LABELS[messageAgentStage]} agent
                    </span>
                  ) : (
                    <span>· Agent response</span>
                  )}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                {messageAgentStage
                  ? `${mentionedAgentNickname} · answered as a ${AGENT_STAGE_LABELS[messageAgentStage]} agent`
                  : mentionedAgentNickname}
              </TooltipContent>
            </Tooltip>
          </div>
        ) : null}
        {/* Relative wrapper lets the "@" pill overhang the bubble's corner —
            MessageContent itself is overflow-hidden and would clip it. */}
        <div className="relative">
        {userMentionNickname ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="absolute -top-2 -right-2 z-10 flex size-5 shrink-0 items-center justify-center rounded-full bg-activated text-[11px] font-medium text-background">
                @
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">Sent to @{userMentionNickname}</TooltipContent>
          </Tooltip>
        ) : null}
        {hasVisibleBubbleContent && (
        <MessageContent
          className={cn(!isAssistant && "relative")}
          data-testid={`chat-message-${message.role}`}
          {...collapse.bubbleProps}
        >
          {message.parts.map((part, i) => {
            // Skip reasoning parts — handled by the combined block below
            if (part.type === "reasoning") {
              if (i !== combinedBlockIndex) return null;
              // Falls through to combined block rendering at this index
            }

            // Combined Reasoning + tool steps block
            if (i === combinedBlockIndex && (part.type === "reasoning" || chainOfThoughtParts.length > 0)) {
              return (
                <div key={`reasoning-combined-${i}`} className="mb-2 flex flex-col gap-2">
                  {reasoningPartIndex !== -1 && (
                    <Reasoning defaultOpen={false} isStreaming={isStreaming && isAssistant && reasoningDisplay.isStreaming}>
                      <ReasoningTrigger
                        hasContent={reasoningDisplay.text.length > 0}
                      />
                      <ReasoningContent>{reasoningDisplay.text}</ReasoningContent>
                    </Reasoning>
                  )}
                  {showToolDetails && chainOfThoughtParts.length > 0 && (
                    <ChainOfThought defaultOpen={false}>
                      <ChainOfThoughtHeader>
                        {getChainOfThoughtLabel(chainOfThoughtParts, toolPrecedingTexts.descriptions, isStreaming && isAssistant)}
                      </ChainOfThoughtHeader>
                      <ChainOfThoughtContent>
                      <div className="space-y-1">
                      {chainOfThoughtParts.map(({ part: tp, index: idx }) => {
                        const isWebSearch = isWebSearchToolPart(tp);
                        const isWebFetch = isWebFetchToolPart(tp);
                        const isKnowledgeBase = isKnowledgeBaseToolPart(tp);
                        const isMyFiles = isMyFilesToolPart(tp);
                        const isMeetingNotes = isMeetingNotesToolPart(tp);
                        const isSharedChats = isSharedChatsToolPart(tp);
                        const isMemory = isMemoryToolPart(tp);
                        const isResolveDateTime = isResolveDateTimeToolPart(tp);
                        const isUpsertNotes = isUpsertRegisterNotesToolPart(tp);
                        const isDeleteNotes = isDeleteRegisterNotesToolPart(tp);
                        const isSearchNotes = isSearchRegisterNotesToolPart(tp);
                        const isQueryNotes = isQueryRegisterNotesToolPart(tp);
                        const isListRegisters = isListRegistersToolPart(tp);
                        const webResults = isWebSearch ? getWebSearchResults(tp.output) : null;

                        const modelDescription = toolPrecedingTexts.descriptions.get(idx);
                        const isDone = tp.state === "output-available";
                        const isCodeExec = isCodeExecutionInput(tp.input);
                        const isSkillExec = isSkillCodeExecutionInput(tp.input);
                        const isEditFile = isEditFileTool(tp);
                        const editFilePath = isEditFile && isRecord(tp.input) && typeof (tp.input as Record<string, unknown>).path === "string"
                          ? ((tp.input as Record<string, unknown>).path as string).replace(/^\/+/, "")
                          : null;
                        const editFileCommand = isEditFile && isRecord(tp.input) && typeof (tp.input as Record<string, unknown>).command === "string"
                          ? (tp.input as Record<string, unknown>).command as string
                          : null;
                        const toolName = getToolLabel(tp);
                        const codeLabel = isCodeExec
                          ? `${isDone ? "Executed" : "Executing"}: ${toolName}`
                          : isEditFile && editFilePath
                            ? editFileCommand === "view"
                              ? `${isDone ? "Viewed" : "Viewing"}: ${editFilePath}`
                              : `${isDone ? "Edited" : "Editing"}: ${editFilePath}`
                            : toolName;
                        let stepLabel: ReactNode = modelDescription ?? codeLabel;
                        let stepDescription: ReactNode | undefined =
                          modelDescription ? codeLabel : undefined;
                        if (isWebSearch && !modelDescription) {
                          const query = getWebSearchQuery(tp.input, tp.output);
                          if (query) stepLabel = query.charAt(0).toUpperCase() + query.slice(1);
                        }
                        const webFetchUrl = isWebFetch ? getWebFetchUrl(tp.input) : null;
                        const webFetchDomain = webFetchUrl ? getWebFetchDomain(webFetchUrl) : null;
                        const webFetchTitle = isWebFetch && isDone
                          ? getWebFetchTitle(tp.output) ?? webFetchDomain
                          : null;
                        if (isWebFetch) {
                          if (isDone && webFetchDomain && webFetchUrl) {
                            stepLabel = "Fetched";
                          } else if (webFetchDomain) {
                            stepLabel = `Fetching from ${webFetchDomain}`;
                          } else {
                            stepLabel = "Fetching...";
                          }
                        }
                        const kbAnswer = isKnowledgeBase ? getKnowledgeBaseAnswer(tp.output) : null;
                        const kbSourcesForStep = isKnowledgeBase
                          ? dedupeKnowledgeBaseSourcesByFile(getKnowledgeBaseSources(tp.output))
                          : [];
                        const resolveDateTimePayload = isResolveDateTime ? getResolveDateTimePayload(tp.output) : null;
                        if (isKnowledgeBase) {
                          const query = getKnowledgeBaseQuery(tp.input);
                          const docName = getKnowledgeBaseDocumentName(tp.output);
                          if (isDone) {
                            // `output.document` reflects a server-side default/fallback
                            // document and is populated independent of whether any chunk
                            // actually matched (chat-runtime.ts's knowledge_base tool) — it
                            // has shown up even on "no relevant information" results, so
                            // it's not trustworthy here. The real per-file list is
                            // kbSourcesForStep, rendered separately below.
                            stepLabel = query ? <>Searched <em className="italic">{query}</em></> : "Searched knowledge base";
                            stepDescription = undefined;
                          } else if (docName) {
                            stepLabel = `Reading: ${docName}`;
                          } else {
                            stepLabel = "Searching knowledge base...";
                          }
                        }
                        const myFilesAction = isMyFiles ? getMyFilesAction(tp.input) : null;
                        const myFilesAnswer = isMyFiles && myFilesAction === "search" ? getKnowledgeBaseAnswer(tp.output) : null;
                        const myFilesSourcesForStep = isMyFiles && myFilesAction === "search"
                          ? dedupeKnowledgeBaseSourcesByFile(getKnowledgeBaseSources(tp.output))
                          : [];
                        const myFilesListedFiles = isMyFiles && myFilesAction === "list" ? getMyFilesListedFiles(tp.output) : [];
                        if (isMyFiles) {
                          if (myFilesAction === "list") {
                            stepLabel = isDone ? `Listed ${myFilesListedFiles.length} ${myFilesListedFiles.length === 1 ? "file" : "files"}` : "Listing your files...";
                          } else {
                            const query = getKnowledgeBaseQuery(tp.input);
                            stepLabel = isDone
                              ? (query ? <>Searched your files for <em className="italic">{query}</em></> : "Searched your files")
                              : "Searching your files...";
                          }
                          stepDescription = undefined;
                        }
                        const meetingNotesSources = isMeetingNotes ? getMeetingNotesSources(tp.output) : [];
                        const meetingNotesAnswer = isMeetingNotes ? getMeetingNotesAnswer(tp.output) : null;
                        if (isMeetingNotes) {
                          const query = getMeetingNotesQuery(tp.input);
                          if (isDone && meetingNotesSources.length > 0) {
                            stepLabel = query ? query.charAt(0).toUpperCase() + query.slice(1) : "Searched meeting notes";
                          } else if (isDone) {
                            stepLabel = "No relevant meeting notes found";
                          } else {
                            stepLabel = "Searching meeting notes...";
                          }
                        }
                        const sharedChatsSources = isSharedChats ? getSharedChatsSources(tp.output) : [];
                        if (isSharedChats) {
                          const query = getSharedChatsQuery(tp.input);
                          if (isDone && sharedChatsSources.length > 0) {
                            stepLabel = query ? query.charAt(0).toUpperCase() + query.slice(1) : "Searched project chats";
                          } else if (isDone) {
                            stepLabel = "No relevant chats found";
                          } else {
                            stepLabel = "Searching project chats...";
                          }
                        }
                        const searchNotes = isSearchNotes ? getRegisterNotes(tp.output) : [];
                        const searchAnswer = isSearchNotes ? getRegisterAnswer(tp.output) : null;
                        if (isSearchNotes) {
                          const { registerKey, query } = getRegisterSearchInput(tp.input);
                          if (isDone && searchNotes.length > 0) {
                            stepLabel = query ? query.charAt(0).toUpperCase() + query.slice(1) : `Searched ${registerKey ?? "register"}`;
                          } else if (isDone) {
                            stepLabel = `No relevant notes found${registerKey ? ` in ${registerKey}` : ""}`;
                          } else {
                            stepLabel = `Searching ${registerKey ?? "register"}...`;
                          }
                        }
                        const queryNotes = isQueryNotes ? getRegisterNotes(tp.output) : [];
                        if (isQueryNotes) {
                          const { registerKey } = getRegisterQueryInput(tp.input);
                          if (isDone) {
                            stepLabel = `Queried ${registerKey ?? "register"} — ${queryNotes.length} ${queryNotes.length === 1 ? "match" : "matches"}`;
                          } else {
                            stepLabel = `Querying ${registerKey ?? "register"}...`;
                          }
                        }
                        if (isUpsertNotes) {
                          const { registerKey, notes } = getUpsertRegisterNotesInput(tp.input);
                          const n = notes.length;
                          stepLabel = isDone
                            ? `Stored ${n} ${n === 1 ? "note" : "notes"} in ${registerKey ?? "register"}`
                            : `Storing ${n || ""} ${n === 1 ? "note" : "notes"}...`;
                        }
                        if (isDeleteNotes) {
                          const { registerKey, externalKeys } = getDeleteRegisterNotesInput(tp.input);
                          const n = externalKeys.length;
                          stepLabel = isDone
                            ? `Deleted ${n || 1} ${(n || 1) === 1 ? "note" : "notes"} from ${registerKey ?? "register"}`
                            : `Deleting ${n || ""} ${(n || 1) === 1 ? "note" : "notes"}...`;
                        }
                        const listedRegisters = isListRegisters ? getListRegistersOutput(tp.output) : [];
                        if (isListRegisters) {
                          stepLabel = isDone
                            ? `Listed ${listedRegisters.length} ${listedRegisters.length === 1 ? "register" : "registers"}`
                            : "Listing registers...";
                        }
                        if (isResolveDateTime && resolveDateTimePayload?.resolved) {
                          if (isDone) {
                            stepLabel = "Resolved date/time";
                            stepDescription = undefined;
                          } else {
                            stepLabel = "Resolving date/time...";
                          }
                        }
                        if (isMemory) {
                          stepLabel = isDone
                            ? (getMemoryToolNotice(tp.input, tp.output)?.message ?? "Checked memory.")
                            : "Checking memory...";
                        }

                        return (
                          <ChainOfThoughtStep
                            key={idx}
                            icon={isWebSearch ? SearchIcon : isWebFetch ? GlobeIcon : isKnowledgeBase ? (isProjectChat ? ProjectKnowledgeBaseStepIcon : KnowledgeBaseStepIcon) : isMyFiles ? MyFilesStepIcon : isMeetingNotes ? MeetingNotesStepIcon : isSharedChats ? SharedChatsStepIcon : isMemory ? MemoryStepIcon : isResolveDateTime ? WrenchIcon : (isUpsertNotes || isDeleteNotes || isSearchNotes || isQueryNotes || isListRegisters) ? ProjectRegisterStepIcon : isSkillExec ? ScrollTextIcon : isCodeExec ? TerminalIcon : isEditFile ? PencilIcon : WrenchIcon}
                            label={stepLabel}
                            description={stepDescription}
                            status={getToolStepStatus(tp.state)}
                          >
                            {webResults && webResults.length > 0 ? (
                              <ChainOfThoughtSearchResults>
                                {webResults.map((r, ri) => (
                                  <ChainOfThoughtSearchResult key={ri}>
                                    {getResultTitle(r)}
                                  </ChainOfThoughtSearchResult>
                                ))}
                              </ChainOfThoughtSearchResults>
                            ) : isWebFetch && webFetchDomain && webFetchUrl ? (
                              <ChainOfThoughtSearchResults>
                                <ChainOfThoughtSearchResult>
                                  <a
                                    href={webFetchUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-0.5"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {webFetchTitle ?? webFetchDomain}
                                    <ArrowUpRightIcon className="size-3" />
                                  </a>
                                </ChainOfThoughtSearchResult>
                              </ChainOfThoughtSearchResults>
                            ) : isKnowledgeBase && kbSourcesForStep.length > 0 ? (
                              <Collapsible>
                                <CollapsibleTrigger className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
                                  {kbSourcesForStep.length} {kbSourcesForStep.length === 1 ? "source" : "sources"}
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="mt-2 space-y-1">
                                    {kbSourcesForStep.map((s, si) => (
                                      <button
                                        key={si}
                                        type="button"
                                        onClick={() =>
                                          handleSourceClick({
                                            id: s.id,
                                            fileId: s.fileId,
                                            content: s.content,
                                            chunkOrder: s.chunkOrder,
                                            similarity: s.similarity,
                                            rank: s.rank,
                                            fileName: s.fileName ?? "Document",
                                          })
                                        }
                                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent/50"
                                      >
                                        <FileIcon className="size-3 shrink-0 text-muted-foreground" />
                                        <span className="min-w-0 flex-1 truncate text-xs text-foreground">{s.fileName}</span>
                                      </button>
                                    ))}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            ) : isKnowledgeBase && kbAnswer ? (
                              <Collapsible>
                                <CollapsibleTrigger className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
                                  Summary
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="mt-2 rounded-md bg-muted/50 p-2 text-xs [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-4 [&_li]:mt-0.5">
                                    <MessageResponse
                                      isAnimating={false}
                                      disallowedElements={["script", "style"]}
                                    >
                                      {kbAnswer}
                                    </MessageResponse>
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            ) : isMyFiles && myFilesAction === "list" && myFilesListedFiles.length > 0 ? (
                              <Collapsible>
                                <CollapsibleTrigger className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
                                  {myFilesListedFiles.length} {myFilesListedFiles.length === 1 ? "file" : "files"}
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="mt-2 space-y-1">
                                    {myFilesListedFiles.map((f) => (
                                      <div key={f.id} className="flex items-center gap-2 rounded-md px-2 py-1.5">
                                        <FileIcon className="size-3 shrink-0 text-muted-foreground" />
                                        <span className="min-w-0 flex-1 truncate text-xs text-foreground">{f.name}</span>
                                      </div>
                                    ))}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            ) : isMyFiles && myFilesSourcesForStep.length > 0 ? (
                              <Collapsible>
                                <CollapsibleTrigger className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
                                  {myFilesSourcesForStep.length} {myFilesSourcesForStep.length === 1 ? "source" : "sources"}
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="mt-2 space-y-1">
                                    {myFilesSourcesForStep.map((s, si) => (
                                      <button
                                        key={si}
                                        type="button"
                                        onClick={() =>
                                          handleSourceClick({
                                            id: s.id,
                                            fileId: s.fileId,
                                            content: s.content,
                                            chunkOrder: s.chunkOrder,
                                            similarity: s.similarity,
                                            rank: s.rank,
                                            fileName: s.fileName ?? "Document",
                                          })
                                        }
                                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent/50"
                                      >
                                        <FileIcon className="size-3 shrink-0 text-muted-foreground" />
                                        <span className="min-w-0 flex-1 truncate text-xs text-foreground">{s.fileName}</span>
                                      </button>
                                    ))}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            ) : isMyFiles && myFilesAnswer ? (
                              <Collapsible>
                                <CollapsibleTrigger className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
                                  Summary
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="mt-2 rounded-md bg-muted/50 p-2 text-xs [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-4 [&_li]:mt-0.5">
                                    <MessageResponse
                                      isAnimating={false}
                                      disallowedElements={["script", "style"]}
                                    >
                                      {myFilesAnswer}
                                    </MessageResponse>
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            ) : isMeetingNotes && meetingNotesSources.length > 0 ? (
                              <Collapsible>
                                <CollapsibleTrigger className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
                                  {meetingNotesSources.length} {meetingNotesSources.length === 1 ? "excerpt" : "excerpts"}
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="mt-2 space-y-1.5">
                                    {meetingNotesSources.map((s, si) => (
                                      <MeetingNotesExcerpt key={si} source={s} projectId={projectId} />
                                    ))}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            ) : isMeetingNotes && meetingNotesAnswer ? (
                              <p className="text-xs text-muted-foreground">{meetingNotesAnswer}</p>
                            ) : isSharedChats && sharedChatsSources.length > 0 ? (
                              <Collapsible>
                                <CollapsibleTrigger className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
                                  {sharedChatsSources.length} {sharedChatsSources.length === 1 ? "excerpt" : "excerpts"}
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="mt-2 space-y-1.5">
                                    {sharedChatsSources.map((s, si) => (
                                      <div key={si} className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-4 [&_li]:mt-0.5">
                                        <MessageResponse isAnimating={false} disallowedElements={["script", "style"]}>
                                          {s.content.length > 240 ? `${s.content.slice(0, 240)}…` : s.content}
                                        </MessageResponse>
                                      </div>
                                    ))}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            ) : isResolveDateTime ? (
                              <ResolveDateTimeResult output={tp.output} />
                            ) : isSearchNotes && searchNotes.length > 0 ? (
                              <Collapsible>
                                <CollapsibleTrigger className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
                                  {searchNotes.length} {searchNotes.length === 1 ? "match" : "matches"}
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="mt-2 space-y-1.5">
                                    {searchNotes.map((d, di) => {
                                      const snippet = JSON.stringify(d.data);
                                      return (
                                        <div key={di} className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground leading-relaxed">
                                          <span className="font-medium text-foreground">{d.external_key}</span>{": "}
                                          {snippet.length > 200 ? `${snippet.slice(0, 200)}…` : snippet}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            ) : isSearchNotes && searchAnswer ? (
                              <p className="text-xs text-muted-foreground">{searchAnswer}</p>
                            ) : isQueryNotes && queryNotes.length > 0 ? (
                              <Collapsible>
                                <CollapsibleTrigger className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
                                  {queryNotes.length} {queryNotes.length === 1 ? "match" : "matches"}
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="mt-2 space-y-1.5">
                                    {queryNotes.map((d, di) => {
                                      const snippet = JSON.stringify(d.data);
                                      return (
                                        <div key={di} className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground leading-relaxed">
                                          <span className="font-medium text-foreground">{d.external_key}</span>{": "}
                                          {snippet.length > 200 ? `${snippet.slice(0, 200)}…` : snippet}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            ) : isUpsertNotes ? (
                              <UpsertRegisterResult
                                registerKey={getUpsertRegisterNotesInput(tp.input).registerKey}
                                notes={getUpsertRegisterNotesInput(tp.input).notes}
                              />
                            ) : isDeleteNotes && isDone ? (
                              <p className="text-xs text-muted-foreground">
                                Deleted <span className="font-medium text-foreground">
                                  {getDeleteRegisterNotesInput(tp.input).externalKeys.length || 1}
                                  {" "}
                                  {(getDeleteRegisterNotesInput(tp.input).externalKeys.length || 1) === 1 ? "note" : "notes"}
                                </span> from{" "}
                                {getDeleteRegisterNotesInput(tp.input).registerKey ?? "register"}
                              </p>
                            ) : isListRegisters && listedRegisters.length > 0 ? (
                              <Collapsible>
                                <CollapsibleTrigger className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground">
                                  {listedRegisters.length} {listedRegisters.length === 1 ? "register" : "registers"}
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="mt-2 space-y-1.5">
                                    {listedRegisters.map((s, si) => (
                                      <div key={si} className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground leading-relaxed">
                                        <span className="font-medium text-foreground">{s.name}</span>
                                        {s.description ? ` — ${s.description}` : ""}
                                      </div>
                                    ))}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            ) : isMemory ? (
                              (() => {
                                const memoryPath = getMemoryToolPath(tp.input);
                                if (!isDone || typeof tp.output !== "string") return null;
                                return (
                                  <div className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground leading-relaxed">
                                    {memoryPath ? <span className="font-medium text-foreground">{memoryPath}</span> : null}
                                    {memoryPath ? ": " : null}
                                    {tp.output.length > 200 ? `${tp.output.slice(0, 200)}…` : tp.output}
                                  </div>
                                );
                              })()
                            ) : !isWebSearch && !isWebFetch && !isKnowledgeBase && !isMyFiles && !isMeetingNotes && !isSharedChats && !isSearchNotes && !isQueryNotes && !isListRegisters && !isMemory && !isDeleteNotes ? (
                              isCodeExecutionInput(tp.input) ? (
                                <>
                                  <ToolScript
                                    input={tp.input}
                                    output={tp.output}
                                    errorText={tp.errorText}
                                  />
                                  {!widgetEditor && getCodeExecutionFiles(tp.output).length > 0 && (
                                    <ToolOutput
                                      output={tp.output}
                                      errorText={tp.errorText}
                                      hideLabel
                                      chatId={chatId}
                                      projectId={projectId}
                                      onOpenInSidebar={onOpenInSidebar}
                                      onArtifactSaved={handleArtifactSaved}
                                    />
                                  )}
                                </>
                              ) : isSkillCodeExecutionInput(tp.input) ? (
                                <ToolInput input={tp.input} />
                              ) : isEditFile ? (
                                <ToolOutput
                                  output={tp.output}
                                  errorText={tp.errorText}
                                  isEditFileTool
                                  chatId={chatId}
                                  projectId={projectId}
                                  onOpenInSidebar={onOpenInSidebar}
                                  onArtifactSaved={handleArtifactSaved}
                                />
                              ) : (
                                <>
                                  <ToolInput input={tp.input} />
                                  <ToolOutput
                                    output={tp.output}
                                    errorText={tp.errorText}
                                    chatId={chatId}
                                    projectId={projectId}
                                    onOpenInSidebar={onOpenInSidebar}
                                    onArtifactSaved={handleArtifactSaved}
                                  />
                                </>
                              )
                            ) : null}
                          </ChainOfThoughtStep>
                        );
                      })}
                    </div>
                      </ChainOfThoughtContent>
                    </ChainOfThought>
                  )}
                </div>
              );
            }

            if (part.type === "text" && isCompactionTextPart(part)) {
              // Anthropic streams the compaction summary as a tagged text block. The
              // summary content is internal — surface a live indicator while it
              // streams, then let the post-finish banner report the token savings
              // instead of dumping the raw summary into the transcript.
              if (part.state === "streaming") {
                return (
                  <div key={i} className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <FrameIcon isAnimating size={14} className="shrink-0" aria-hidden="true" />
                    <Shimmer as="span" duration={1.6}>
                      Compacting conversation context…
                    </Shimmer>
                  </div>
                );
              }
              return null;
            }

            if (part.type === "text") {
              // Render each text group once, joining across citation metadata
              // and preserving whitespace so lists and punctuation stay intact.
              const rawRenderableText = renderableTextParts.get(i);
              const renderableText = rawRenderableText && hasStructuredWoodsResult
                ? stripDuplicateStructuredResultTables(rawRenderableText)
                : rawRenderableText;
              if (renderableText === undefined) return null;
              if (!renderableText.trim()) return null;
              return (
                <MessageResponse
                  data-testid={isAssistant ? "assistant-message-text" : "user-message-text"}
                  key={i}
                  className={cn(
                    isAssistant && "chat-response-table",
                    !isAssistant && "[&_p]:whitespace-pre-wrap"
                  )}
                  isAnimating={isStreaming && isAssistant}
                  components={isAssistant ? docLinkComponents : undefined}
                >
                  {renderableText}
                </MessageResponse>
              );
            }

            // Persisted artifact from DB — lazy signed URL on click
            if ((part as unknown as ArtifactPart).type === "artifact") {
              if (widgetEditor) return null;
              if (!latestArtifacts(message.parts).includes(part as unknown as ArtifactPart)) return null;
              return (
                <PersistedArtifact
                  key={i}
                  artifact={part as unknown as ArtifactPart}
                  projectId={projectId}
                  onOpenInSidebar={onOpenInSidebar}
                />
              );
            }

            if (part.type === "source-document") {
              if (widgetEditor) return null;
              const hasPersistedArtifact = message.parts.some(
                (p) => (p as unknown as ArtifactPart).type === "artifact"
              );
              if (hasPersistedArtifact) return null;

              const source = getOpenAIContainerFileSource(part as SourceDocumentUIPart);
              if (!source) return null;

              return (
                <GeneratedFileArtifact
                  key={i}
                  fileId={source.fileId}
                  provider="openai"
                  containerId={source.containerId}
                  filename={source.filename}
                  chatId={chatId}
                  projectId={projectId}
                  onOpenInSidebar={onOpenInSidebar}
                  onArtifactSaved={handleArtifactSaved}
                />
              );
            }

            if (part.type === "dynamic-tool" || part.type.startsWith("tool-")) {
              const toolPart = part as ToolPart;

              // Memory calls without pending approvals get a chain-of-thought step
              // (chainOfThoughtParts above). A write also surfaces as a
              // standalone Notice, but anchored above the composer at the
              // ChatView level (see `activeToolNotice`/`getToolWriteNotice`)
              // rather than inline here, so it doesn't scroll away with the message.
              if (isMemoryToolPart(toolPart)) {
                if (!toolPart.approval || !["approval-requested", "approval-responded", "output-denied"].includes(toolPart.state)) return null;
                const memoryInput = (toolPart.input ?? {}) as Record<string, unknown>;
                const previewFields = [
                  ["Action", "command"], ["Memory", "path"],
                  ["From", "old_path"], ["To", "new_path"],
                  ["Content", "file_text"], ["Previous text", "old_str"],
                  ["New text", "new_str"], ["Add", "insert_text"],
                ];
                return (
                  <div key={i} className="px-3 my-2">
                    <Confirmation approval={toolPart.approval} state={toolPart.state}>
                      <ConfirmationTitle>
                        <ConfirmationRequest>Allow this change to your saved memory?</ConfirmationRequest>
                        <ConfirmationAccepted>Memory change approved.</ConfirmationAccepted>
                        <ConfirmationRejected>Memory change declined.</ConfirmationRejected>
                      </ConfirmationTitle>
                      <ConfirmationRequest>
                        {previewFields.map(([label, field]) => typeof memoryInput[field] === "string" && (
                          <p key={field} className="whitespace-pre-wrap break-words text-sm">
                            <strong>{label}:</strong> {memoryInput[field] as string}
                          </p>
                        ))}
                      </ConfirmationRequest>
                      <ConfirmationActions>
                        <ConfirmationAction type="button" variant="outline" onClick={() =>
                          onToolApprovalResponse({ id: toolPart.approval!.id, approved: false })
                        }>Deny</ConfirmationAction>
                        <ConfirmationAction type="button" onClick={() =>
                          onToolApprovalResponse({ id: toolPart.approval!.id, approved: true })
                        }>Confirm</ConfirmationAction>
                      </ConfirmationActions>
                    </Confirmation>
                  </div>
                );
              }

              const codeExecutionFiles = getCodeExecutionFiles(toolPart.output);
              if (codeExecutionFiles.length > 0) {
                // Repeated exports are transfer snapshots of the same app.
                // The editor owns their preview and download controls.
                if (widgetEditor) return null;
                // If the message already has a persisted artifact part, skip the
                // live FileArtifact — PersistedArtifact renders the same file below.
                const hasPersistedArtifact = message.parts.some(
                  (p) => (p as unknown as ArtifactPart).type === "artifact"
                );
                if (hasPersistedArtifact) return null;
                // Export snapshots can repeat across tool steps. Resolve the whole
                // response together and persist only its final named deliverables.
                const firstFilePart = message.parts.findIndex((p) =>
                  getCodeExecutionFiles((p as ToolPart).output).length > 0);
                if (i !== firstFilePart || isStreaming) return null;
                const fileIds = [...new Set(message.parts.flatMap((p) =>
                  getCodeExecutionFiles((p as ToolPart).output).map((file) => file.fileId)))];
                return (
                  <GeneratedFileArtifacts
                    key={i}
                    fileIds={fileIds}
                    chatId={chatId}
                    projectId={projectId}
                    onOpenInSidebar={onOpenInSidebar}
                    onArtifactSaved={handleArtifactSaved}
                  />
                );
              }

              // present_files: render file rows so the user can reopen the preview
              if (isPresentFilesTool(toolPart) && toolPart.state === "output-available") {
                if (widgetEditor) return null;
                const files = getPresentFilesOutput(toolPart.output);
                if (files?.length) {
                  return (
                    <div key={i} className="my-4 flex w-full flex-col gap-2">
                      {files.map((file) => (
                        <PresentedFileRow
                          key={file.filename}
                          filename={file.filename}
                          content={file.content}
                          projectId={projectId}
                          userId={userId}
                          onSaveHtmlToWidgets={onSaveHtmlToWidgets}
                          onOpenInSidebar={onOpenInSidebar}
                        />
                      ))}
                    </div>
                  );
                }
              }

              // ask_clarifying_question is gated behind human approval (see toolApproval
              // in the chat route) purely so it can render clickable option buttons —
              // it always resolves denied, with the user's pick packed into the reason
              // (see buildClarifyingAnswerReason in ai-elements/question.tsx).
              if (isAskClarifyingQuestionToolPart(toolPart)) {
                if (toolPart.state === "input-streaming" || toolPart.state === "input-available") {
                  return null;
                }
                return (
                  <div key={i} className="my-2">
                    <AskClarifyingQuestionCard
                      toolPart={toolPart}
                      onAnswer={(reason) =>
                        toolPart.approval &&
                        onToolApprovalResponse({ id: toolPart.approval.id, approved: false, reason })
                      }
                    />
                  </div>
                );
              }

              // Core Woods actions are registered internally by the chat routes
              // and never appear in the user-configurable Tools catalogue.
              if (isWoodsActionToolPart(toolPart)) {
                if (!shouldShowWoodsAction(getWoodsActionName(toolPart)!, toolPart.input)) return null;
                if (toolPart.state === "input-streaming" || toolPart.state === "input-available") {
                  return null;
                }
                return (
                  <div key={i} className="my-2 w-full">
                    <WoodsActionCard
                      toolPart={toolPart}
                      onApprovalResponse={onToolApprovalResponse}
                      onUseAgent={onUseAgent}
                      onAgentDeactivated={onAgentDeactivated}
                    />
                  </div>
                );
              }

              // Register note writes are batched into one tool call, so
              // one approval covers the complete proposed change.
              if (
                isUpsertRegisterNotesToolPart(toolPart) ||
                isDeleteRegisterNotesToolPart(toolPart)
              ) {
                if (toolPart.state === "input-streaming" || toolPart.state === "input-available") {
                  return null;
                }

                const isUpsert = isUpsertRegisterNotesToolPart(toolPart);
                const upsertInput = getUpsertRegisterNotesInput(toolPart.input);
                const deleteInput = getDeleteRegisterNotesInput(toolPart.input);
                const count = isUpsert
                  ? upsertInput.notes.length
                  : deleteInput.externalKeys.length;
                const registerKey = isUpsert ? upsertInput.registerKey : deleteInput.registerKey;
                const action = isUpsert ? "save" : "permanently delete";
                const objectLabel = `${count} ${count === 1 ? "record" : "records"} in “${registerKey ?? "project register"}”`;

                return (
                  <div key={i} className="px-3 my-2">
                    <Confirmation approval={toolPart.approval} state={toolPart.state}>
                      <ConfirmationTitle>
                        <ConfirmationRequest>
                          Woods AI wants to {action} {objectLabel}. Allow this?
                        </ConfirmationRequest>
                        <ConfirmationAccepted>Approved - {action} {objectLabel}.</ConfirmationAccepted>
                        <ConfirmationRejected>Denied - Woods AI will not {action} {objectLabel}.</ConfirmationRejected>
                      </ConfirmationTitle>
                      <ConfirmationActions>
                        <ConfirmationAction
                          variant="outline"
                          onClick={() =>
                            toolPart.approval &&
                            onToolApprovalResponse({ id: toolPart.approval.id, approved: false })
                          }
                        >
                          Deny
                        </ConfirmationAction>
                        <ConfirmationAction
                          onClick={() =>
                            toolPart.approval &&
                            onToolApprovalResponse({ id: toolPart.approval.id, approved: true })
                          }
                        >
                          Confirm
                        </ConfirmationAction>
                      </ConfirmationActions>
                    </Confirmation>
                  </div>
                );
              }

              // create_register / delete_register are gated behind
              // human approval (see toolApproval in the chat route) — render a visible
              // confirm/deny prompt instead of folding them into the collapsed
              // chain-of-thought log used for auto-executed tool calls.
              if (isCreateRegisterToolPart(toolPart) || isDeleteRegisterToolPart(toolPart)) {
                const isCreate = isCreateRegisterToolPart(toolPart);

                // For create: show an editable form so the user can review/adjust
                // the name, key, and description before the register is created.
                if (isCreate) {
                  // Don't mount until input is fully streamed — useState initializes once on
                  // mount, so returning null here ensures the component mounts fresh with
                  // the complete AI-suggested values when state reaches approval-requested.
                  if (toolPart.state === "input-streaming" || toolPart.state === "input-available") {
                    return null;
                  }
                  return (
                    <div key={i} className="px-3 my-2">
                      <RegisterCreateConfirmation
                        toolPart={toolPart}
                        onCancel={() =>
                          toolPart.approval &&
                          onToolApprovalResponse({ id: toolPart.approval.id, approved: false })
                        }
                        onSubmit={async (values) => {
                          if (!onRegisterCreate) throw new Error("Register creation not available");
                          const result = await onRegisterCreate(values);
                          if (!result.success) throw new Error(result.error ?? "Failed to create register");
                          if (toolPart.approval) {
                            const descPart = values.description ? `, description='${values.description}'` : "";
                            onToolApprovalResponse({
                              id: toolPart.approval.id,
                              approved: false,
                              reason: `User created the register via form. Details: key='${values.key}', name='${values.name}'${descPart}. The register is ready — do not create it again.`,
                            });
                          }
                        }}
                      />
                    </div>
                  );
                }

                // For delete: keep the standard confirm/deny UI.
                const deleteKey = getDeleteRegisterKey(toolPart.input);
                const deleteLabel = `permanently delete the "${deleteKey ?? ""}" register and all its notes`;

                return (
                  // Padding on the wrapper (not margin on Confirmation itself) keeps the
                  // alert's own w-full within MessageContent's overflow-hidden bounds —
                  // margin here would push a full-width box past the edge and get clipped.
                  <div key={i} className="px-3 my-2">
                    <Confirmation approval={toolPart.approval} state={toolPart.state}>
                      <ConfirmationTitle>
                        <ConfirmationRequest>
                          Woods AI wants to {deleteLabel}. Allow this?
                        </ConfirmationRequest>
                        <ConfirmationAccepted>Approved — {deleteLabel}.</ConfirmationAccepted>
                        <ConfirmationRejected>Denied — Woods AI will not {deleteLabel}.</ConfirmationRejected>
                      </ConfirmationTitle>
                      <ConfirmationActions>
                        <ConfirmationAction
                          variant="outline"
                          onClick={() =>
                            toolPart.approval &&
                            onToolApprovalResponse({ id: toolPart.approval.id, approved: false })
                          }
                        >
                          Deny
                        </ConfirmationAction>
                        <ConfirmationAction
                          onClick={() =>
                            toolPart.approval &&
                            onToolApprovalResponse({ id: toolPart.approval.id, approved: true })
                          }
                        >
                          Confirm
                        </ConfirmationAction>
                      </ConfirmationActions>
                    </Confirmation>
                  </div>
                );
              }

              // Tool steps are rendered inside the combined Reasoning block above;
              // skip individual tool parts here (memory-tool nulls handled above).
              if (i !== chainOfThoughtFirstIndex) return null;

              // If the combined block already rendered at an earlier reasoning part,
              // skip the first tool index too.
              if (chainOfThoughtFirstIndex !== combinedBlockIndex) return null;

              // combinedBlockIndex === chainOfThoughtFirstIndex and no reasoning part —
              // the combined block was already rendered in the i === combinedBlockIndex branch above.
              return null;
            }

            return null;
          })}
          {isAssistant && projectSources.length > 0 && (
            <Sources className="mt-3">
              <SourcesTrigger count={projectSources.length}>
                <div className="flex items-center gap-1.5">
                  <div className="flex -space-x-2">
                    {projectSources.slice(0, 4).map((item) => (
                      <div
                        key={item.key}
                        className="flex size-6 items-center justify-center rounded-full bg-muted"
                      >
                        {renderSourceIcon(item.kind, 12, isProjectChat ? "text-foreground" : "text-muted-foreground")}
                      </div>
                    ))}
                  </div>
                  <p className="font-medium">Sources</p>
                  <ChevronDownIcon className="h-4 w-4" />
                </div>
              </SourcesTrigger>
              <SourcesContent>
                {projectSources.map((item) => (
                  <button
                    key={item.key}
                    className="flex items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-muted text-foreground"
                    onClick={() => handleProjectSourceClick(item)}
                    type="button"
                  >
                    {renderSourceIcon(item.kind, 14, "shrink-0 text-muted-foreground")}
                    <span className="font-medium">{item.label}</span>
                    {item.kind === "file" && item.rank !== null && (
                      <span className="text-muted-foreground">#{item.rank}</span>
                    )}
                  </button>
                ))}
              </SourcesContent>
            </Sources>
          )}
          {isAssistant && webSources.length > 0 && (
            <Sources className="mt-3">
              <SourcesTrigger count={webSources.length} className="group" type="button">
                <span className="font-medium">Web Sources</span>
                <ChevronRightIcon size={16} className="transition-transform group-data-[state=open]:rotate-90" />
              </SourcesTrigger>
              <SourcesContent>
                {webSources.map((source) => (
                  <Source key={source.url} href={source.url} title={getResultTitle(source)}>
                    <span className="font-medium">{getResultTitle(source)}</span>
                  </Source>
                ))}
              </SourcesContent>
            </Sources>
          )}
          {/* Inline loading animation, aligned with message content and shown
              above the feedback/copy toolbar while the assistant is working. */}
          {isAssistant && showThinking && !reasoningDisplay.isStreaming && (
            <ThinkingIndicator />
          )}
          {collapse.showToggle && <MessageCollapseToggle collapse={collapse} />}
        </MessageContent>
        )}
        </div>
        {!isAssistant && userMessageText ? (
          <MessageToolbar className="chat-message-actions justify-end">
            <MessageActions>
              <MessageAction
                aria-label="Retry message"
                className="chat-message-action"
                label="Retry"
                onClick={() => onRetry?.(index)}
                tooltip="Retry"
              >
                <RefreshCwIcon className="chat-message-action-icon" />
              </MessageAction>
              <MessageAction
                aria-label="Edit message"
                className="chat-message-action"
                label="Edit"
                onClick={() => {
                  setEditValue(userMessageText);
                  setIsEditing(true);
                }}
                tooltip="Edit"
              >
                <PencilIcon className="chat-message-action-icon" />
              </MessageAction>
              <MessageAction
                aria-label="Copy message"
                className="chat-message-action"
                label="Copy"
                onClick={handleCopyUserMessage}
                tooltip={isUserCopied ? "Copied" : "Copy"}
              >
                <UserCopyActionIcon className="chat-message-action-icon" />
              </MessageAction>
            </MessageActions>
          </MessageToolbar>
        ) : null}
        {isAssistant && !isStreaming && (message.metadata as ChatMessageMetadata | undefined)?.stepLimitHit ? (
          <Notice
            className="mx-4 mb-1"
            icon={<InfoIcon className="size-4 shrink-0" />}
            actions={
              onContinue ? (
                <Button size="sm" type="button" variant="outline" onClick={onContinue}>
                  Continue
                </Button>
              ) : undefined
            }
          >
            {(() => {
              const meta = message.metadata as ChatMessageMetadata;
              const reasoning = meta.reasoningStepsUsed ?? 0;
              const maxReasoning = meta.maxReasoningSteps ?? 0;
              const total = meta.stepsUsed ?? 0;
              return `I hit my step limit here — ${reasoning} of ${maxReasoning} reasoning steps used across ${total} total steps.`;
            })()}
          </Notice>
        ) : null}
        {isAssistant && !isStreaming && (message.metadata as ChatMessageMetadata | undefined)?.compactionEvents?.length ? (
          <div className="mx-auto mt-2 w-[95%]">
            {(() => {
              const events = (message.metadata as ChatMessageMetadata).compactionEvents!;
              const freedTokens = events.reduce((sum, e) => sum + e.freedTokens, 0);
              const avgPercent = Math.round(
                events.reduce((sum, e) => sum + e.percentFreed, 0) / events.length
              );
              const times = events.length > 1 ? ` ${events.length}×` : "";
              return (
                <ChainOfThought defaultOpen={false}>
                  <ChainOfThoughtHeader>
                    <span className="flex items-center gap-2">
                      <ChevronsRightLeftIcon size={14} className="shrink-0" aria-hidden="true" />
                      {`Compacted context${times} · freed ${compactTokenCount(freedTokens)} tokens (${avgPercent}%)`}
                    </span>
                  </ChainOfThoughtHeader>
                  <ChainOfThoughtContent>
                    <div className="space-y-1">
                      {events.map((e, i) => (
                        <ChainOfThoughtStep
                          key={i}
                          icon={Minimize2Icon}
                          label={`${compactTokenCount(e.preTokens)} → ${compactTokenCount(e.postTokens)} tokens`}
                          description={`Freed ${compactTokenCount(e.freedTokens)} tokens (${Math.round(e.percentFreed)}% reduction)`}
                          status="complete"
                        />
                      ))}
                    </div>
                  </ChainOfThoughtContent>
                </ChainOfThought>
              );
            })()}
          </div>
        ) : null}
        {isAssistant && !isStreaming && (message.metadata as ChatMessageMetadata | undefined)?.interrupted ? (
          isEditingPrompt ? (
            <div className="mx-4 mb-1 flex flex-col gap-2 rounded-2xl border border-border bg-card px-4 py-3">
              <Textarea
                autoFocus
                className="min-h-[80px] resize-none bg-background text-foreground"
                value={editPromptValue}
                onChange={(e) => setEditPromptValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") { setIsEditingPrompt(false); return; }
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    const trimmed = editPromptValue.trim();
                    if (trimmed && previousIndex !== undefined) {
                      onEditPrevious?.(previousIndex, trimmed);
                      setIsEditingPrompt(false);
                    }
                  }
                }}
              />
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <InfoIcon size={12} />
                Use ↵ to send · Shift+↵ for new line
              </p>
              <div className="flex justify-end gap-2">
                <Button size="sm" type="button" variant="outline" onClick={() => setIsEditingPrompt(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  type="button"
                  disabled={!editPromptValue.trim()}
                  onClick={() => {
                    const trimmed = editPromptValue.trim();
                    if (trimmed && previousIndex !== undefined) {
                      onEditPrevious?.(previousIndex, trimmed);
                      setIsEditingPrompt(false);
                    }
                  }}
                >
                  Send
                </Button>
              </div>
            </div>
          ) : (
            <Notice
              className="mx-4 mb-1"
              icon={<InfoIcon className="size-4 shrink-0" />}
              actions={
                <>
                  {onEditPrevious ? (
                    <Button
                      size="sm"
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setEditPromptValue(previousUserMessageText ?? "");
                        setIsEditingPrompt(true);
                      }}
                    >
                      Edit prompt
                    </Button>
                  ) : null}
                  {onRetryPrevious && previousIndex !== undefined ? (
                    <Button
                      size="sm"
                      type="button"
                      variant="outline"
                      onClick={() => onRetryPrevious(previousIndex)}
                    >
                      Try again
                    </Button>
                  ) : null}
                </>
              }
            >
              Woods AI&apos;s response was interrupted.
            </Notice>
          )
        ) : null}
        {isAssistant && copyText ? (
          <MessageToolbar className="chat-message-actions">
            <MessageActions>
              <MessageAction
                aria-label="Like message"
                aria-pressed={chatFeedback === "positive"}
                className="chat-message-action chat-message-action-positive"
                disabled={feedbackDisabled}
                label="Like"
                onClick={onLike}
                tooltip={chatFeedback === "positive" ? "Liked" : "Like"}
              >
                <ThumbsUpIcon className="chat-message-action-icon" />
              </MessageAction>
              <MessageAction
                aria-label="Dislike message"
                aria-pressed={chatFeedback === "negative"}
                className="chat-message-action chat-message-action-negative"
                disabled={feedbackDisabled}
                label="Dislike"
                onClick={onDislike}
                tooltip={chatFeedback === "negative" ? "Disliked" : "Dislike"}
              >
                <ThumbsDownIcon className="chat-message-action-icon" />
              </MessageAction>
              <MessageAction
                aria-label="Copy message"
                className="chat-message-action"
                label="Copy"
                onClick={handleCopyMessage}
                tooltip={isCopied ? "Copied" : "Copy"}
              >
                <CopyActionIcon className="chat-message-action-icon" />
              </MessageAction>
              {messageAgentId && userId && previousUserMessageText && !isStreaming ? (
                <MessageAction aria-label="Create test from this response" label="Create test" tooltip="Create test"
                  className="chat-message-action" disabled={creatingTest} onClick={() => void handleCreateTest()}>
                  <PlusIcon size={16} />
                </MessageAction>
              ) : null}
            </MessageActions>
          </MessageToolbar>
        ) : null}
      </Message>
      {testDraft ? (
        <AgentTestDialog open onOpenChange={(open) => { if (!open) setTestDraft(null); }}
          test={testDraft} onSave={handleSaveChatTest}
          description="Check this response before using it as the model answer. Include any earlier context or attachment content needed to make the question standalone; chat history and files are not replayed." />
      ) : null}
      <Dialog open={kbChunkPreview !== null} onOpenChange={(open) => { if (!open) { setKbChunkPreview(null); setPreviewChunks([]); } }}>
        <DialogContent className="flex max-h-[85vh] w-[65vw] max-w-[65vw] flex-col gap-0 p-0 sm:max-w-[65vw]">
          <DialogHeader className="shrink-0 border-b border-border px-6 py-4">
            <DialogTitle className="flex items-center gap-2 text-base leading-snug">
              <FileIcon className="size-4 shrink-0" />
              {kbChunkPreview?.fileName}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-6 py-4">
            {chunksLoading ? (
              <Skeleton className="h-32 rounded-lg bg-muted" />
            ) : (
              <div>
                {previewChunks.map((chunk) => (
                  <div key={chunk.id} ref={chunk.isMatch ? matchedChunkRef : undefined} className={chunk.isMatch ? undefined : "opacity-20"}>
                    <MessageResponse
                      isAnimating={false}
                      disallowedElements={["script", "style"]}
                    >
                      {chunk.content}
                    </MessageResponse>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={meetingPreview !== null} onOpenChange={(open) => { if (!open) setMeetingPreview(null); }}>
        <DialogContent className="flex max-h-[85vh] w-[65vw] max-w-[65vw] flex-col gap-0 p-0 sm:max-w-[65vw]">
          <DialogHeader className="shrink-0 border-b border-border px-6 py-4">
            <DialogTitle className="flex items-center gap-2 text-base leading-snug">
              <AudioLinesIcon size={16} className="shrink-0" />
              {meetingPreview?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-6 py-4">
            <MessageResponse isAnimating={false} disallowedElements={["script", "style"]}>
              {meetingPreview?.content ?? ""}
            </MessageResponse>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

type PendingPrompt = {
  key: number;
  content: string;
  name: string;
  autoSend?: boolean;
  files?: FileUIPart[];
};

type PendingAttachment = {
  key: number;
  file: File;
};

const PromptDraftSync = ({
  pendingPrompt,
  onPromptConsumed,
  onSend,
  ready = true,
}: {
  pendingPrompt?: PendingPrompt | null;
  onPromptConsumed?: () => void;
  onSend?: (msg: PromptInputMessage) => void;
  ready?: boolean;
}) => {
  const controller = usePromptInputController();
  const lastAppliedKeyRef = useRef<number | null>(null);

  useEffect(() => {
    if (!ready) return;
    if (!pendingPrompt || lastAppliedKeyRef.current === pendingPrompt.key) {
      return;
    }

    lastAppliedKeyRef.current = pendingPrompt.key;
    onPromptConsumed?.();

    if (pendingPrompt.autoSend && onSend) {
      onSend({ text: pendingPrompt.content, files: pendingPrompt.files ?? [] });
      return;
    }

    const currentValue = controller.textInput.value.trim();
    const nextValue = currentValue
      ? `${currentValue}\n\n${pendingPrompt.content}`
      : pendingPrompt.content;

    controller.textInput.setInput(nextValue);
  }, [ready, controller, pendingPrompt, onPromptConsumed, onSend]);

  return null;
};

const AttachmentDraftSync = ({
  pendingAttachment,
  onAttachmentConsumed,
}: {
  pendingAttachment?: PendingAttachment | null;
  onAttachmentConsumed?: () => void;
}) => {
  const controller = usePromptInputController();
  const lastAppliedKeyRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      !pendingAttachment ||
      lastAppliedKeyRef.current === pendingAttachment.key
    ) {
      return;
    }

    lastAppliedKeyRef.current = pendingAttachment.key;
    controller.attachments.add([pendingAttachment.file]);
    onAttachmentConsumed?.();
  }, [controller, onAttachmentConsumed, pendingAttachment]);

  return null;
};

// ── Floating input ────────────────────────────────────────────────────────────

const ConversationInput = ({
  live,
  activeAgentLabel,
  myFilesLabel,
  incognito = false,
  latestAssistantMetadata,
  mentionableAgents = [],
  modelId,
  modelContextWindow,
  variant = "general",
  webSearch,
  onWebSearchChange,
  onSend,
  status,
  onStop,
  queuedCount = 0,
  activeToolNotice,
  onDismissToolNotice,
  showNotificationPrompt = false,
  onTurnOnNotifications,
  onDismissNotificationPrompt,
}: {
  live: ReturnType<typeof useLiveChat>;
  activeAgentLabel?: string;
  myFilesLabel?: string | null;
  incognito?: boolean;
  latestAssistantMetadata?: ChatUIMessage["metadata"];
  mentionableAgents?: MentionableAgent[];
  modelId: ModelId;
  modelContextWindow: number;
  variant?: "general" | "code";
  webSearch: boolean;
  onWebSearchChange: (v: boolean) => void;
  onSend: (msg: PromptInputMessage, agentMention: PendingAgentMention | null) => void;
  status: ReturnType<typeof useChat>["status"];
  onStop: () => void;
  queuedCount?: number;
  activeToolNotice?: (ToolWriteNotice & { toolCallId: string }) | null;
  onDismissToolNotice?: (toolCallId: string) => void;
  showNotificationPrompt?: boolean;
  onTurnOnNotifications?: () => void;
  onDismissNotificationPrompt?: () => void;
}) => {
  const router = useRouter();
  const reduceComposerMotion = useReducedMotion();
  const liveChatEnabled = useLiveChatFlag();
  const liveActive = live.status !== "idle";
  const personaState = live.status === "connecting" ? "thinking"
    : live.status !== "connected" ? "asleep"
    : live.playbackBlocked ? "idle"
    : live.speaking ? "speaking"
    : live.working ? "thinking"
    : live.muted ? "asleep" : "listening";
  const pendingMentionRef = useRef<PendingAgentMention | null>(null);
  const handleSubmit = useCallback(
    (msg: PromptInputMessage) => {
      if (liveActive) {
        if (msg.files.length) onSend({ ...msg, text: "I've attached these files for our voice conversation. Briefly acknowledge receipt and wait for my question." }, null);
        return;
      }
      const mention = pendingMentionRef.current;
      pendingMentionRef.current = null;
      onSend(msg, mention);
    },
    [onSend, liveActive]
  );

  return (
  // pointer-events-none on the full-width overlay so clicks pass through the
  // transparent sides; re-enabled on the centred content column
  (<div className="pointer-events-none absolute inset-x-0 bottom-0">
    <div className={`${COL} pointer-events-auto px-4 pb-4`}>
      {showNotificationPrompt ? (
        <ResponseNotificationPrompt
          onDismiss={() => onDismissNotificationPrompt?.()}
          onTurnOn={() => onTurnOnNotifications?.()}
        />
      ) : null}
      {activeToolNotice ? (
        <Notice
          className="mb-2 py-2"
          icon={
            activeToolNotice.kind === "memory" ? (
              <BrainIcon size={16} className="shrink-0 text-muted-foreground" />
            ) : (
              <DatabaseIcon className="size-4 shrink-0 text-muted-foreground" />
            )
          }
          actions={
            activeToolNotice.href && activeToolNotice.actionLabel ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7"
                onClick={() => router.push(activeToolNotice.href!)}
              >
                {activeToolNotice.actionLabel}
              </Button>
            ) : undefined
          }
          onDismiss={() => onDismissToolNotice?.(activeToolNotice.toolCallId)}
        >
          {activeToolNotice.message}
        </Notice>
      ) : null}
      {queuedCount > 0 && (
        <p className="mb-1.5 px-1 text-xs text-muted-foreground">
          {queuedCount === 1
            ? "Message queued — it'll send once the current response finishes."
            : `${queuedCount} messages queued — they'll send one after another.`}
        </p>
      )}
      <motion.div
        initial={false}
        animate={{ maxWidth: liveActive ? 384 : 896 }}
        transition={{ duration: reduceComposerMotion ? 0 : 0.24, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "mx-auto w-full",
          liveActive
            ? "overflow-hidden bg-background"
            : "overflow-hidden rounded-2xl shadow-lg",
          !liveActive && (variant === "code"
            ? "bg-background"
            : "border border-border bg-background")
        )}
      >
        <PromptInput
          inputGroupClassName={cn(
            liveActive && "border-0 bg-background shadow-none",
            variant === "code" &&
              "border-foreground/40 bg-background has-[[data-slot=input-group-control]:focus-visible]:border-foreground/40 has-[[data-slot=input-group-control]:focus-visible]:ring-0"
          )}
          onSubmit={handleSubmit}
          multiple
        >
          <PromptInputBody>
            <PromptInputAttachmentPreviews />
            {liveActive ? (
              <motion.div
                initial={reduceComposerMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: reduceComposerMotion ? 0 : 0.15 }}
                role="region" aria-label="Live voice" className="flex flex-col items-center gap-1 px-4 pt-3 text-center">
                <div aria-hidden="true">
                  <LivePersona variant="halo" state={personaState} className="size-20" />
                </div>
                <div role="status" className="sr-only">
                  {live.status === "connecting" ? "Connecting live voice…" : live.status === "closing"
                    ? "Ending live voice…" : live.playbackBlocked ? "Ready to play audio"
                    : live.speaking ? "Woods AI is speaking" : live.working ? "Thinking…" : live.muted ? "Microphone muted" : "Listening to you"}
                </div>
              </motion.div>
            ) : null}
            {!liveActive ? <AgentMentionTextarea
              data-testid="chat-input"
              disabled={liveActive}
              placeholder="Reply…"
              agents={mentionableAgents}
              pendingMentionRef={pendingMentionRef}
            /> : null}
          </PromptInputBody>
          <PromptInputFooter className={liveActive ? "justify-center gap-2 pb-3" : undefined}>
            <PromptInputTools>
              <PromptInputActionMenu>
                <PromptInputActionMenuTrigger
                  tooltip={{ content: "Add attachment", side: "top" }}
                  className={liveActive ? "size-10 rounded-full bg-muted text-foreground hover:bg-accent" : undefined}
                />
                <PromptInputActionMenuContent>
                  <PromptInputActionAddAttachments />
                  <PromptInputActionAddScreenshot />
                </PromptInputActionMenuContent>
              </PromptInputActionMenu>
              {liveActive ? <>
                  <PromptInputButton type="button" aria-label={live.muted ? "Unmute microphone" : "Mute microphone"}
                    aria-pressed={live.muted}
                    tooltip={{ content: live.muted ? "Unmute" : "Mute", side: "top" }}
                    className="size-10 rounded-full bg-muted text-foreground hover:bg-accent"
                    disabled={live.status !== "connected"} onClick={live.toggleMuted}>
                    {live.muted ? <MicOffIcon className="size-4" /> : <MicIcon size={16} />}
                  </PromptInputButton>
                  {live.playbackBlocked ? <Button type="button" size="sm" variant="outline"
                    onClick={() => void live.playAudio()}>Play audio</Button> : null}
                  <PromptInputButton type="button" aria-label="End live voice"
                    tooltip={{ content: "End live", side: "top" }}
                    className="size-10 rounded-full bg-destructive text-background hover:bg-destructive/90 hover:text-background"
                    onClick={live.stop} disabled={live.status === "closing"}>
                    <PhoneOffIcon className="size-4" />
                  </PromptInputButton>
              </> : null}
              {!liveActive ? <PromptInputButton
                aria-label={webSearch ? "Web search on" : "Web search off"}
                tooltip={{ content: webSearch ? "Web search on" : "Web search off", side: "top" }}
                onClick={() => onWebSearchChange(!webSearch)}
                className="text-muted-foreground"
              >
                <EarthIcon
                  className={webSearch ? "text-activated" : "text-muted-foreground"}
                  size={16}
                />
              </PromptInputButton> : null}
            </PromptInputTools>
            <PromptInputTools>
              {!liveActive ? <ChatUsageContext
                className="h-7 gap-2 rounded-full border border-border px-2 text-[11px] text-muted-foreground [&>span]:hidden sm:[&>span]:inline"
                metadata={latestAssistantMetadata}
                contextTokens={latestAssistantMetadata?.lastStepInputTokens ?? latestAssistantMetadata?.totalTokens}
                contextWindow={modelContextWindow}
                modelId={modelId}
              /> : null}
              {!liveActive && activeAgentLabel ? (
                <Badge
                  className={[
                    "h-7 max-w-40 min-w-0 gap-1 rounded-full bg-muted px-2 text-[11px]",
                    incognito ? "text-activated" : "text-foreground",
                  ].join(" ")}
                  variant="outline"
                >
                  <BotMessageSquareIcon aria-hidden="true" className="shrink-0" size={10} />
                  <span className="hidden truncate sm:inline">{activeAgentLabel}</span>
                </Badge>
              ) : null}
              {!liveActive && myFilesLabel ? (
                <Badge
                  className={[
                    "h-7 max-w-40 min-w-0 gap-1 rounded-full bg-muted px-2 text-[11px]",
                    incognito ? "text-activated" : "text-foreground",
                  ].join(" ")}
                  variant="outline"
                  title={`This chat is scoped to: ${myFilesLabel}`}
                >
                  <BookTextIcon aria-hidden="true" className="shrink-0" size={10} />
                  <span className="hidden truncate sm:inline">{myFilesLabel}</span>
                </Badge>
              ) : null}
              {!liveActive ? <VoiceInputButton /> : null}
              {liveActive ? <LiveAttachmentSubmit busy={live.working || status === "streaming" || status === "submitted"} /> : <>
              {liveChatEnabled ? <PromptInputButton
                type="button"
                aria-label={liveActive ? "End live voice" : "Start live voice"}
                aria-pressed={liveActive}
                tooltip={{ content: liveActive ? "End live voice" : "Live voice", side: "top" }}
                disabled={live.status === "closing" || (!liveActive && (status === "streaming" || status === "submitted"))}
                className={liveActive ? "text-activated" : "text-muted-foreground"}
                onClick={() => { if (liveActive) live.stop(); else void live.start(); }}
              >
                <AudioLinesIcon size={16} animateOnMount={live.status === "connected"} />
              </PromptInputButton> : null}
              <PromptInputSubmit onStop={onStop} status={status} />
              </>}
            </PromptInputTools>
          </PromptInputFooter>
        </PromptInput>
      </motion.div>
      {!liveActive ? <p className="mt-2 text-center text-xs text-muted-foreground">
        Woods AI can make mistakes. Please double-check responses.
      </p> : null}
    </div>
  </div>)
  );
};

// ── Presented file row (create_file / present_files tools) ───────────────────
// Claude-inspired DocumentCard: stacked-page icon, bold name, Kind·TYPE subtitle,
// ghost Download button. Clicking opens a self-contained right-docked preview panel.

const PRESENT_MIME: Record<string, string> = {
  html: "text/html", htm: "text/html",
  svg: "image/svg+xml",
  md: "text/markdown", mdx: "text/markdown",
  txt: "text/plain",
  xml: "text/xml",
  csv: "text/csv",
  json: "application/json",
};

function createPresentedFileUrl(filename: string, content: string) {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  const mime = PRESENT_MIME[ext] ?? "text/plain";
  if (ext === "md" || ext === "mdx") {
    return {
      url: `data:${mime};charset=utf-8,${encodeURIComponent(content)}`,
      revoke: false,
    };
  }

  return {
    url: URL.createObjectURL(new Blob([content], { type: mime })),
    revoke: true,
  };
}

const PresentedFileRow = ({
  filename,
  content,
  projectId,
  userId,
  onSaveHtmlToWidgets,
  onOpenInSidebar,
}: {
  filename: string;
  content: string;
  /** Current project, if this chat belongs to one — enables "Add to project". */
  projectId?: string | null;
  userId?: string | null;
  /** HTML output is an "widget" in this app's model (the Widgets viewer only
   * renders HTML) — save it into the project's Widgets library. Everything
   * else (md, txt, csv, code, ...) is a real project file, indexed for RAG. */
  onSaveHtmlToWidgets?: (name: string, url: string) => Promise<{ ok: boolean }>;
  onOpenInSidebar?: (
    filename: string,
    url: string,
    onAddToProject?: () => Promise<{ ok: boolean; error?: string }>
  ) => void;
}) => {
  const previewUrlRef = useRef<string | null>(null);
  const [addToProjectState, setAddToProjectState] = useState<"idle" | "pending" | "added">("idle");

  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  const mime = PRESENT_MIME[ext] ?? "text/plain";
  const isMarkdown = ext === "md" || ext === "mdx";
  const isHtmlFile = ext === "html" || ext === "htm";
  const canAddToProject = !!projectId && (isHtmlFile ? !!onSaveHtmlToWidgets : !!userId);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  const handleAddToProject = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    if (!projectId) return { ok: false, error: "No project" };
    setAddToProjectState("pending");

    if (isHtmlFile && onSaveHtmlToWidgets) {
      const preview = createPresentedFileUrl(filename, content);
      try {
        const result = await onSaveHtmlToWidgets(filename.replace(/\.[^.]+$/, ""), preview.url);
        setAddToProjectState(result.ok ? "added" : "idle");
        return result;
      } finally {
        if (preview.revoke) URL.revokeObjectURL(preview.url);
      }
    }

    if (!userId) return { ok: false, error: "No user" };
    const result = await uploadPresentedFileToProject({ filename, content, mimeType: mime, projectId, userId });
    if (result.ok) {
      setAddToProjectState("added");
      toast.success("Added to project files.");
    } else {
      setAddToProjectState("idle");
      toast.error("Failed to add to project.");
    }
    return result;
  }, [content, filename, mime, isHtmlFile, projectId, userId, onSaveHtmlToWidgets]);

  const handlePreview = useCallback(() => {
    if (!onOpenInSidebar) return;
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }

    const preview = createPresentedFileUrl(filename, content);
    if (preview.revoke) previewUrlRef.current = preview.url;
    onOpenInSidebar(filename, preview.url, canAddToProject ? handleAddToProject : undefined);
  }, [content, filename, onOpenInSidebar, canAddToProject, handleAddToProject]);

  const handleDownload = useCallback(() => {
    const url = isMarkdown
      ? `data:${mime};charset=utf-8,${encodeURIComponent(content)}`
      : URL.createObjectURL(new Blob([content], { type: mime }));
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    if (!isMarkdown) URL.revokeObjectURL(url);
  }, [isMarkdown, mime, content, filename]);

  return (
    <GeneratedFileRow
      filename={filename}
      onClickDownload={handleDownload}
      onClickPreview={onOpenInSidebar ? handlePreview : undefined}
      onAddToProject={canAddToProject ? handleAddToProject : undefined}
      isAddingToProject={addToProjectState === "pending"}
      isAddedToProject={addToProjectState === "added"}
    />
  );
};

// ── Public component ──────────────────────────────────────────────────────────

export type ChatViewProps = {
  userId?: string | null;
  isSuperAdmin?: boolean;
  /** All agents the user can @mention for a one-off, per-turn answer. */
  agents?: DbAgent[];
  activeAgent?: DbAgent | null;
  /** Raw agent UUID from the URL — used as agentId fallback when activeAgent hasn't resolved yet (agents list still loading). */
  pendingAgentId?: string | null;
  /**
   * True while the `agents` list is still being fetched. Used to hold off
   * auto-sending a deep-linked first message when `pendingAgentId` is set but
   * hasn't resolved to a full `DbAgent` yet — otherwise that first turn would
   * silently run with no agent prompt/tools/knowledge base at all.
   */
  agentsLoading?: boolean;
  /**
   * The chat's agent is no longer visible to the user (share removed, moved
   * out of their team, archived). The history stays readable; sending is
   * turned off so later turns don't silently run without the agent.
   */
  agentUnavailable?: boolean;
  activeAgentLabel?: string;
  activeTeamName?: string | null;
  projectId?: string | null;
  /** Current sharing level of the active chat. */
  chatSharing?: string | null;
  /** user_id of the chat owner — gates auto-title generation for non-owners. */
  chatOwnerId?: string | null;
  chatName?: string | null;
  chatModel?: string | null;
  onActiveModelChange?: (modelId: string, provider: string | undefined) => void;
  isUserNameReady?: boolean;
  userName: string;
  /**
   * The persisted Supabase chat ID, or null for a temp/new chat.
   * When null, a DB record will be created on the first message sent.
   */
  chatId: string | null;
  /**
   * The project_chats ID for this session, when the chat belongs to a project.
   * Mutually exclusive with chatId: when set, messages go to project_messages.
   */
  projectChatId?: string | null;
  /**
   * Override for whether this is a fresh session. Pass true when the chat DB
   * record was pre-created (e.g. agent activation) so title generation still
   * runs even though chatId is non-null at mount.
   */
  isNewSession?: boolean;
  /** Keeps the entire chat session in memory only. */
  incognito?: boolean;
  /** Called once the DB record has been created with the real UUID. */
  onChatCreated: (id: string) => void;
  /** Called once a project_chats record has been created with the real UUID. */
  onProjectChatCreated?: (args: { id: string; projectId: string; name: string }) => void;
  onActivateAgent?: () => void;
  /** Persistently switches the current conversation to an agent selected from an in-chat result card. */
  onUseAgent?: (agentId: string) => void;
  /** Synchronizes the open chat after the hidden deactivate-agent action succeeds. */
  onAgentDeactivated?: () => void;
  /** Switches the open session to the project_chat created by the hidden move action. */
  onChatMovedToProject?: (args: { projectChatId: string; projectId: string; name: string }) => void;
  pendingPrompt?: PendingPrompt | null;
  startLiveChat?: boolean;
  onLiveChatStarted?: () => void;
  onPromptConsumed?: () => void;
  pendingAttachment?: PendingAttachment | null;
  onAttachmentConsumed?: () => void;
  titleOverride?: string;
  /** Called to create the DB chat record. Returns the new chat ID or null. */
  createChat: (params: {
    name: string;
    model: string;
    agentId?: string;
    projectId?: string;
    /** Persisted so the chat reopens on the surface that created it. */
    type?: DbChatType;
    chatTools?: PersistedChatTool[] | null;
  }) => Promise<string | null>;
  /** Called to create a project_chats record when chatting within a project. */
  createProjectChat?: (params: {
    name: string;
    model: string;
    agentId?: string;
    projectId: string;
    chatTools?: PersistedChatTool[] | null;
  }) => Promise<string | null>;
  updateChatName: (
    chatId: string,
    name: string,
    options?: { manuallyEdited?: boolean }
  ) => Promise<boolean>;
  /** Persists a per-chat model override on a personal chat. */
  updateChatModel?: (chatId: string, model: string) => Promise<boolean>;
  /** Persists a per-chat model override on a project chat. */
  updateProjectChatModel?: (projectChatId: string, model: string) => Promise<boolean>;
  updateProjectChatName?: (projectChatId: string, name: string) => Promise<boolean>;
  deleteChat: (chatId: string) => Promise<boolean>;
  deleteProjectChat?: (projectChatId: string) => Promise<boolean>;
  onChatDeleted: () => void;
  /** Called after "Remove from project" succeeds, with the id of the newly-created
   * personal chat, so the caller can navigate to it instead of resetting to blank. */
  onChatMovedToPersonal?: (chatId: string) => void;
  /** Called after a project chat is successfully renamed, so the caller can
   * update its own display-name state (the header title doesn't come from a
   * reactive list the way personal chats' does — see handleRename). */
  onProjectChatRenamed?: (name: string) => void;
  onProjectChanged?: () => void;
  /** Called after a successful assistant response has fully resolved. */
  onResponseFinished?: () => void;
  /**
   * Initial per-chat tool settings, loaded from chats.chat_tools.
   * null for new chats (not yet saved) or old chats created before this
   * feature was added — in both cases the effective user/agent settings apply.
   */
  initialChatTools?: Record<string, boolean> | null;
  /**
   * Tool state overrides for brand-new chats (e.g. passed from a project page
   * where the user toggled web search before starting the chat). Applied on top
   * of the user's default settings when initialChatTools is absent.
   */
  initialToolOverrides?: Partial<Record<string, boolean>> | null;
  /**
   * myFiles scope loaded from chats.chat_tools, or supplied for a brand-new
   * chat from the Knowledge Base page's "Start chat" action. Scopes the tool
   * to these file/collection ids instead of the caller's entire knowledge base.
   */
  initialMyFilesConfig?: MyFilesScopeConfig | null;
  /**
   * Short human-readable label for the myFiles scope above (e.g. a file name,
   * "3 files", or a collection name). Shown as a pill next to the agent badge
   * so the chat visibly reflects it was started from the Knowledge Base page.
   */
  initialMyFilesLabel?: string | null;
  /**
   * Model chosen for a brand-new chat before it exists in the DB (e.g. from a
   * project page's composer). Seeds chatModelOverride so it behaves exactly
   * like a header-dropdown pick made right after the chat is created.
   */
  initialModelOverride?: ModelId | null;
  /** Called when the user saves an HTML sidebar preview to the Widgets library. */
  onSaveHtmlToWidgets?: (name: string, url: string) => Promise<{ ok: boolean }>;
  /**
   * Chat mode. "code" makes the code-execution sandbox the only filesystem:
   * attachments are uploaded into it and the chat file tools are not
   * registered, so a file never exists both on disk and as prompt text.
   * Defaults to "general".
   */
  chatMode?: ChatMode;
  /** Whether to show the temporary-chat toggle button. */
  showIncognitoToggle?: boolean;
  /** Called when the user clicks the temporary-chat toggle. */
  onToggleIncognito?: () => void;
  /**
   * Page-supplied controls for the chat's top-right icon cluster, rendered
   * beside the temporary-chat toggle in both the desktop header and the
   * floating mobile cluster. Used by /code for its sandbox-files trigger.
   */
  headerActions?: React.ReactNode;
  headerLeading?: React.ReactNode;
  widgetEditing?: WidgetEditingContext;
  sendDisabled?: boolean;
  onActivityChange?: (active: boolean) => void;
  onWidgetDraftSaved?: (revision: number) => void;
};

export const ChatView = ({
  userId,
  isSuperAdmin = false,
  agents,
  activeAgent,
  pendingAgentId,
  agentsLoading = false,
  agentUnavailable = false,
  activeAgentLabel,
  activeTeamName,
  projectId,
  chatSharing,
  chatOwnerId,
  chatName,
  chatModel,
  onActiveModelChange,
  isUserNameReady = true,
  userName,
  chatId,
  projectChatId,
  isNewSession: isNewSessionProp,
  incognito = false,
  onChatCreated,
  onProjectChatCreated,
  onActivateAgent,
  onUseAgent,
  onAgentDeactivated,
  onChatMovedToProject,
  pendingPrompt,
  startLiveChat = false,
  onLiveChatStarted,
  onPromptConsumed,
  pendingAttachment,
  onAttachmentConsumed,
  titleOverride,
  createChat,
  createProjectChat,
  updateChatName,
  updateChatModel,
  updateProjectChatModel,
  updateProjectChatName,
  deleteChat,
  deleteProjectChat,
  onChatDeleted,
  onChatMovedToPersonal,
  onProjectChatRenamed,
  onProjectChanged,
  onResponseFinished,
  initialChatTools,
  initialToolOverrides,
  initialMyFilesConfig,
  initialMyFilesLabel,
  initialModelOverride,
  onSaveHtmlToWidgets,
  chatMode = "general",
  showIncognitoToggle = false,
  onToggleIncognito,
  headerActions: extraHeaderActions,
  headerLeading,
  widgetEditing,
  sendDisabled = false,
  onActivityChange,
  onWidgetDraftSaved,
}: ChatViewProps) => {
  const recoveryRouter = useRouter();
  const { models: globalModels } = useGlobalModels();
  const [forkingChat, setForkingChat] = useState(false);
  const [forkError, setForkError] = useState<string | null>(null);
  const [dismissedRecoveryError, setDismissedRecoveryError] = useState<Error | null>(null);
  const { settings } = useUserSettings(userId);
  const [sidebarDoc, setSidebarDoc] = useState<{
    filename: string;
    url: string;
    onAddToProject?: () => Promise<{ ok: boolean; error?: string }>;
  } | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(() =>
    typeof window !== "undefined" ? Math.round(window.innerWidth * 0.4) : 420
  );
  const handleOpenInSidebar = useCallback(
    (
      filename: string,
      url: string,
      onAddToProject?: () => Promise<{ ok: boolean; error?: string }>
    ) => {
      setSidebarDoc({ filename, url, onAddToProject });
    },
    []
  );
  const handleRegisterCreate = useCallback(async (
    values: RegisterCreateValues,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!projectId) return { success: false, error: "No project selected" };
    const { error } = await supabase.from("project_registers").insert({
      project_id: projectId,
      key: values.key,
      name: values.name,
      description: values.description ?? null,
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  }, [projectId]);
  const [localSharing, setLocalSharing] = useState<string | null | undefined>(chatSharing);
  useEffect(() => { setLocalSharing(chatSharing); }, [chatSharing]);
  const [chatFeedback, setChatFeedback] = useState<"positive" | "negative" | null>(null);
  const [isFeedbackUpdating, setIsFeedbackUpdating] = useState(false);
  const [isNegativeFeedbackDialogOpen, setIsNegativeFeedbackDialogOpen] = useState(false);
  const [negativeFeedbackNote, setNegativeFeedbackNote] = useState("");
  const [consentToReadChat, setConsentToReadChat] = useState(false);
  const [isNegativeFeedbackSubmitting, setIsNegativeFeedbackSubmitting] = useState(false);
  const agentSettings = useMemo(
    () => getAgentSettings(activeAgent),
    [activeAgent]
  );
  // @mention support: the full agent list (for the autocomplete) and a
  // lookup map (for resolving a persisted message's agent_id to a label).
  const mentionableAgents = useMemo<MentionableAgent[]>(
    () => (agents ?? []).map((a) => ({ id: a.id, nickname: a.nickname })),
    [agents]
  );
  const agentsById = useMemo(
    () => new Map((agents ?? []).map((a) => [a.id, a])),
    [agents]
  );
  // Resolve from user profile + agent overrides (agent's authoritative tool list).
  // In a project the agent adds to the chat's tools instead of replacing them:
  // the union of the user/project defaults and the agent's tools.
  const defaultEffectiveTools = useMemo(() => {
    const agentTools = resolveTools(settings, agentSettings);
    if (!projectId || !agentSettings) return agentTools;
    const userTools = resolveTools(settings);
    return Object.fromEntries(
      TOOL_IDS.map((toolId) => [toolId, userTools[toolId] || agentTools[toolId]])
    ) as typeof agentTools;
  }, [agentSettings, settings, projectId]);
  // Per-chat overrides. Initialized from chats.chat_tools when loading an
  // existing chat; null for new chats (falls back to defaultEffectiveTools).
  const [chatTools, setChatTools] = useState<Record<string, boolean> | null>(
    initialChatTools ?? null
  );
  // Base tool state: for new chats with overrides (e.g. from project page toggles),
  // merge overrides on top of user defaults. Otherwise use defaults directly.
  const baseToolsForNewChat = useMemo(
    () => initialToolOverrides ? { ...defaultEffectiveTools, ...initialToolOverrides } : defaultEffectiveTools,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialToolOverrides is props, stable after mount
    [defaultEffectiveTools]
  );
  // Effective tools for this chat: chat_tools when set, else user/agent defaults.
  // knowledgeBase is always agent-determined — not stored in chat_tools.
  const effectiveTools = useMemo(() => {
    if (!chatTools) return baseToolsForNewChat;
    // Re-apply agent-configured tool enables on top of the per-chat snapshot.
    // This ensures that tools added to an agent after a chat was first created
    // are honoured without forcing users to start a new chat.
    // knowledgeBase is always agent-determined (never in chat_tools).
    // Note: per-chat disables (user explicitly turned a tool off) are preserved
    // because we only apply tools the agent explicitly enables — we do not
    // force-disable tools the agent doesn't include.
    const agentEnables = agentSettings?.tools != null
      ? Object.fromEntries(agentSettings.tools.map((t) => [t, true] as const))
      : {};
    return {
      ...chatTools,
      ...agentEnables,
      knowledgeBase: defaultEffectiveTools.knowledgeBase,
    } as Record<string, boolean> & typeof defaultEffectiveTools;
  }, [baseToolsForNewChat, chatTools, defaultEffectiveTools, agentSettings]);
  const toolResolution = useMemo(
    () => resolveToolSettings(settings, agentSettings),
    [agentSettings, settings]
  );
  // In-session model override picked from the header dropdown. Takes effect
  // immediately; persisted to chats/project_chats when the record exists (new
  // chats pick it up via createChat/createProjectChat's model param).
  const [chatModelOverride, setChatModelOverride] = useState<ModelId | null>(
    initialModelOverride ?? null
  );
  useEffect(() => {
    if (!initialModelOverride || !isValidModelId(initialModelOverride)) return;
    // Capture a project deep-link choice that arrived after the first render,
    // without replacing a model the user has since picked in this chat.
    setChatModelOverride((current) => current ?? initialModelOverride);
  }, [initialModelOverride]);
  const activeModel = useMemo<ModelId>(() => {
    const selected = resolveActiveChatModel({
      sessionOverride: chatModelOverride,
      // Keep the one-shot project-page choice live as a prop. It can arrive
      // after ChatView's first render when Home consumes the deep-link params.
      initialOverride: initialModelOverride,
      persistedModel: chatModel,
      agentModel: activeAgent?.model,
      userModel: settings.model,
    });
    if ((widgetEditing || chatMode === "code") && !globalModels.some((model) => model.model_id === selected && model.is_active &&
      (model.provider === "anthropic" || (!widgetEditing && model.provider === "openai")))) {
      const claudeModels = globalModels.filter((model) => model.provider === "anthropic" && model.is_active);
      return claudeModels.find((model) => model.model_id === DEFAULT_MODEL)?.model_id ?? claudeModels[0]?.model_id ?? DEFAULT_MODEL;
    }
    return selected;
  }, [activeAgent?.model, chatModel, chatModelOverride, initialModelOverride, settings.model, widgetEditing, chatMode, globalModels]);
  useEffect(() => {
    onActiveModelChange?.(activeModel, globalModels.find((model) => model.model_id === activeModel)?.provider);
  }, [activeModel, globalModels, onActiveModelChange]);
  const activeModelContextWindow = useMemo(
    () => globalModels.find((model) => model.model_id === activeModel)?.context_window ?? 200_000,
    [activeModel, globalModels]
  );
  const [effortSelection, setEffortSelection] = useState<{ modelId: string; effort: ChatEffort } | null>(null);
  const chatEffort = effortSelection?.modelId === activeModel
    ? resolveChatEffort(activeModel, effortSelection.effort)
    : "default";
  const reasoningEffortRef = useRef<ChatEffort>(chatEffort);
  useEffect(() => { reasoningEffortRef.current = chatEffort; }, [chatEffort]);
  const handleEffortChange = (effort: ChatEffort) => {
    const resolved = resolveChatEffort(activeModel, effort);
    reasoningEffortRef.current = resolved;
    setEffortSelection({ modelId: activeModel, effort: resolved });
  };
  const modelSource =
    chatModelOverride && isValidModelId(chatModelOverride)
      ? "chat"
      : initialModelOverride && isValidModelId(initialModelOverride)
        ? "chat"
        : chatModel && isValidModelId(chatModel)
          ? "chat"
          : activeAgent?.model && isValidModelId(activeAgent.model)
            ? "agent"
            : "user";

  // Fetch DB tool records so we can pass executor_config to the route
  const { tools: dbTools } = useTools();

  // Build the activeTools array from resolved flags + DB executor_configs.
  // Returns undefined when dbTools haven't loaded so the route falls back to
  // legacy boolean synthesis. Returns [] (no tools) when dbTools are loaded
  // but none are enabled — the route treats [] as "explicitly no tools."
  const resolvedActiveTools = useMemo<ActiveToolConfig[] | undefined>(() => {
    if (dbTools.length === 0) return undefined;
    const kbConfig = activeAgent?.knowledge_base_config ?? null;
    const wsConfig = activeAgent?.web_search_config ?? null;
    const rdtConfig = activeAgent?.resolve_date_time_config ?? {
      default_timezone: settings.resolveDateTimeConfig.defaultTimezone,
    };
    const builtinConfigs = TOOL_IDS
      .filter((toolId) => effectiveTools[toolId])
      .flatMap((toolId) => {
        const uuid = BUILTIN_TOOL_UUID[toolId];
        const dbTool = dbTools.find((t) => t.id === uuid);
        if (!dbTool) return [];
        return [{
          toolId,
          executorConfig: dbTool.executor_config as ActiveToolConfig["executorConfig"],
          ...(toolId === "knowledgeBase" && kbConfig ? { config: kbConfig as Record<string, unknown> } : {}),
          ...(toolId === "webSearch" && wsConfig ? { config: wsConfig as Record<string, unknown> } : {}),
          ...(toolId === "resolveDateTime" ? { config: rdtConfig as Record<string, unknown> } : {}),
          ...(toolId === "myFiles" && initialMyFilesConfig
            ? { config: initialMyFilesConfig as Record<string, unknown> }
            : {}),
        }];
      });

    // Custom tools (source_type='custom') assigned to this agent — agent-scoped
    // only, same as Knowledge Base. Not available in plain (non-agent) chats.
    const assignedCustomToolIds = activeAgent?.tools ?? null;
    const customToolConfigs: ActiveToolConfig[] = assignedCustomToolIds
      ? dbTools
          .filter((t) => t.source_type === "custom" && assignedCustomToolIds.includes(t.id))
          .map((t) => ({
            toolId: t.id,
            executorConfig: t.executor_config as ActiveToolConfig["executorConfig"],
            executorType: t.executor_type,
            schema: t.schema as ActiveToolConfig["schema"],
            name: t.name,
            description: t.description,
            config: {},
          }))
      : [];

    return [...builtinConfigs, ...customToolConfigs];
  }, [
    dbTools,
    effectiveTools,
    activeAgent?.knowledge_base_config,
    activeAgent?.web_search_config,
    activeAgent?.resolve_date_time_config,
    activeAgent?.tools,
    settings.resolveDateTimeConfig.defaultTimezone,
    initialMyFilesConfig,
  ]);

  const modelRef = useRef<ModelId>(activeModel);
  const activeToolsRef = useRef<ActiveToolConfig[]>(resolvedActiveTools);
  const userIdRef = useRef<string | null | undefined>(userId);
  const accessTokenRef = useRef<string | null>(null);
  const incognitoRef = useRef<boolean>(incognito);
  // Gates `resume` below — see resumable-stream-gate.ts. Flips true once
  // getSession() resolves, so the reconnect request always has whatever
  // token is available rather than firing with headers: undefined.
  const [accessTokenReady, setAccessTokenReady] = useState(false);

  // Keep the access token fresh so the transport can send it as a Bearer header
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      accessTokenRef.current = data.session?.access_token ?? null;
      setAccessTokenReady(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      accessTokenRef.current = session?.access_token ?? null;
    });
    return () => subscription.unsubscribe();
  }, []);

  // Whether persisted messages have finished loading from the DB — declared
  // here (rather than beside the effect that sets it, further down) because
  // it must be in scope for the useChat() `resume` gate below. See
  // resumable-stream-gate.ts for why resume must not fire before this (and
  // accessTokenReady) are both true.
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const chatModeRef = useRef<ChatMode>(chatMode);
  const woodsProjectRef = useRef<string | null>(activeTeamName ?? null);
  const projectIdRef = useRef<string | null>(projectId ?? null);
  const agentIdRef = useRef<string | null>(activeAgent?.id ?? null);
  const agentPromptRef = useRef<string | null>(activeAgent?.prompt?.trim() || null);
  // Which agent actually answers the *current* turn — the chat's normal agent,
  // or a one-off @mentioned agent. Read by onFinish to persist messages.agent_id.
  const turnAgentIdRef = useRef<string | null>(activeAgent?.id ?? null);
  // Edit/retry while a turn is still streaming: queue the resend and wait for
  // status to settle back to "ready" before firing, so it never races the
  // in-flight request (the AI SDK shares one `activeResponse` slot per chat —
  // two concurrent makeRequest calls stomp on it and throw/duplicate replies).
  const pendingResendRef = useRef<{
    parts: ChatUIMessage["parts"];
    agentId: string | null;
  } | null>(null);
  const agentContextRef = useRef<AgentRuntimeContext | null>(
    activeAgent
      ? {
          includeProfileContext: activeAgent.include_profile_context,
          includeTeamInstructions: activeAgent.include_team_instructions,
          includeWoodsContext: activeAgent.include_woods_context,
          includeNzContext: activeAgent.include_nz_context,
          includeCivilEngineeringContext:
            activeAgent.include_civil_engineering_context,
        }
      : null
  );
  useEffect(() => { modelRef.current = activeModel; }, [activeModel]);
  useEffect(() => { activeToolsRef.current = resolvedActiveTools; }, [resolvedActiveTools]);
  useEffect(() => { userIdRef.current = userId; }, [userId]);
  useEffect(() => { incognitoRef.current = incognito; }, [incognito]);
  useEffect(() => { chatModeRef.current = chatMode; }, [chatMode]);
  useEffect(() => { woodsProjectRef.current = activeTeamName ?? null; }, [activeTeamName]);
  useEffect(() => { projectIdRef.current = projectId ?? null; }, [projectId]);
  useEffect(() => {
    agentIdRef.current = activeAgent?.id ?? null;
    agentPromptRef.current = activeAgent?.prompt?.trim() || null;
    agentContextRef.current = activeAgent
      ? {
          includeProfileContext: activeAgent.include_profile_context,
          includeTeamInstructions: activeAgent.include_team_instructions,
          includeWoodsContext: activeAgent.include_woods_context,
          includeNzContext: activeAgent.include_nz_context,
          includeCivilEngineeringContext:
            activeAgent.include_civil_engineering_context,
        }
      : null;
  }, [activeAgent]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      return;
    }

    console.debug("[ChatView] resolved chat settings", {
      chatId,
      agentId: activeAgent?.id ?? null,
      agentName: activeAgent?.name ?? null,
      model: {
        source: modelSource,
        effective: activeModel,
        user: settings.model,
        chat: chatModel ?? null,
        agent: activeAgent?.model ?? null,
        overridden:
          modelSource !== "user" &&
          activeModel !== settings.model,
      },
      activeTools: (resolvedActiveTools ?? []).map((t) => t.toolId),
      toolResolution,
    });
  }, [
    activeAgent?.id,
    activeAgent?.model,
    activeAgent?.name,
    activeModel,
    chatId,
    chatModel,
    modelSource,
    resolvedActiveTools,
    settings.model,
    toolResolution,
  ]);

  const setChatTool = useCallback(
    (toolId: ToolId, nextValue: boolean) => {
      const base = chatTools ?? baseToolsForNewChat;
      const next = { ...base, [toolId]: nextValue };
      setChatTools(next);
      if (chatIdRef.current) {
        supabase
          .from("chats")
          .update({ chat_tools: buildPersistedChatTools(next as Record<ToolId, boolean>, initialMyFilesConfig) })
          .eq("id", chatIdRef.current)
          .then(({ error }) => {
            if (error) console.error(`[ChatView] updateChatTools (${toolId}) error:`, error);
          });
      }
    },
    [chatTools, baseToolsForNewChat, initialMyFilesConfig]
  );

  const handleWebSearchChange = useCallback(
    (nextValue: boolean) => setChatTool("webSearch", nextValue),
    [setChatTool]
  );

  // Per-chat model switch: general and project chats only — agent chats keep
  // the agent's configured model. Applies to the next message immediately and
  // persists on the chat record so the choice survives reloads.
  const handleModelChange = useCallback(
    (model: ModelId) => {
      reasoningEffortRef.current = "default";
      setEffortSelection(null);
      setChatModelOverride(model);
      const activeProjectChatId = projectChatIdRef.current;
      const activeChatId = chatIdRef.current;
      if (activeProjectChatId && updateProjectChatModel) {
        void updateProjectChatModel(activeProjectChatId, model).then((ok) => {
          if (!ok) toast.error("Failed to save the model for this chat.");
        });
      } else if (activeChatId && updateChatModel) {
        void updateChatModel(activeChatId, model).then((ok) => {
          if (!ok) toast.error("Failed to save the model for this chat.");
        });
      }
    },
    [updateChatModel, updateProjectChatModel]
  );

  // True when this session started fresh. Defaults to chatId being null at mount,
  // but the parent can override (e.g. agent chats pre-create the DB record).
  const isNewSession = useRef(isNewSessionProp ?? chatId === null).current;

  // Keep a ref to the current chatId so persistence callbacks can read it
  // without closing over stale values.
  const chatIdRef = useRef<string | null>(chatId);
  useEffect(() => { chatIdRef.current = chatId; }, [chatId]);

  const projectChatIdRef = useRef<string | null>(projectChatId ?? null);
  useEffect(() => { projectChatIdRef.current = projectChatId ?? null; }, [projectChatId]);

  const { saveUnsaved, markSaved, reset } = useMessagePersistence(chatIdRef, projectChatIdRef);
  const hasShownNotificationPromptRef = useRef(false);
  const notificationPromptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  // artifact manifest: aiSdkMessageId → ArtifactPart[]
  const artifactsRef = useRef<Map<string, ArtifactPart[]>>(new Map());
  // mapping populated after saveUnsaved: aiSdkMessageId → supabase messages.id
  const supabaseIdMapRef = useRef<Map<string, string>>(new Map());

  const handleArtifactSaved = useCallback(
    async (aiSdkMsgId: string, artifact: ArtifactPart) => {
      const current = artifactsRef.current.get(aiSdkMsgId)
        ?? latestArtifacts(messagesRef.current.find((m) => m.id === aiSdkMsgId)?.parts ?? []);
      const updated = latestArtifacts([...current, artifact]);
      artifactsRef.current.set(aiSdkMsgId, updated);

      // If the message is already in the DB, update its parts immediately
      const supabaseId = supabaseIdMapRef.current.get(aiSdkMsgId);
      if (supabaseId) {
        // Merge artifact parts with the full existing message parts so text,
        // reasoning, and tool parts aren't lost on reload.
        const msg = messagesRef.current.find((m) => m.id === aiSdkMsgId);
        const existingParts = (msg?.parts ?? []) as unknown[];
        const nonArtifactParts = existingParts.filter(
          (p) => (p as { type: string }).type !== "artifact"
        );
        const mergedParts = [...nonArtifactParts, ...updated];
        const msgTable = projectChatIdRef.current ? "project_messages" : "messages";
        await supabase.from(msgTable).update({ parts: mergedParts }).eq("id", supabaseId);
      }
    },
    []
  );

  // Reset saved-message tracking whenever we switch to a different chat (personal or project).
  const prevChatKey = useRef<string | null>(chatId ?? projectChatId ?? null);
  useEffect(() => {
    const activeChatKey = chatId ?? projectChatId ?? null;
    if (prevChatKey.current !== activeChatKey) {
      reset();
      artifactsRef.current = new Map();
      supabaseIdMapRef.current = new Map();
      prevChatKey.current = activeChatKey;
    }
  }, [chatId, projectChatId, reset]);

  const widgetEditingRef = useRef(widgetEditing);
  widgetEditingRef.current = widgetEditing;
  const transport = useChatTransport(
    modelRef,
    activeToolsRef,
    userIdRef,
    accessTokenRef,
    chatIdRef,
    incognitoRef,
    woodsProjectRef,
    projectIdRef,
    agentIdRef,
    agentPromptRef,
    agentContextRef,
    projectChatIdRef,
    chatModeRef,
    reasoningEffortRef,
    widgetEditingRef
  );
  // Refs used inside onFinish (declared here so useChat callback can read latest values).
  const incognitoRefForFinish = useRef(incognito);
  useEffect(() => { incognitoRefForFinish.current = incognito; }, [incognito]);

  // Captured once per mount (lazy initializer), never updated afterward.
  // useChat recreates its entire internal Chat instance — discarding
  // whatever's currently rendered — the moment its `id` option changes from
  // null/undefined to a non-null value (see @ai-sdk/react's
  // shouldRecreateChat). For a chat created mid-session, chatId/projectChatId
  // start out null and only become real IDs after the first exchange
  // completes — feeding that transition straight into useChat's `id` would
  // wipe the just-rendered messages the instant the real id arrived. A chat
  // reopened from history already has a stable id from the first render, so
  // this only ever "activates" (goes non-undefined) for genuinely new mounts,
  // never mid-flight within one.
  const [stableChatIdentity] = useState(() => chatId ?? projectChatId ?? undefined);
  const resumeEnabled = shouldEnableStreamResume({
    hasChatIdentity: Boolean(stableChatIdentity),
    incognito,
    historyLoaded,
    accessTokenReady,
  });

  const [approvalSignatures] = useState(() => new ToolApprovalSignatures());
  const appliedLiveAgentActions = useRef(new Set<string>());
  const lastFinishedMessageIdRef = useRef<string | undefined>(undefined);
  const liveDelegationRef = useRef<{
    resolve: () => void;
    reject: (error: Error) => void;
    update: (message?: ChatUIMessage, final?: boolean) => void;
  } | null>(null);
  const { messages, setMessages, sendMessage: sdkSendMessage, status, error, stop, addToolApprovalResponse: sdkAddToolApprovalResponse } = useChat<ChatUIMessage>({
    transport,
    onError: (error) => {
      console.error("[ChatView] response failed:", error);
      liveDelegationRef.current?.reject(error);
      liveDelegationRef.current = null;
    },
    // Identifies the chat to the SDK's own resumption bookkeeping — paired
    // with `resume` below and prepareReconnectToStreamRequest in
    // useChatTransport, this is what triggers the GET .../stream reconnect
    // request once resume flips true. Undefined (no id, no resume) for a
    // chat that hasn't been created yet (this session) or is incognito —
    // there's no server-side row to reattach to either way.
    id: stableChatIdentity,
    // `resume` is reactive (@ai-sdk/react re-fires its resume effect
    // whenever this value changes), so it deliberately starts false and
    // flips true only once historyLoaded/accessTokenReady catch up — see
    // resumable-stream-gate.ts. Firing resumeStream() any earlier snapshots
    // off empty/incomplete SDK message state and/or sends the reconnect
    // request with no Authorization header, either of which loses whatever
    // the server is still generating for a chat reopened mid-stream.
    resume: resumeEnabled,
    // Once the user approves/denies a create_register or
    // delete_register call, automatically resume the stream so the
    // model doesn't need another user turn to continue.
    sendAutomaticallyWhen: (options) => {
      // Restore before the SDK starts the continuation request, not after it.
      setMessages(approvalSignatures.restore(options.messages));
      return lastAssistantMessageIsCompleteWithApprovalResponses(options);
    },
    // Persist on completion only. The AI SDK calls onFinish unconditionally —
    // including on abort/error — so guard here or a stopped/failed turn's
    // partial assistant message (e.g. reasoning with no text yet) leaks into
    // Supabase as an orphaned row.
    onFinish: ({ messages: finishedMessages, isAbort, isError }) => {
      finishedMessages = denyUnansweredToolApprovals(approvalSignatures.restore(finishedMessages));
      // Queued voice work can start before the next React effect runs.
      lastFinishedMessageIdRef.current = finishedMessages.at(-1)?.id;
      setMessages(finishedMessages);
      if (isAbort) {
        const lastMsg = finishedMessages[finishedMessages.length - 1];
        if (lastMsg?.role === "assistant") {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === lastMsg.id
                ? { ...m, metadata: { ...(m.metadata as ChatMessageMetadata | undefined), interrupted: true } }
                : m
            )
          );
        } else {
          // Stopped before the SDK ever appended an assistant message (e.g.
          // the user hit Stop while it was still "submitted", before the
          // first token arrived) — there's nothing to tag, so add an empty
          // placeholder just to carry the interrupted banner.
          setMessages((prev) => [
            ...prev,
            { id: crypto.randomUUID(), role: "assistant", parts: [], metadata: { interrupted: true } },
          ]);
        }
      }
      if (isAbort || isError) {
        liveDelegationRef.current?.reject(new Error("The backend response did not complete."));
        liveDelegationRef.current = null;
        return;
      }

      // If a tool approval is still pending, the turn isn't truly over — a second
      // stream will follow once the user responds. Skip saving now so the message
      // is persisted with its final resolved state (approval-responded / output-denied)
      // rather than the intermediate approval-requested state.
      const hasPendingApproval = finishedMessages.some((m) =>
        m.role === "assistant" &&
        (m.parts ?? []).some((p) => (p as { state?: string }).state === "approval-requested")
      );
      if (hasPendingApproval) return;

      // Apply only results from the turn that just completed. Replaying old
      // action cards must never switch the user's current agent again.
      const completedTurn = finishedMessages.at(-1);
      for (const part of completedTurn?.parts ?? []) {
        if (part.type !== "tool-activate_agent") continue;
        const action = part as unknown as { state: string; toolCallId: string; approval?: { approved?: boolean }; output?: { activated?: boolean; agent?: { id?: string } } };
        if (action.state === "output-available" && action.output?.activated && action.output.agent?.id && action.approval?.approved === true && !appliedLiveAgentActions.current.has(action.toolCallId)) {
          appliedLiveAgentActions.current.add(action.toolCallId);
          onUseAgent?.(action.output.agent.id);
        }
      }

      if (liveDelegationRef.current) {
        liveDelegationRef.current.update(finishedMessages.at(-1), true);
        liveDelegationRef.current.resolve();
        liveDelegationRef.current = null;
      }
      if (incognitoRefForFinish.current) return;

      // No toast fired directly from here, deliberately: this callback only
      // ever fires for the chat that's currently mounted as ChatView, i.e.
      // the one already on screen — showing "your response is ready" over a
      // response the user is looking at would just be noise. If the user
      // opted in via the "Turn on response notifications" prompt and then
      // navigated to a different chat/page before this turn finished, the
      // toast comes from the app-shell listener instead (see
      // use-global-response-notifications.ts / notify-on-finish.ts), driven
      // off the DB write landing rather than this callback.

      onResponseFinished?.();

      const movedDestination = chatIdRef.current && !projectChatIdRef.current
        ? getMovedChatDestination(finishedMessages)
        : null;
      if (movedDestination) {
        // The action has deleted the personal parent row. Flip both refs
        // synchronously before saveUnsaved reads them so this completed turn
        // lands in project_messages under its new parent.
        chatIdRef.current = null;
        projectChatIdRef.current = movedDestination.projectChatId;
      }

      const cid = chatIdRef.current;
      const projectCid = projectChatIdRef.current;

      // Use the messages passed by the SDK — these include the just-finished
      // assistant message with its metadata, unlike messagesRef.current which
      // is updated by a useEffect and is stale at this point.
      saveUnsaved(finishedMessages, modelRef.current, turnAgentIdRef.current).then((mapping) => {
        mapping.forEach((supabaseId, aiSdkId) => {
          supabaseIdMapRef.current.set(aiSdkId, supabaseId);
        });
        if (movedDestination) {
          onChatMovedToProject?.({
            ...movedDestination,
            name: chatName ?? "Chat",
          });
        }
        // Marking "seen" on completion is NOT done here. This client's own
        // saveUnsaved resolving doesn't mean the turn is *fully* persisted —
        // a chat reopened mid-stream (possibly across several
        // navigate-away-and-back cycles) is still being generated by the
        // *original* request, kept alive server-side via resumable streams,
        // whose own persistence write can commit many seconds (observed:
        // 25s+, scaling with reconnect count) after this client's — no fixed
        // buffer can safely absorb that. Marking seen is instead driven by
        // the Realtime INSERT event itself, at the app-shell level (see
        // use-global-response-notifications.ts), which fires at the exact
        // moment whichever writer actually wins lands its row — no guessing.
      });
      // Compute cumulative token usage from the fresh messages list so that
      // total_tokens includes the current turn (conversationUsageMetadataRef
      // is still stale here because its useEffect hasn't fired yet).
      const assistantMetadata = finishedMessages
        .filter((m) => m.role === "assistant")
        .map((m) => (m as ChatUIMessage).metadata);
      const usageMetadata = sumChatMessageMetadata(assistantMetadata);
      const netTokens = getChatNetTotalTokens(usageMetadata);
      // Recomputed the same way as netTokens (from scratch over every
      // assistant message in the chat) rather than incremented turn-by-turn,
      // so a null cost (unpriced turn, e.g. pricing wasn't set yet) never
      // permanently understates the total — it's just absent from this sum
      // until every turn has a priced cost.
      const totalCost = sumChatMessageCost(assistantMetadata);
      if ((cid || projectCid) && netTokens !== undefined && netTokens > 0) {
        supabase
          .from(projectCid ? "project_chats" : "chats")
          .update({
            total_tokens: netTokens,
            ...(totalCost !== null && { total_cost: totalCost }),
          })
          .eq("id", projectCid ?? cid!)
          .then(({ error }) => {
            if (error) console.error("[ChatView] failed to update total_tokens:", error);
          });
      }
    },
  });

  // Fetches this chat's persisted messages and, if any exist, applies them
  // to SDK state. Returns the row count so callers can tell "found nothing"
  // apart from "errored" without inspecting state. Shared by the initial
  // history load below and the awaiting-resume poll a bit further down —
  // both need the exact same query/mapping.
  const loadPersistedMessages = useCallback(async (): Promise<number> => {
    if (!chatId && !projectChatId) return 0;

    const query = projectChatId
      ? supabase
          .from("project_messages")
          .select("id, role, content, parts, agent_id, agent_stage")
          .eq("project_chat_id", projectChatId)
      : supabase
          .from("messages")
          .select("id, role, content, parts, token_usage, agent_id, agent_stage")
          .eq("chat_id", chatId!);

    const { data, error } = await query.order("sequence_number", { ascending: true });
    if (error) {
      console.error("[ChatView] load messages:", error);
      return 0;
    }
    if (!data || data.length === 0) return 0;

    const uiMessages: ChatUIMessage[] = data.map((m) => {
      const artifactParts = Array.isArray(m.parts)
        ? (m.parts as ArtifactPart[]).filter((p) => p?.type === "artifact")
        : [];
      const agentId = (m as { agent_id?: string | null }).agent_id ?? null;
      const agentStage = (m as { agent_stage?: ChatMessageMetadata["agentStage"] }).agent_stage ?? null;
      const usageMetadata = tokenUsageToMessageMetadata(
        (m as { token_usage?: TokenUsage | null }).token_usage ?? null
      );
      return {
        id: m.id,
        role: m.role as ChatUIMessage["role"],
        parts: Array.isArray(m.parts) && m.parts.length > 0
          ? (m.parts as ChatUIMessage["parts"])
          : [{ type: "text" as const, text: m.content }, ...artifactParts] as ChatUIMessage["parts"],
        metadata: agentId ? { ...usageMetadata, agentId, ...(agentStage ? { agentStage } : {}) } : usageMetadata,
      };
    });
    setMessages(uiMessages.map(cleanLiveRequestMessage));
    markSaved(uiMessages.map((m) => m.id));
    return uiMessages.length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, projectChatId]);

  // True while we've asked the SDK to resumeStream() but nothing has shown
  // up yet — the reconnect round-trips through Redis pub/sub to ask the
  // still-running generation for its buffered-so-far output (see
  // resumable-stream-gate.ts and getResumableStreamContext's doc comment),
  // which is a handful of network hops rather than instant. Drives a
  // skeleton instead of the empty/welcome state below so a chat reopened
  // mid-stream doesn't look broken while that handshake is in flight.
  // Capped: an existing chat that legitimately has nothing to resume (its
  // row exists but no turn ever completed) shouldn't show a skeleton
  // forever.
  const [isAwaitingResume, setIsAwaitingResume] = useState(false);
  useEffect(() => {
    if (!resumeEnabled || messages.length > 0) {
      setIsAwaitingResume(false);
      return;
    }
    setIsAwaitingResume(true);
    // Falling back to the empty state here is always recoverable — the
    // reconciliation poll below has no timeout of its own and keeps
    // watching regardless — so this cap only bounds how long a skeleton
    // sits on screen with nothing yet to show, not correctness. Generous
    // on purpose: a heavy tool-call-and-file turn has taken close to a
    // minute to even start persisting in practice.
    const giveUp = setTimeout(() => setIsAwaitingResume(false), 90_000);
    return () => clearTimeout(giveUp);
  }, [resumeEnabled, messages.length]);

  // Separate from the skeleton above, and deliberately NOT gated on
  // messages.length: resumeStream() only ever relays the assistant's own
  // *output* — the reconnecting client never receives the user message
  // that started the turn, because it isn't part of the resumable SSE
  // stream, and it isn't in the DB yet either while the turn is still
  // running (both messages are only written together, at the very end —
  // see saveCompletedMessagesServerSide). So the instant resume delivers
  // its first chunk, the user's own message is missing from state, and
  // nothing about that fixes itself just because *something* is now on
  // screen — messages.length is already > 0 from the assistant reply
  // alone. Keep re-checking the DB — regardless of what's already
  // rendered — until it actually has the real, persisted pair, then let
  // that overwrite whatever the live resume alone assembled.
  //
  // A 204 from the one-shot resumeStream() attempt doesn't reliably mean
  // "nothing is generating" either — server-side, active_stream_id is
  // cleared *before* the finished turn is persisted (see
  // resumable-stream.ts's onEnd) — so this is also what recovers a resume
  // that landed in that gap and got nothing at all.
  //
  // Gated on status === "ready" (read via ref, not a dep — this must not
  // restart the interval every time status flips) rather than firing on
  // whatever tick happens to land: loadPersistedMessages()'s setMessages()
  // fully replaces the array, and doing that while resumeStream() is still
  // actively driving its own message (still expecting to update it in
  // place by id) makes the SDK lose track of "the message it's writing
  // into" — its next chunk then lands as a brand new message instead of
  // an update, so the turn appears to render twice. Once status is
  // "ready" the SDK is done touching state on its own, so this is safe.
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    if (!resumeEnabled) return;
    // Counts consecutive ticks where the turn is *not* actively streaming
    // (status === "ready") and the DB still has nothing — i.e. genuinely
    // idle, not just slow. Reset to 0 on every tick that's still
    // generating, so this never gives up while a turn is legitimately in
    // progress, no matter how long it takes (a long generation — e.g. a
    // 1500-word poem — was previously cut off by a flat wall-clock
    // timeout that started counting from mount, before the turn had even
    // finished, leaving nothing to reconcile once it eventually did).
    let idleTicks = 0;
    const poll = setInterval(() => {
      if (statusRef.current !== "ready") {
        idleTicks = 0;
        return;
      }
      void loadPersistedMessages().then((count) => {
        if (count === 0) {
          idleTicks += 1;
          if (idleTicks >= 15) clearInterval(poll); // ~30s idle with nothing to find
          return;
        }
        clearInterval(poll);
        // This chat was open right up until the turn completed, and this
        // poll's own loadPersistedMessages() call just confirmed (via a
        // fresh SELECT) that the row actually exists in the DB right now —
        // unlike the timing-guesswork this used to rely on, there's nothing
        // to race here, so a plain "now" is correct. This poll is what
        // covers the resumeStream()-got-a-204 case specifically: onFinish
        // never fires there (the turn had already finished by the time we
        // reconnected — see the big comment above), so nothing else ever
        // notices this turn completed. Without this, the chats list's dot
        // would wrongly stay lit despite the owner being right here.
        if (chatIdRef.current && !projectChatIdRef.current) {
          void supabase
            .from("chats")
            .update({ last_opened_at: "now" })
            .eq("id", chatIdRef.current);
        }
      });
    }, 2000);
    return () => clearInterval(poll);
  }, [resumeEnabled, loadPersistedMessages]);

  // Covers normal, queued, and retry/edit sends. Updating SDK state directly
  // avoids starting an extra approval-continuation request before the new turn.
  const sendMessage = useCallback<typeof sdkSendMessage>((message, options) => {
    if (message != null) {
      setMessages((current) => denyUnansweredToolApprovals(current, current.length));
    }
    return sdkSendMessage(message, options);
  }, [sdkSendMessage, setMessages]);

  const addToolApprovalResponse = useCallback<typeof sdkAddToolApprovalResponse>(async (response) => {
    // Read live SDK state rather than a potentially stale React render.
    setMessages((current) => {
      approvalSignatures.capture(current);
      return current;
    });
    await sdkAddToolApprovalResponse(response);
    setMessages((current) => approvalSignatures.restore(current));
  }, [approvalSignatures, sdkAddToolApprovalResponse, setMessages]);
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
    liveDelegationRef.current?.update(messages.at(-1));
  }, [messages]);

  const handleLiveApprovalResponse = useCallback(async (approval: LiveVoiceApproval, response: LiveVoiceApprovalResponse) => {
    if (response.id !== approval.id) return false;
    // Re-check SDK state synchronously: the user may have clicked a control,
    // sent a new message or changed the proposal while speech was settling.
    let stillPending = false;
    setMessages((current) => {
      stillPending = getLiveVoiceApproval(current, agents)?.key === approval.key;
      return current;
    });
    if (!stillPending) return false;
    await addToolApprovalResponse(response);
    return true;
  }, [addToolApprovalResponse, agents, setMessages]);

  // Offer response notifications every time a turn has genuinely been
  // running a while (1 minute) — not the instant it starts, which would nag
  // on every normal quick reply, and not gated on a prior chat's decision:
  // each chat gets its own prompt, once per turn that runs this long. The
  // timer starts on "submitted" and is left running (not reset) through the
  // "submitted" → "streaming" sub-transition; it's only cleared if the turn
  // finishes before the minute is up.
  useEffect(() => {
    const isActive = status === "submitted" || status === "streaming";

    if (
      isActive &&
      !notificationPromptTimerRef.current &&
      !hasShownNotificationPromptRef.current &&
      !incognito
    ) {
      notificationPromptTimerRef.current = setTimeout(() => {
        notificationPromptTimerRef.current = null;
        hasShownNotificationPromptRef.current = true;
        setShowNotificationPrompt(true);
      }, 60 * 1000);
    }

    if (!isActive) {
      if (notificationPromptTimerRef.current) {
        clearTimeout(notificationPromptTimerRef.current);
        notificationPromptTimerRef.current = null;
      }
      // The turn is done — no reason to keep offering to notify about it.
      setShowNotificationPrompt(false);
    }
  }, [status, incognito]);

  // Clear a still-pending timer on unmount so it doesn't fire against a
  // detached component.
  useEffect(() => {
    return () => {
      if (notificationPromptTimerRef.current) {
        clearTimeout(notificationPromptTimerRef.current);
        notificationPromptTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if ((!chatId && !projectChatId) || incognito) {
      setChatFeedback(null);
      return;
    }

    let isCancelled = false;

    const table = projectChatId ? "project_chats" : "chats";
    const id = (projectChatId ?? chatId) as string;

    supabase
      .from(table)
      .select("feedback")
      .eq("id", id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (isCancelled) {
          return;
        }

        if (error) {
          console.error("[ChatView] load feedback:", error);
          setChatFeedback(null);
          return;
        }

        setChatFeedback(
          data?.feedback === "positive" || data?.feedback === "negative"
            ? data.feedback
            : null
        );
      });

    return () => {
      isCancelled = true;
    };
  }, [chatId, projectChatId, incognito]);

  const resetNegativeFeedbackDialog = useCallback(() => {
    setNegativeFeedbackNote("");
    setConsentToReadChat(false);
    setIsNegativeFeedbackDialogOpen(false);
  }, []);

  // Load persisted messages when mounting with an existing chatId or projectChatId
  useEffect(() => {
    void loadPersistedMessages().finally(() => setHistoryLoaded(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Marks this chat "seen" for the chats list's unseen-completion dot (see
  // src/app/chats/page.tsx) — personal chats only, matching what that list
  // actually shows; project chats have no such indicator yet. Incognito
  // chats have no server-side row to update either way. Fire-and-forget:
  // this is a UI nicety, not something worth blocking or surfacing errors
  // for.
  useEffect(() => {
    if (!chatId || projectChatId || incognito) return;
    void supabase
      .from("chats")
      .update({ last_opened_at: "now" })
      .eq("id", chatId)
      .then(({ error }) => {
        if (error) console.error("[ChatView] mark chat opened:", error);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persistence is handled inside useChat's `onFinish` callback (above) so we
  // only ever write fully-resolved messages — aborts/errors leave the store
  // untouched, preventing orphaned tool_use blocks from being persisted.
  const prevStatus = useRef(status);
  useEffect(() => {
    prevStatus.current = status;
  }, [status]);

  // Sending while the previous turn is still in flight doesn't start a second
  // concurrent request — it's held here and replayed once `status` clears,
  // so a message typed mid-stream still goes out without racing the current one.
  const pendingSendQueueRef = useRef<{ message: PromptInputMessage; agentMention: PendingAgentMention | null }[]>([]);
  const [queuedMessages, setQueuedMessages] = useState<PromptInputMessage[]>([]);

  // toolCallIds of tool-write Notices the user dismissed — cleared only on a
  // fresh mount (e.g. reload), not persisted.
  const [dismissedToolNoticeIds, setDismissedToolNoticeIds] = useState<Set<string>>(() => new Set());

  // The most recent, not-yet-dismissed notice-worthy tool write across the
  // whole chat (memory, register notes, ...) — anchored above the
  // composer (not inline per-message) so it stays visible instead of
  // scrolling away as the conversation continues. To make a new tool type
  // participate, teach getToolWriteNotice about it — this loop doesn't change.
  const activeToolNotice = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const parts = messages[i].parts;
      for (let j = parts.length - 1; j >= 0; j--) {
        const part = parts[j];
        if (part.type !== "dynamic-tool" && !part.type.startsWith("tool-")) continue;
        const toolPart = part as ToolPart;
        if (dismissedToolNoticeIds.has(toolPart.toolCallId)) continue;
        const notice = getToolWriteNotice(toolPart, { projectId });
        if (notice) return { ...notice, toolCallId: toolPart.toolCallId };
      }
    }
    return null;
  }, [messages, dismissedToolNoticeIds, projectId]);

  const handleSend = useCallback(
    async (message: PromptInputMessage, agentMention?: PendingAgentMention | null, signal?: AbortSignal) => {
      if (widgetEditing && !globalModels.some((model) => model.model_id === activeModel && model.provider === "anthropic" && model.is_active)) {
        if (signal) throw new Error("No active Claude model is available.");
        toast.error("No active Claude model is available. Wait for models to load or contact an administrator.");
        return;
      }
      if (sendDisabled) {
        if (signal) throw new Error("Save or reload the widget draft before sending.");
        toast.error("Save or reload the widget draft before sending.");
        return;
      }
      if (signal?.aborted) return;
      if (!signal && (status === "streaming" || status === "submitted")) {
        pendingSendQueueRef.current = [...pendingSendQueueRef.current, { message, agentMention: agentMention ?? null }];
        setQueuedMessages((prev) => [...prev, message]);
        return;
      }
      const { text, files } = message;
      // An auto-sent project prompt can run before the effect that normally
      // mirrors activeModel into the transport ref. Pin it synchronously so
      // the provider always matches the model displayed in the header.
      modelRef.current = activeModel;

      let activeChatId = chatIdRef.current;

      // A confirmed @mention answers this one turn with a different agent's
      // prompt/tools/model, without touching the chat's normal active agent.
      const mentionedAgent = agentMention ? agentsById.get(agentMention.agentId) ?? null : null;
      const sendText = agentMention && mentionedAgent ? stripAgentMentionToken(text, agentMention) : text;
      const requestOverride = mentionedAgent
        ? buildAgentRequestOverride(mentionedAgent, settings, dbTools)
        : null;
      const turnAgentId = mentionedAgent?.id ?? activeAgent?.id ?? pendingAgentId ?? null;
      turnAgentIdRef.current = turnAgentId;

      // First message in a new unsaved chat creates the DB record unless this
      // session is explicitly temporary-only.
      if (!incognito && !activeChatId && !projectChatIdRef.current) {
        const name =
          titleOverride?.slice(0, MAX_CHAT_TITLE_LENGTH) ||
          (sendText ?? "").slice(0, MAX_CHAT_TITLE_LENGTH) ||
          "New chat";
        if (projectIdRef.current && createProjectChat) {
          // Route to project_chats when chatting in a project context.
          // Snapshot the projectId before awaiting so the callback below can't
          // observe a mutated ref (e.g. navigation) after the await resolves.
          const projectId = projectIdRef.current;
          const newId = await createProjectChat({
            name,
            model: modelRef.current,
            agentId: activeAgent?.id ?? pendingAgentId ?? undefined,
            projectId,
            chatTools: buildPersistedChatTools(
              effectiveTools as Record<ToolId, boolean>,
              initialMyFilesConfig
            ),
          });
          if (newId) {
            projectChatIdRef.current = newId;
            onProjectChatCreated?.({ id: newId, projectId, name });
          }
        } else {
          const newId = await createChat({
            name,
            model: modelRef.current,
            agentId: activeAgent?.id ?? undefined,
            // Stamp the surface onto the row so reopening this chat comes back
            // here rather than to the home chat. Read through the ref, like the
            // transport does, so this callback keeps its existing deps.
            type: chatModeRef.current,
            // Snapshot the current effective tools as the per-chat starting state.
            chatTools: buildPersistedChatTools(
              effectiveTools as Record<ToolId, boolean>,
              initialMyFilesConfig
            ),
          });
          if (newId) {
            chatIdRef.current = newId;
            activeChatId = newId;
            onChatCreated(newId);
          }
        }
      }

      if (process.env.NODE_ENV !== "production") {
        console.debug("[ChatView] sending message with applied settings", {
          chatId: activeChatId,
          agentId: activeAgent?.id ?? null,
          mentionedAgentId: mentionedAgent?.id ?? null,
          model: activeModel,
          tools: effectiveTools,
        });
      }

      if (signal?.aborted) return;
      await sendMessage(
        {
          text: sendText,
          files,
          // Attribute the user message to whichever agent handles this turn,
          // including the chat's persistent active agent. While that agent is
          // active the pill stays suppressed as redundant; if the chat later
          // switches/deactivates, both sides of the historical pair reveal
          // their matching attribution immediately. Reloads read the same ID
          // from messages.agent_id.
          metadata: signal ? { agentId: turnAgentId, liveVoiceRequest: true } : turnAgentId ? { agentId: turnAgentId } : undefined,
        },
        requestOverride ? { body: requestOverride } : undefined
      );
      // The user message is persisted via the status-change effect once
      // status transitions away from 'submitted'.
    },
    [
      activeAgent?.id,
      activeModel,
      agentsById,
      createChat,
      createProjectChat,
      dbTools,
      effectiveTools,
      incognito,
      initialMyFilesConfig,
      onChatCreated,
      onProjectChatCreated,
      pendingAgentId,
      sendMessage,
      sendDisabled,
      widgetEditing,
      globalModels,
      settings,
      status,
      titleOverride,
    ]
  );

  const handleLiveDelegate = useCallback((transcript: string, signal: AbortSignal, observer: LiveDelegateObserver): Promise<void> => {
    if (signal.aborted || liveDelegationRef.current) {
      return Promise.reject(new Error("The chat is busy. Please wait for the current task."));
    }
    return new Promise<void>((resolve, reject) => {
      // Ignore the previous assistant message while a new chat row is created.
      const existingIds = new Set(messagesRef.current.map((message) => message.id));
      if (lastFinishedMessageIdRef.current) existingIds.add(lastFinishedMessageIdRef.current);
      let resultAgentName = activeAgentLabel ?? "Woods AI";
      let introduced = false;
      const relay = createLiveTextRelay((text) => {
        observer.onText(introduced ? text
          : `Response authored by ${JSON.stringify(resultAgentName)}. Present these incremental answer chunks in order, crediting the author once. Do not repeat earlier chunks.\n${text}`);
        introduced = true;
      });
      const onAbort = () => {
        if (liveDelegationRef.current !== pending) return;
        liveDelegationRef.current = null;
        void stop();
        reject(new Error("Live voice ended."));
      };
      const pending = {
        resolve: () => { signal.removeEventListener("abort", onAbort); resolve(); },
        reject: (error: Error) => { signal.removeEventListener("abort", onAbort); reject(error); },
        update: (message?: ChatUIMessage, final = false) => {
          if (signal.aborted || !message || existingIds.has(message.id)) return;
          const resultAgentId = message.metadata?.agentId ?? turnAgentIdRef.current;
          resultAgentName = resultAgentId ? agentsById.get(resultAgentId)?.name ?? "Selected agent" : "Woods AI";
          if (message.role === "assistant" && message.parts.some((part) => part.type === "text" && part.text.trim())) observer.onFirstText();
          if (final) relay.finish(message); else relay.update(message);
        },
      };
      liveDelegationRef.current = pending;
      signal.addEventListener("abort", onAbort, { once: true });
      void handleSend({
        text: "Help with the latest request in this live voice conversation. " +
          "Earlier entries are context; answer the latest request. " +
          "Existing tool approval requirements still apply.\n\n" + transcript,
        files: [],
      }, null, signal).catch((error: unknown) => {
        if (liveDelegationRef.current !== pending) return;
        liveDelegationRef.current = null;
        pending.reject(error instanceof Error ? error : new Error("Backend request failed."));
      });
    });
  }, [activeAgentLabel, agentsById, handleSend, stop]);

  const liveChatEnabled = useLiveChatFlag();
  const pendingVoiceApproval = useMemo(() => getLiveVoiceApproval(messages, agents), [messages, agents]);
  const live = useLiveChat({
    messages,
    onDelegate: handleLiveDelegate,
    scope: `${stableChatIdentity ?? "new"}:${projectId ?? ""}`,
    context: getLiveAgentContext(activeAgent?.id ?? pendingAgentId ?? null, activeAgentLabel ?? null, activeModel),
    autoStart: startLiveChat && liveChatEnabled && (!pendingAgentId || !agentsLoading),
    onAutoStarted: onLiveChatStarted,
    pendingApproval: pendingVoiceApproval,
    onApprovalResponse: handleLiveApprovalResponse,
  });

  const handleRecoveryFork = async () => {
    if (forkingChat || !userId || incognito) return;
    const forkMessages = getRecoveryForkMessages(messages);
    if (!forkMessages.length) return;
    setForkingChat(true);
    setForkError(null);
    let newId: string | null = null;
    const isProjectFork = Boolean(projectId && createProjectChat);
    try {
      const params = {
        name: `Fork · ${titleOverride || "Recovered chat"}`.slice(0, 200),
        model: activeModel,
        agentId: activeAgent?.id,
        chatTools: buildPersistedChatTools(
          effectiveTools as Record<ToolId, boolean>,
          initialMyFilesConfig
        ),
      };
      newId = isProjectFork
        ? await createProjectChat!({ ...params, projectId: projectId! })
        : await createChat({ ...params, type: chatMode });
      if (!newId) throw new Error("Could not create the new chat. Please try again.");
      const { error: saveError } = await supabase
        .from(isProjectFork ? "project_messages" : "messages")
        .insert(forkMessages.map((message, index) => ({
          [isProjectFork ? "project_chat_id" : "chat_id"]: newId,
          user_id: userId,
          client_message_id: crypto.randomUUID(),
          role: message.role,
          content: message.parts.map((part) => part.type === "text" ? part.text : "").join("\n\n"),
          parts: message.parts,
          model: activeModel,
          sequence_number: index + 1,
          image_paths: [],
        })));
      if (saveError) throw saveError;
      recoveryRouter.push(isProjectFork
        ? `/?projectChat=${newId}&project=${projectId}`
        : `${chatMode === "code" ? "/code" : "/"}?chat=${newId}`);
    } catch (cause) {
      if (newId) await supabase.from(isProjectFork ? "project_chats" : "chats").delete().eq("id", newId);
      setForkError(cause instanceof Error ? cause.message : "Could not copy the conversation. Please try again.");
    } finally {
      setForkingChat(false);
    }
  };

  // Dequeues exactly one message per idle transition — sending it flips
  // `status` back to streaming, which re-fires this effect once that turn
  // finishes, naturally draining the rest of the queue one at a time.
  useEffect(() => {
    if (status !== "ready") return;
    const [next, ...rest] = pendingSendQueueRef.current;
    if (!next) return;
    pendingSendQueueRef.current = rest;
    setQueuedMessages((prev) => prev.slice(1));
    void handleSend(next.message, next.agentMention);
  }, [status, handleSend]);

  // Retry/edit submits a new user message, preserving every earlier exchange.
  // Never pass messageId: the SDK would truncate the conversation at that ID.
  // If a turn is streaming, stop it before sending the new attempt.
  const resendUserMessage = useCallback(
    async (userMsg: ChatUIMessage, text: string) => {
      // Neither retry nor edit lets the user change attachments, so carry the
      // original file parts forward — only the text part is replaced.
      const fileParts = userMsg.parts.filter((p): p is FileUIPart => p.type === "file");
      const parts: ChatUIMessage["parts"] = [...fileParts, { type: "text", text }];

      if (status === "streaming" || status === "submitted") {
        pendingResendRef.current = {
          parts,
          agentId: activeAgent?.id ?? null,
        };
        // In a resumable-stream setup, stop() only closes this tab's
        // connection — the server keeps generating unless told otherwise
        // (see src/app/api/chat/resumable-stream.ts). The explicit stop
        // endpoint persists whatever partial reply is on screen and wins the
        // race to claim the active stream, so the superseded turn's eventual
        // completion can never write a stray message after this edit/retry.
        const lastMsg = messagesRef.current[messagesRef.current.length - 1];
        const assistantMessage = lastMsg?.role === "assistant" ? lastMsg : undefined;
        const stopPath = projectChatId
          ? `/api/chat/project/${projectChatId}/stop`
          : chatId
            ? `/api/chat/${chatId}/stop`
            : null;
        if (stopPath) {
          const token = accessTokenRef.current;
          void fetch(stopPath, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              assistantMessage,
              model: modelRef.current,
              agentId: turnAgentIdRef.current,
            }),
          });
        }
        stop();
        return;
      }
      const resendAgentId = activeAgent?.id ?? null;
      turnAgentIdRef.current = resendAgentId;
      sendMessage({
        parts,
        metadata: resendAgentId ? { agentId: resendAgentId } : undefined,
      });
    },
    [activeAgent?.id, chatId, projectChatId, sendMessage, status, stop]
  );

  useEffect(() => {
    if (status !== "ready") return;
    const pending = pendingResendRef.current;
    if (!pending) return;
    pendingResendRef.current = null;
    turnAgentIdRef.current = pending.agentId;
    sendMessage({
      parts: pending.parts,
      metadata: pending.agentId ? { agentId: pending.agentId } : undefined,
    });
  }, [status, sendMessage]);

  const handleRetry = useCallback(
    (messageIndex: number) => {
      const userMsg = messagesRef.current[messageIndex];
      if (!userMsg || userMsg.role !== "user") return;
      void resendUserMessage(userMsg, extractUserMessageText(userMsg));
    },
    [resendUserMessage]
  );

  const handleEdit = useCallback(
    (messageIndex: number, text: string) => {
      const userMsg = messagesRef.current[messageIndex];
      if (!userMsg || userMsg.role !== "user") return;
      void resendUserMessage(userMsg, text);
    },
    [resendUserMessage]
  );

  const updateChatFeedback = useCallback(
    async (nextFeedback: "positive" | "negative" | null) => {
      const activeChatId = chatIdRef.current;
      const activeProjectChatId = projectChatIdRef.current;

      if (incognito || (!activeChatId && !activeProjectChatId)) {
        toast.error("Feedback is only available for saved chats.");
        return false;
      }

      if (isFeedbackUpdating) {
        return false;
      }

      const previousFeedback = chatFeedback;
      setIsFeedbackUpdating(true);
      setChatFeedback(nextFeedback);

      const { error } = activeProjectChatId
        ? await supabase.from("project_chats").update({ feedback: nextFeedback }).eq("id", activeProjectChatId)
        : await supabase.from("chats").update({ feedback: nextFeedback }).eq("id", activeChatId!);

      if (error) {
        console.error("[ChatView] update feedback:", error);
        setChatFeedback(previousFeedback);
        toast.error("Failed to update feedback.");
        setIsFeedbackUpdating(false);
        return false;
      }

      if (nextFeedback === null) {
        toast.success("Feedback removed.");
      } else if (nextFeedback === "positive") {
        toast.success("Positive feedback recorded.");
      } else {
        toast.success("Negative feedback recorded.");
      }

      setIsFeedbackUpdating(false);
      return true;
    },
    [chatFeedback, incognito, isFeedbackUpdating]
  );

  const handleLike = useCallback(() => {
    void updateChatFeedback(chatFeedback === "positive" ? null : "positive");
  }, [chatFeedback, updateChatFeedback]);

  const handleDislike = useCallback(() => {
    if (chatFeedback === "negative") {
      void updateChatFeedback(null);
      return;
    }

    setIsNegativeFeedbackDialogOpen(true);
  }, [chatFeedback, updateChatFeedback]);

  const handleSubmitNegativeFeedback = useCallback(async () => {
    const activeChatId = chatIdRef.current;
    const activeProjectChatId = projectChatIdRef.current;

    if (incognito || (!activeChatId && !activeProjectChatId)) {
      toast.error("Feedback is only available for saved chats.");
      return;
    }

    if (!negativeFeedbackNote.trim()) {
      toast.error("Please add a note before submitting feedback.");
      return;
    }

    if (isNegativeFeedbackSubmitting) {
      return;
    }

    setIsNegativeFeedbackSubmitting(true);

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      console.error("[ChatView] load user for feedback:", userError);
      toast.error("You need to be signed in to submit feedback.");
      setIsNegativeFeedbackSubmitting(false);
      return;
    }

    const { error: insertError, data: insertedRows } = await supabase
      .from("chat_feedbacks")
      .insert({
        user_id: user.id,
        ...(activeProjectChatId ? { project_chat_id: activeProjectChatId } : { chat_id: activeChatId! }),
        feedback: negativeFeedbackNote.trim(),
        consent_to_read_chat: consentToReadChat,
      })
      .select("id, created_at")
      .single();

    if (insertError) {
      console.error("[ChatView] insert feedback:", insertError);
      toast.error("Failed to submit feedback.");
      setIsNegativeFeedbackSubmitting(false);
      return;
    }

    // Fire-and-forget: notify superadmins via email
    void (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const feedbackTargetId = activeProjectChatId ?? activeChatId!;
        const chatRecord = activeProjectChatId
          ? await supabase
              .from("project_chats")
              .select("name")
              .eq("id", activeProjectChatId)
              .maybeSingle()
          : await supabase
              .from("chats")
              .select("name")
              .eq("id", activeChatId!)
              .maybeSingle();
        await fetch(
          `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/notify-feedback-submitted`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session?.access_token ?? ""}`,
              apikey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "",
            },
            body: JSON.stringify({
              feedbackId: insertedRows?.id ?? "",
              chatId: feedbackTargetId,
              projectChatId: activeProjectChatId ?? undefined,
              chatName: chatRecord.data?.name ?? feedbackTargetId,
              feedbackContent: negativeFeedbackNote.trim(),
              consentToReadChat: consentToReadChat,
              submittedAt: insertedRows?.created_at ?? new Date().toISOString(),
            }),
          }
        );
      } catch (notifyErr) {
        console.warn("[ChatView] feedback notification failed:", notifyErr);
      }
    })();

    const didUpdateFeedback = await updateChatFeedback("negative");
    setIsNegativeFeedbackSubmitting(false);

    if (didUpdateFeedback) {
      resetNegativeFeedbackDialog();
    }
  }, [
    consentToReadChat,
    incognito,
    isNegativeFeedbackSubmitting,
    negativeFeedbackNote,
    resetNegativeFeedbackDialog,
    updateChatFeedback,
  ]);

  const isStreaming = status === "submitted" || status === "streaming";
  const savedWidgetRevisionRef = useRef(widgetEditing?.expectedRevision ?? 0);
  useEffect(() => {
    onActivityChange?.(isStreaming);
  }, [isStreaming, onActivityChange]);
  useEffect(() => {
    if (!widgetEditing || !onWidgetDraftSaved) return;
    const latest = messages.at(-1);
    if (latest?.role !== "assistant") return;
    for (const part of latest.parts) {
      if (part.type !== "tool-edit_file" && part.type !== "tool-create_file" && part.type !== "tool-save_widget_from_container") continue;
      const output = (part as unknown as { state: string; output?: { widgetRevision?: number } });
      const revision = output.output?.widgetRevision;
      if (output.state === "output-available" && typeof revision === "number" && revision > savedWidgetRevisionRef.current) {
        savedWidgetRevisionRef.current = revision;
        onWidgetDraftSaved(revision);
      }
    }
  }, [widgetEditing, messages, onWidgetDraftSaved]);
  const lastMessageIndex = messages.length - 1;

  // Latest assistant message's metadata — drives the context ring % and debug rows.
  const latestAssistantMetadata = useMemo(
    () => messages.findLast((m) => m.role === "assistant")?.metadata,
    [messages]
  );

  const isOwner = !chatOwnerId || chatOwnerId === userId;
  const { isGeneratingTitle, isTitleAnimating, title, clearGeneratedTitle } = useChatTitleGeneration({
    chatId,
    chatName,
    incognito,
    isNewSession,
    messages,
    status,
    titleOverride,
    typingSpeed: TITLE_TYPING_SPEED,
    updateChatName: isOwner ? updateChatName : async () => false,
  });
  // An untitled chat shows "New chat" in the header but reads "Chat · Woods AI"
  // in the browser tab, where the route already implies it's a new one.
  useTabTitle(incognito ? "Temporary chat" : title === "New chat" ? "Chat" : title);

  const showHeader = messages.length > 0 || Boolean(widgetEditing);

  // Per-chat model switching is for general and project chats only — agent
  // chats always follow the agent's configured model. Non-owners (shared
  // project chats) can't change it either.
  const canChangeModel = !activeAgent && !pendingAgentId && isOwner;
  const effortControl = canChangeModel && !projectId && !widgetEditing ? (
    <ChatEffortControl modelId={activeModel}
      value={chatEffort} onChange={handleEffortChange} />
  ) : null;
  const modelSelector = canChangeModel ? (
    <>
      <ChatModelDropdown
        claudeOnly={Boolean(widgetEditing)}
        codeOnly={chatMode === "code"}
        models={globalModels}
        onChange={handleModelChange}
        value={activeModel}
      />
      {effortControl}
    </>
  ) : null;

  const handleRename = useCallback(
    async (name: string) => {
      const activeProjectChatId = projectChatIdRef.current;
      if (activeProjectChatId) {
        if (!updateProjectChatName) return false;
        const ok = await updateProjectChatName(activeProjectChatId, name);
        // Unlike personal chats (whose name lives in the reactive `chats` array
        // that updateChatName itself updates), the header title for a project
        // chat comes from home-client's own activeProjectChatName state — the
        // DB write alone doesn't make it re-render with the new name.
        if (ok) onProjectChatRenamed?.(name);
        return ok;
      }
      const activeChatId = chatIdRef.current;
      if (!activeChatId) return false;
      const ok = await updateChatName(activeChatId, name);
      if (ok) clearGeneratedTitle();
      return ok;
    },
    [clearGeneratedTitle, updateChatName, updateProjectChatName, onProjectChatRenamed]
  );

  const handleDelete = useCallback(async () => {
    const activeProjectChatId = projectChatIdRef.current;
    if (activeProjectChatId) {
      if (!deleteProjectChat) return;
      const ok = await deleteProjectChat(activeProjectChatId);
      if (ok) {
        onChatDeleted();
      } else {
        toast.error("Failed to delete chat.");
      }
      return;
    }
    const activeChatId = chatIdRef.current;
    if (!activeChatId) return;
    const ok = await deleteChat(activeChatId);
    if (ok) {
      onChatDeleted();
    } else {
      toast.error("Failed to delete chat.");
    }
  }, [deleteChat, deleteProjectChat, onChatDeleted]);

  const handleRemoveFromProject = useCallback(async () => {
    const activeProjectChatId = projectChatIdRef.current;
    if (!activeProjectChatId || !projectId) return;
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const res = await fetch(
      `/api/projects/${projectId}/project-chats/${activeProjectChatId}/move-to-personal`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      toast.error((body as { error?: string }).error ?? "Failed to remove chat from project.");
      return;
    }
    toast.success("Chat moved to your personal space.");
    onProjectChanged?.();
    const newChatId = (body as { chatId?: string }).chatId;
    if (newChatId && onChatMovedToPersonal) {
      onChatMovedToPersonal(newChatId);
    } else {
      onChatDeleted();
    }
  }, [projectId, onProjectChanged, onChatDeleted, onChatMovedToPersonal]);

  // HTML intake: each attached .html/.htm asks whether to read it as code
  // (original file → code execution container) or as a rendered page (PDF).
  const activeModelProvider =
    globalModels.find((model) => model.model_id === activeModel)?.provider ??
    (activeModel.startsWith("claude") ? "anthropic" : /^(gpt-|openai\/)/.test(activeModel) ? "openai" : undefined);
  const containerCapable = activeModelProvider === "anthropic" || activeModelProvider === "openai";
  const generalFileExplorer = chatMode === "general" && !projectId && !widgetEditing && !activeAgent && isOwner && containerCapable;
  const [workspaceFilesOpen, setWorkspaceFilesOpen] = useState(false);
  const [workspaceAttachment, setWorkspaceAttachment] = useState<{ key: number; file: File } | null>(null);
  const workspaceAttachmentKey = useRef(0);
  const workspaceProvider = activeModelProvider === "openai" ? "openai" : "anthropic";
  const workspaceFiles = useContainerFiles(generalFileExplorer ? chatId : null, workspaceProvider, generalFileExplorer && workspaceFilesOpen);
  const refreshWorkspace = workspaceFiles.refresh;
  const downloadWorkspaceFile = workspaceFiles.downloadFile;
  const previousWorkspaceStatus = useRef(status);
  useEffect(() => {
    const wasBusy = previousWorkspaceStatus.current === "streaming" || previousWorkspaceStatus.current === "submitted";
    previousWorkspaceStatus.current = status;
    if (!wasBusy || status !== "ready" || !workspaceFilesOpen || !generalFileExplorer) return;
    const timer = window.setTimeout(() => void refreshWorkspace(), 400);
    return () => window.clearTimeout(timer);
  }, [status, workspaceFilesOpen, generalFileExplorer, refreshWorkspace]);
  const handleWorkspaceAttach = useCallback(async (file: AttachableContainerFile) => {
    const content = await downloadWorkspaceFile(file.path);
    workspaceAttachmentKey.current += 1;
    setWorkspaceAttachment({ key: workspaceAttachmentKey.current,
      file: new File([content], file.name, { type: content instanceof Blob ? content.type : "text/plain" }),
    });
    setWorkspaceFilesOpen(false);
  }, [downloadWorkspaceFile]);
  const handleWorkspaceDownload = useCallback(async (file: AttachableContainerFile) => {
    const blob = await downloadWorkspaceFile(file.path);
    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally { URL.revokeObjectURL(url); }
  }, [downloadWorkspaceFile]);
  const headerActions = extraHeaderActions || generalFileExplorer ? <>
    {extraHeaderActions}
    {generalFileExplorer ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button aria-label="Chat files" aria-pressed={workspaceFilesOpen}
            disabled={!chatId}
            className={`size-9 rounded-full border-transparent bg-transparent shadow-none ${workspaceFilesOpen ? "text-activated hover:bg-transparent hover:text-activated" : "text-muted-foreground hover:bg-transparent hover:text-foreground"}`}
            onClick={() => setWorkspaceFilesOpen((open) => !open)} size="icon" type="button" variant="ghost">
            <FileTextIcon size={16} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Chat files</TooltipContent>
      </Tooltip>
    ) : null}
  </> : undefined;
  const [htmlIntakeRequest, setHtmlIntakeRequest] = useState<{
    filename: string;
    resolve: (choice: HtmlIntakeChoice | null) => void;
  } | null>(null);
  // Several HTML files dropped at once are asked about one at a time.
  const htmlIntakeQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  // Converted PDFs too large to read natively go to the container instead, so
  // the client-side PDF size rejection must not apply to them.
  const containerBoundFilesRef = useRef(new WeakSet<File>());
  // prepareFile awaits the user, so it reads the latest state through a ref.
  const htmlIntakeContextRef = useRef({
    containerCapable,
    codeExecutionOn: Boolean(effectiveTools.codeExecution),
    setChatTool,
  });
  useEffect(() => {
    htmlIntakeContextRef.current = {
      containerCapable,
      codeExecutionOn: Boolean(effectiveTools.codeExecution),
      setChatTool,
    };
  }, [containerCapable, effectiveTools.codeExecution, setChatTool]);

  const askHtmlIntake = useCallback((filename: string) => {
    const answer = htmlIntakeQueueRef.current.then(
      () =>
        new Promise<HtmlIntakeChoice | null>((resolve) => {
          setHtmlIntakeRequest({ filename, resolve });
        })
    );
    htmlIntakeQueueRef.current = answer;
    return answer;
  }, []);

  const handleHtmlIntakeChoice = useCallback(
    (choice: HtmlIntakeChoice | null) => {
      htmlIntakeRequest?.resolve(choice);
      setHtmlIntakeRequest(null);
    },
    [htmlIntakeRequest]
  );

  const prepareFile = useCallback(
    async (file: File): Promise<PreparedAttachment | null> => {
      if (generalFileExplorer) {
        containerBoundFilesRef.current.add(file);
        const { codeExecutionOn, setChatTool: setTool } = htmlIntakeContextRef.current;
        if (!codeExecutionOn) setTool("codeExecution", true);
        return { file, providerMetadata: CONTAINER_ATTACHMENT_METADATA };
      }
      if (!isHtmlFile(file)) return { file };
      const choice = await askHtmlIntake(file.name);
      if (!choice) return null;

      const enableCodeExecution = () => {
        const { codeExecutionOn, setChatTool: setTool } = htmlIntakeContextRef.current;
        if (!codeExecutionOn) setTool("codeExecution", true);
      };

      if (choice === "code") {
        enableCodeExecution();
        return { file, providerMetadata: CONTAINER_ATTACHMENT_METADATA };
      }

      // Progress shows on the attachment card itself (it stays in its
      // uploading state until prepareFile and the upload both finish), so
      // only failures get a toast — same as any other attachment.
      let pdf: File;
      try {
        pdf = await convertHtmlToPdf(file);
      } catch (err) {
        console.error("[ChatView] HTML to PDF conversion failed:", err);
        toast.error(`Couldn't convert ${file.name} to PDF. Try attaching it as code instead.`);
        return null;
      }

      if (pdf.size <= MAX_PDF_ATTACHMENT_BYTES) {
        return { file: pdf };
      }
      if (!htmlIntakeContextRef.current.containerCapable) {
        toast.error(
          `${pdf.name} is ${formatMegabytes(pdf.size)} — too large to read as a PDF. ` +
            `Switch to a Claude model to load it into the code execution sandbox instead.`
        );
        return null;
      }
      containerBoundFilesRef.current.add(pdf);
      enableCodeExecution();
      return { file: pdf, providerMetadata: CONTAINER_ATTACHMENT_METADATA };
    },
    [askHtmlIntake, generalFileExplorer]
  );

  const uploadFile = useCallback(
    (file: File): Promise<string> => {
      const rejection = containerBoundFilesRef.current.has(file)
        ? null
        : chatAttachmentRejection(file);
      if (rejection) {
        // The provider drops an attachment whose upload rejects; say why.
        toast.error(rejection);
        return Promise.reject(new Error(rejection));
      }
      if (!userId) {
        // No user logged in — fall back to blob URL (incognito / unauthenticated)
        return Promise.resolve(URL.createObjectURL(file));
      }
      // Pasted text (text/plain with no file extension) — skip Supabase upload
      // and return a stable data: URL so it never fails in any environment.
      const isPastedText =
        file.type === "text/plain" && !/\.[a-zA-Z0-9]{1,6}$/.test(file.name);
      if (isPastedText) {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        });
      }
      return uploadAttachment(file, userId);
    },
    [userId]
  );

  // A deep-linked `?agent=` id arrives before the agents list finishes
  // fetching. Auto-sending the first message while activeAgent is still null
  // would silently drop that agent's model/prompt/tools/knowledge base for
  // the whole turn, so hold off until it resolves (or agents finish loading
  // and it turns out not to exist, in which case don't block forever).
  const awaitingAgentResolution = Boolean(pendingAgentId) && !activeAgent && agentsLoading;

  return (
    <PromptInputProvider uploadFile={uploadFile} prepareFile={prepareFile}>
      <HtmlIntakeDialog
        filename={htmlIntakeRequest?.filename ?? null}
        codeAvailable={containerCapable}
        onChoose={handleHtmlIntakeChoice}
      />
      <PromptDraftSync pendingPrompt={pendingPrompt} onPromptConsumed={onPromptConsumed} onSend={handleSend} ready={historyLoaded && !awaitingAgentResolution} />
      <AttachmentDraftSync
        pendingAttachment={workspaceAttachment ?? pendingAttachment}
        onAttachmentConsumed={workspaceAttachment ? () => setWorkspaceAttachment(null) : onAttachmentConsumed}
      />
      {!historyLoaded ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="size-5 animate-spin rounded-full border-2 border-border border-t-foreground" />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 overflow-hidden overscroll-none">
          <div className={`relative min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${generalFileExplorer && workspaceFilesOpen ? "hidden md:flex" : "flex"}`}>
            {!headerLeading && (modelSelector || (showHeader && !projectChatId && chatId) || (showIncognitoToggle && onToggleIncognito) || headerActions) ? (
              <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
                {/* Featured announcement chip, left of the share/temporary-chat
                    icons. When the desktop ChatHeader is visible it renders its
                    own in-flow copy next to the share icon, so hide this
                    floating one on md+ to avoid the two overlapping. */}
                <FeaturedAnnouncementChip
                  className={showHeader ? "md:hidden" : undefined}
                />
                {showHeader && !projectChatId && chatId ? (
                  // ChatHeader (and the share icon inside it) is hidden below md;
                  // mirror just the share action here so mobile users can still reach it.
                  <div className="md:hidden">
                    <ShareChatPanel chatId={chatId} />
                  </div>
                ) : null}
                {showIncognitoToggle && onToggleIncognito ? (
                  // ChatHeader renders its own copy of this toggle once there are
                  // messages, so hide this floating one on md+ then to avoid
                  // duplicates. Before that (new/temporary chat with no messages
                  // yet) ChatHeader isn't rendered at all, so keep this visible.
                  <div className={showHeader ? "md:hidden" : undefined}>
                    <IncognitoToggleButton incognito={incognito} onToggleIncognito={onToggleIncognito} />
                  </div>
                ) : null}
                {headerActions ? (
                  // Same duplicate-avoidance as the toggle above: ChatHeader
                  // renders its own copy of these once it is visible at md+.
                  <div className={showHeader ? "md:hidden" : undefined}>{headerActions}</div>
                ) : null}
              </div>
            ) : null}
            <ChatFeedbackDialog
              consentToReadChat={consentToReadChat}
              isSubmitting={isNegativeFeedbackSubmitting || isFeedbackUpdating}
              note={negativeFeedbackNote}
              onConsentToReadChatChange={setConsentToReadChat}
              onNoteChange={setNegativeFeedbackNote}
              onOpenChange={(open) => {
                if (open) {
                  setIsNegativeFeedbackDialogOpen(true);
                  return;
                }

                if (!isNegativeFeedbackSubmitting) {
                  resetNegativeFeedbackDialog();
                }
              }}
              onSubmit={() => {
                void handleSubmitNegativeFeedback();
              }}
              open={isNegativeFeedbackDialogOpen}
            />
            {showHeader ? (
              <div className={headerLeading ? undefined : "hidden md:block"}>
                <ChatHeader
                  chatId={chatId}
                  projectChatId={projectChatId}
                  userId={userId}
                  isGeneratingTitle={isGeneratingTitle}
                  isAnimatingTitle={isTitleAnimating}
                  title={title}
                  projectId={projectId}
                  isProjectChat={!!projectChatId}
                  sharing={localSharing}
                  onRename={handleRename}
                  onDelete={handleDelete}
                  onRemoveFromProject={handleRemoveFromProject}
                  onProjectChanged={onProjectChanged}
                  onSharingChanged={setLocalSharing}
                  onChatDeleted={onChatDeleted}
                  modelSelector={modelSelector}
                  incognito={incognito}
                  showIncognitoToggle={showIncognitoToggle}
                  onToggleIncognito={onToggleIncognito}
                  headerActions={headerActions}
                  headerLeading={headerLeading}
                />
              </div>
            ) : null}
            {!headerLeading && (modelSelector || (showHeader && !projectChatId && chatId) || (showIncognitoToggle && onToggleIncognito) || headerActions) ? (
              // Reserves room below the floating share/incognito icons on mobile,
              // where ChatHeader (and its layout space) is hidden.
              <div className="h-12 shrink-0 md:hidden" />
            ) : null}

            {isAwaitingResume ? (
              <ChatSkeleton />
            ) : !widgetEditing && messages.length === 0 && live.transcript.length === 0 && !pendingPrompt?.autoSend && live.status === "idle" ? (
              <WelcomeScreen
                liveVoiceControl={isOwner && liveChatEnabled ? (
                  <PromptInputButton type="button" aria-label="Start live voice"
                    tooltip={{ content: "Live voice", side: "top" }}
                    disabled={status === "streaming" || status === "submitted"}
                    onClick={() => void live.start()}>
                    <AudioLinesIcon size={16} />
                  </PromptInputButton>
                ) : null}
                variant={chatMode === "code" ? "code" : "general"}
                activeAgent={activeAgent}
                activeAgentLabel={activeAgentLabel}
                myFilesLabel={initialMyFilesLabel}
                activeModel={activeModel}
                onModelChange={handleModelChange}
                effortControl={effortControl}
                incognito={incognito}
                isUserNameReady={isUserNameReady}
                mentionableAgents={mentionableAgents}
                webSearch={effectiveTools.webSearch}
                onWebSearchChange={handleWebSearchChange}
                onActivateAgent={onActivateAgent}
                onSend={handleSend}
                onStop={stop}
                status={status}
                userName={userName}
              />
            ) : (
              <>
                {/* Messages — extra bottom padding so the last message isn't hidden
                    behind the floating input (~input height + disclaimer + gap) */}
                <Conversation className="min-h-0 flex-1">
                  <ConversationContent className={cn(COL, "px-4", live.status === "idle" ? "pb-56" : "pb-64")} scrollClassName="h-full overflow-y-auto overscroll-contain scrollbar-minimal">
                    {Array.from({ length: messages.length + 1 }, (_, i) => {
                      const voiceBubbles = live.transcript
                        .filter((bubble) => i === Math.min(bubble.anchorCount ?? live.transcriptAnchor, messages.length))
                        .map((bubble) => (
                          <Message key={`voice-${bubble.id}`} from={bubble.role}>
                            <MessageContent className="whitespace-pre-wrap">
                              <span className="text-xs text-muted-foreground">{bubble.role === "user" ? "You" : "Woods AI"} · Voice</span>
                              {bubble.text}
                            </MessageContent>
                          </Message>
                        ));
                      const msg = messages[i] ? cleanLiveRequestMessage(messages[i]) : undefined;
                      if (!msg || (i >= live.transcriptAnchor && msg.metadata?.liveVoiceRequest && live.transcript.length > 0)) return voiceBubbles;
                      const isLast = i === lastMessageIndex;
                      const lastPart = msg.parts?.[msg.parts.length - 1] as
                        | { type?: string; text?: string }
                        | undefined;
                      const lastIsActiveText =
                        lastPart?.type === "text" && (lastPart.text ?? "").length > 0;
                      const showThinking =
                        isStreaming &&
                        isLast &&
                        msg.role === "assistant" &&
                        !lastIsActiveText;
                      const previousUserMessage =
                        msg.role === "assistant" && i > 0 && messages[i - 1]?.role === "user"
                          ? messages[i - 1]
                          : undefined;
                      return (
                        <Fragment key={msg.id}>
                        {voiceBubbles}
                        <ChatMessage
                          widgetEditor={Boolean(widgetEditing)}
                          chatFeedback={chatFeedback}
                          feedbackDisabled={isFeedbackUpdating || incognito || (!chatId && !projectChatId)}
                          key={msg.id}
                          index={i}
                          previousIndex={previousUserMessage ? i - 1 : undefined}
                          isStreaming={isStreaming && isLast}
                          showThinking={showThinking}
                          showToolDetails={isSuperAdmin}
                          message={msg.role === "assistant" && msg.metadata?.agentId === undefined && previousUserMessage?.metadata?.agentId !== undefined
                            ? { ...msg, metadata: { ...msg.metadata, agentId: previousUserMessage.metadata.agentId } }
                            : msg}
                          chatId={chatId}
                          isProjectChat={!!projectChatId}
                          projectId={projectId}
                          userId={userId}
                          onSaveHtmlToWidgets={onSaveHtmlToWidgets}
                          agentsById={agentsById}
                          activeAgentId={activeAgent?.id ?? null}
                          onDislike={handleDislike}
                          onLike={handleLike}
                          onOpenInSidebar={handleOpenInSidebar}
                          onArtifactSaved={incognito ? undefined : handleArtifactSaved}
                          onRetry={msg.role === "user" ? handleRetry : undefined}
                          onEdit={msg.role === "user" ? handleEdit : undefined}
                          onRetryPrevious={previousUserMessage ? handleRetry : undefined}
                          onEditPrevious={previousUserMessage ? handleEdit : undefined}
                          previousUserMessageText={previousUserMessage ? extractUserMessageText(previousUserMessage) : undefined}
                          onToolApprovalResponse={addToolApprovalResponse}
                          onUseAgent={onUseAgent}
                          onAgentDeactivated={onAgentDeactivated}
                          onRegisterCreate={projectId ? handleRegisterCreate : undefined}
                          onContinue={
                            isLast ? () => handleSend({ text: "Continue", files: [] }) : undefined
                          }
                        />
                        </Fragment>
                      );
                    })}
                    {error && (
                      <Message from="assistant">
                        <MessageContent>
                          <Alert variant="destructive">
                            <AlertTitle>Failed to generate a response.</AlertTitle>
                            <AlertDescription>{error.message || "Please try again."}</AlertDescription>
                          </Alert>
                          {isOwner && !incognito && getRecoveryForkMessages(messages).length > 0 && (
                            <Confirmation
                              variant="destructive"
                              approval={dismissedRecoveryError === error
                                ? { id: "fork-chat-recovery", approved: false }
                                : { id: "fork-chat-recovery" }}
                              state={dismissedRecoveryError === error ? "output-denied" : "approval-requested"}
                            >
                              <ConfirmationTitle>
                                <ConfirmationRequest>
                                  Do you want to start a new chat as a fork from your last successful reply?
                                  The fork keeps the conversation text through that reply. Attachments and tool records are left out.
                                </ConfirmationRequest>
                                <ConfirmationRejected>You stayed in this chat.</ConfirmationRejected>
                              </ConfirmationTitle>
                              {forkError && <p role="alert">{forkError}</p>}
                              <ConfirmationActions>
                                <ConfirmationAction variant="outline" disabled={forkingChat} onClick={() => setDismissedRecoveryError(error)}>
                                  Stay here
                                </ConfirmationAction>
                                <ConfirmationAction disabled={forkingChat} onClick={() => void handleRecoveryFork()}>
                                  {forkingChat ? "Creating chat…" : "Start new chat"}
                                </ConfirmationAction>
                              </ConfirmationActions>
                            </Confirmation>
                          )}
                        </MessageContent>
                      </Message>
                    )}
                    {/* Placeholder assistant bubble shown after submit but
                        before the assistant message has been appended, so the
                        loading animation is aligned with the message column. */}
                    {isStreaming &&
                      messages[lastMessageIndex]?.role !== "assistant" && (
                        <Message from="assistant">
                          <MessageContent>
                            <ThinkingIndicator />
                          </MessageContent>
                        </Message>
                      )}
                    {/* Queued messages — shown faded, in send order, until
                        each one's turn comes up and it actually sends. */}
                    {queuedMessages.map((qm, i) =>
                      qm.text ? (
                        <Message key={i} from="user" className="opacity-40">
                          <MessageContent className="whitespace-pre-wrap">{qm.text}</MessageContent>
                        </Message>
                      ) : null
                    )}
                  </ConversationContent>
                  <ConversationScrollButton />
                </Conversation>

                {agentUnavailable ? (
                  <div className="flex items-center justify-center gap-2 border-t border-border px-4 py-3 text-xs text-muted-foreground">
                    <LockIcon aria-hidden="true" className="size-3.5 shrink-0" />
                    You no longer have access to this chat&apos;s agent, so it&apos;s read-only.
                  </div>
                ) : isOwner ? (
                  <ConversationInput
                    live={live}
                    activeToolNotice={activeToolNotice}
                    onDismissToolNotice={(toolCallId) =>
                      setDismissedToolNoticeIds((prev) => new Set(prev).add(toolCallId))
                    }
                    showNotificationPrompt={showNotificationPrompt}
                    onTurnOnNotifications={() => {
                      if (projectChatIdRef.current) {
                        armNotifyOnFinish(projectChatIdRef.current, "project");
                      } else if (chatIdRef.current) {
                        armNotifyOnFinish(chatIdRef.current, "chat");
                      }
                      setShowNotificationPrompt(false);
                    }}
                    onDismissNotificationPrompt={() => setShowNotificationPrompt(false)}
                    activeAgentLabel={activeAgentLabel}
                    myFilesLabel={initialMyFilesLabel}
                    incognito={incognito}
                    latestAssistantMetadata={latestAssistantMetadata}
                    mentionableAgents={mentionableAgents}
                    modelId={activeModel}
                    modelContextWindow={activeModelContextWindow}
                    variant={chatMode === "code" ? "code" : "general"}
                    webSearch={effectiveTools.webSearch}
                    onWebSearchChange={handleWebSearchChange}
                    onSend={handleSend}
                    onStop={stop}
                    status={status}
                    queuedCount={queuedMessages.length}
                  />
                ) : (
                  <div className="flex items-center justify-center gap-2 border-t border-border px-4 py-3 text-xs text-muted-foreground">
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    View only — this chat was shared by another project member
                  </div>
                )}
              </>
            )}
          </div>

          {generalFileExplorer && workspaceFilesOpen ? (
            <ContainerFilesSidePanel
              key={`${chatId}:${workspaceProvider}`}
              provider={workspaceProvider}
              files={workspaceFiles.files} status={workspaceFiles.status}
              loading={workspaceFiles.loading} error={workspaceFiles.error}
              listedAt={workspaceFiles.listedAt} usableUntil={workspaceFiles.usableUntil}
              onReadFile={workspaceFiles.readFile} onRefresh={workspaceFiles.refresh}
              onRestore={workspaceProvider === "anthropic" ? workspaceFiles.refresh : undefined}
              onAttachFile={handleWorkspaceAttach} onDownloadFile={handleWorkspaceDownload}
              onClose={() => setWorkspaceFilesOpen(false)}
            />
          ) : sidebarDoc && (
            <>
              {/* Drag handle */}
              <div
                className="group relative z-10 w-px shrink-0 cursor-col-resize select-none"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const startX = e.clientX;
                  const startWidth = sidebarWidth;

                  // Overlay blocks the iframe from stealing events during drag
                  const overlay = document.createElement("div");
                  overlay.style.cssText = "position:fixed;inset:0;z-index:9999;cursor:col-resize;";
                  document.body.appendChild(overlay);

                  const onMove = (mv: MouseEvent) => {
                    const delta = startX - mv.clientX;
                    setSidebarWidth(Math.max(288, Math.min(window.innerWidth * 0.75, startWidth + delta)));
                  };
                  const onUp = () => {
                    overlay.remove();
                    window.removeEventListener("mousemove", onMove);
                    window.removeEventListener("mouseup", onUp);
                  };
                  window.addEventListener("mousemove", onMove);
                  window.addEventListener("mouseup", onUp);
                }}
              >
                <div className="absolute inset-y-0 -left-2 -right-2" />
              </div>
              <DocPreviewPanel
                filename={sidebarDoc.filename}
                url={sidebarDoc.url}
                width={sidebarWidth}
                onClose={() => setSidebarDoc(null)}
                onSaveToWidgets={
                  onSaveHtmlToWidgets
                    ? async () => {
                        await onSaveHtmlToWidgets(
                          sidebarDoc.filename.replace(/\.(html|htm)$/i, ""),
                          sidebarDoc.url
                        );
                      }
                    : undefined
                }
                onAddToProject={sidebarDoc.onAddToProject}
              />
            </>
          )}
        </div>
      )}
    </PromptInputProvider>
  );
};
